/**
 * S-20 — Education wire schemas (Learning :5600, Exam :5700, Engagement :5800).
 *
 * Zod schemas mirror the BACKEND DTOs field-by-field (CourseSummaryDto,
 * ModuleDto, LessonDto, EnrollmentDto, ExamSummaryDto, EventSummaryDto, …).
 * Responses are parsed at the client seam so a contract drift fails LOUDLY at
 * the boundary instead of as a silent `undefined` deep in the UI (the auth
 * launch bug). Dates travel as ISO strings; the envelope is unwrapped by
 * createApiClient, so these schemas describe the `data` payload only.
 */
import { z } from 'zod';

import { dashboardFilterSchema } from './dashboardSegments';

// TCH-XLSX: the bulk-register row report rides in a ProblemDetails EXTENSION, so the readers below need
// the error shape. Type-only import — no runtime coupling between the two modules.
import type { ProblemDetails } from './envelope';
import { safeExternalUrl } from './siteMenu';
import { parseVideoUrl, type SiteVideoRef } from './siteVideo';

/** ISO-8601 timestamp as serialized by System.Text.Json */
const isoDate = z.string().min(1);

/** paged list payload [STD-API-8]: { items, totalItems } */
export function pagedListSchema<T extends z.ZodType>(item: T) {
  // Client zarfi acar: liste endpointlerinde data = DUZ DIZI, sayfalama meta.pagination
  // icindedir [STD-API-2]. Eski {items,totalItems} sekli de kabul edilir (2026-07-06).
  return z.union([
    z.object({ items: z.array(item), totalItems: z.number() }),
    z.array(item).transform((items) => ({ items, totalItems: items.length }))
  ]);
}

// ==============================|| LEARNING (:5600) ||============================== //

export const courseStatuses = ['draft', 'published', 'archived'] as const;
/** LRN-RD (mig 578): a live_session lesson is only MARKED live on the course — time/link/teacher are per event. */
export const lessonContentTypes = ['video', 'text', 'quiz_ref', 'file', 'live_session', 'scorm', 'assignment'] as const;
export type LessonContentType = (typeof lessonContentTypes)[number];
/** live_session completion: joined or watched the recording / joined only / an admin marks it. */
export const liveCompletionRules = ['join_or_recording', 'join', 'teacher_marks'] as const;
export type LiveCompletionRule = (typeof liveCompletionRules)[number];
/** LRN-LIB step 2 (mig 598): how a text lesson completes — null/'manual' = "Mark as complete". */
export const textCompletionRules = ['manual', 'scrolled_to_end'] as const;
export type TextCompletionRule = (typeof textCompletionRules)[number];
/** LRN-LIB step 2 (mig 598): how a file lesson completes — null/'manual' = "Mark as complete"; 'opened' = opened or downloaded. */
export const fileCompletionRules = ['manual', 'opened', 'last_page', 'listened_90', 'quiz'] as const;
export type FileCompletionRule = (typeof fileCompletionRules)[number];
/** LRN-LIB step 4 (mig 600): how a video lesson completes — watched_90 needs a video the player plays itself. */
export const videoCompletionRules = ['manual', 'watched_90', 'quiz'] as const;
export type VideoCompletionRule = (typeof videoCompletionRules)[number];
/** LRN-LIB step 4 (mig 600): how an assignment lesson completes. */
export const assignmentCompletionRules = ['handed_in', 'graded_pass'] as const;
export type AssignmentCompletionRule = (typeof assignmentCompletionRules)[number];
/** LRN-LIB step 4 (mig 600): what learners hand in for an assignment lesson. */
export const submissionModes = ['text_or_file', 'text', 'file'] as const;
export type SubmissionMode = (typeof submissionModes)[number];
/** LRN-LIB (mig 596): what a SCORM package must report for its lesson to count as completed. */
export const scormCompletionRules = ['completed', 'passed', 'completed_and_passed'] as const;
export type ScormCompletionRule = (typeof scormCompletionRules)[number];
/** LRN-LIB (mig 596): a content library item's kind — decides how the player shows it. */
/** 'text' (mig 604, v2 E3): .md / .txt — imported into a text lesson or handed out; never a lesson's own content. */
export const libraryItemKinds = ['video', 'audio', 'pdf', 'document', 'image', 'scorm', 'text'] as const;
export type LibraryItemKind = (typeof libraryItemKinds)[number];
/** The kinds each lesson type can show (mirror of LibraryItemKinds.ForLessonType). */
export const libraryKindsForLessonType: Partial<Record<LessonContentType, readonly LibraryItemKind[]>> = {
  video: ['video'],
  file: ['audio', 'pdf', 'document', 'image'],
  scorm: ['scorm']
};
/** How a learner got the course (ck_enrollments_source). Only 'event' learners take live sessions. */
export const enrollmentSources = ['self', 'admin', 'order', 'event'] as const;
export type EnrollmentSource = (typeof enrollmentSources)[number];
export const enrollmentStatuses = ['active', 'completed', 'dropped'] as const;
export type EnrollmentStatus = (typeof enrollmentStatuses)[number];

/** CourseSummaryDto */
export const courseSummarySchema = z.object({
  id: z.guid(),
  title: z.string(),
  status: z.enum(courseStatuses),
  createdAtUtc: isoDate,
  updatedAtUtc: isoDate,
  // LRN-RD (mig 578) — optional so older fixtures/servers still parse.
  summary: z.string().nullish(),
  courseTypeId: z.guid().nullish(),
  ageCategoryId: z.guid().nullish(),
  programTypeIds: z.array(z.guid()).nullish(),
  isFree: z.boolean().default(false),
  hasCoverImage: z.boolean().default(false),
  lessonCount: z.number().int().default(0),
  durationMinutes: z.number().int().default(0),
  /** Authors only — null for everyone else. */
  learnerCount: z.number().int().nullish(),
  // v2 E1 (mig 604): the admin table's row. completedCount / instructor* are authors-only (null otherwise).
  code: z.string().nullish(),
  moduleCount: z.number().int().default(0),
  completedCount: z.number().int().nullish(),
  instructorRef: z.guid().nullish(),
  instructorName: z.string().nullish()
});

/** v2 E1: GET /courses/stats — the admin course list's KPI tiles (completion = share who finished). */
export const courseStatsSchema = z.object({
  publishedCourses: z.number().int(),
  draftCourses: z.number().int(),
  activeLearners: z.number().int(),
  enrollments: z.number().int(),
  completedEnrollments: z.number().int(),
  completionPct: z.number().nullish()
});
export type CourseStats = z.infer<typeof courseStatsSchema>;

/**
 * PublicCourseDto (C-3) — a published course on the anonymous marketing catalog
 * (GET /api/v1/learning/public/courses). Title + description; no status
 * (public = always published) and no editor timestamps beyond updatedAtUtc.
 */
export const publicCourseSchema = z.object({
  id: z.guid(),
  title: z.string(),
  description: z.string().nullish(),
  updatedAtUtc: isoDate
});
export const publicCourseListSchema = z.array(publicCourseSchema);
export type PublicCourse = z.infer<typeof publicCourseSchema>;

/** CourseDetailDto */
export const courseDetailSchema = z.object({
  id: z.guid(),
  title: z.string(),
  description: z.string().nullish(),
  status: z.enum(courseStatuses),
  moduleCount: z.number().int(),
  lessonCount: z.number().int(),
  createdAtUtc: isoDate,
  updatedAtUtc: isoDate,
  // LRN-RD (mig 578)
  summary: z.string().nullish(),
  /** One outcome per line. */
  outcomes: z.string().nullish(),
  courseTypeId: z.guid().nullish(),
  ageCategoryId: z.guid().nullish(),
  programTypeIds: z.array(z.guid()).nullish(),
  language: z.string().nullish(),
  responsibleUserId: z.guid().nullish(),
  hasCoverImage: z.boolean().default(false),
  certificateTemplateId: z.guid().nullish(),
  isFree: z.boolean().default(false),
  sequential: z.boolean().default(false),
  durationMinutes: z.number().int().default(0),
  /** v2 D4 (mig 603): "Who this course is for" */
  audience: z.string().nullish(),
  /** v2 D4 (mig 603): a library video shown as the trailer — bytes at GET /courses/{id}/trailer */
  trailerLibraryItemId: z.guid().nullish(),
  /** v2 E1 (mig 604) */
  code: z.string().nullish()
});

/** ModuleDto */
export const moduleSchema = z.object({
  id: z.guid(),
  courseId: z.guid(),
  title: z.string(),
  sortOrder: z.number().int(),
  lessonCount: z.number().int(),
  createdAtUtc: isoDate,
  updatedAtUtc: isoDate
});

/** LRN-LIB step 5 (mig 601): how a lesson body is written — plain text, or sanitised rich text (HTML). */
export const lessonBodyFormats = ['text', 'html'] as const;
export type LessonBodyFormat = (typeof lessonBodyFormats)[number];

/** LessonDto */
export const lessonSchema = z.object({
  id: z.guid(),
  courseId: z.guid(),
  moduleId: z.guid(),
  title: z.string(),
  contentType: z.enum(lessonContentTypes),
  contentRef: z.string().nullish(),
  body: z.string().nullish(),
  sortOrder: z.number().int(),
  durationMinutes: z.number().int(),
  createdAtUtc: isoDate,
  updatedAtUtc: isoDate,
  // LRN-RD (mig 578)
  isFreePreview: z.boolean().default(false),
  /** quiz_ref only: a required quiz blocks what follows and counts toward completion. */
  isRequired: z.boolean().default(true),
  // live_session or scorm rule (the server keeps each to its own type)
  completionRule: z
    .enum([
      ...liveCompletionRules,
      ...scormCompletionRules,
      ...textCompletionRules,
      ...fileCompletionRules,
      ...videoCompletionRules,
      ...assignmentCompletionRules
    ])
    .nullish(),
  /** Learners only: true = locked by the sequential order; null for authors/visitors. */
  locked: z.boolean().nullish(),
  /** LRN-LIB (mig 596): the library item the lesson shows — null in the outline. */
  libraryItemId: z.guid().nullish(),
  file: z
    .object({
      kind: z.enum(libraryItemKinds),
      fileName: z.string(),
      contentType: z.string(),
      sizeBytes: z.number().nullish(),
      scormVersion: z.string().nullish(),
      // 598: read at upload; null when not known
      durationSeconds: z.number().nullish(),
      pageCount: z.number().nullish(),
      screenCount: z.number().nullish(),
      /** 601: a poster image — GET /lessons/{id}/thumbnail */
      hasThumbnail: z.boolean().default(false),
      /** 601: the video's caption languages — each read at GET /lessons/{id}/captions/{captionId} */
      captions: z
        .array(z.object({ id: z.guid(), language: z.string(), label: z.string() }))
        .nullish()
        .transform((v) => v ?? []),
      /** 612 (v2 B3.4): a document's PDF of the current file exists — GET /lessons/{id}/document */
      previewReady: z.boolean().default(false)
    })
    .nullish(),
  /** LRN-LIB step 2 (mig 598): per-type settings */
  allowDownload: z.boolean().default(true),
  transcript: z.string().nullish(),
  openInNewWindow: z.boolean().default(false),
  /** 612 (v2 B3.4): Document lessons — view in the player and download (true) or download only (false) */
  documentView: z.boolean().default(false),
  /** v2 B3.5: the library item's kind (pdf / audio / document / image…) — also on the outline, for the type label */
  fileKind: z.string().nullish(),
  /** LRN-LIB step 3 (mig 599): extra files handed out with the lesson — null when not loaded (create/update answers) */
  attachments: z
    .array(
      z.object({
        libraryItemId: z.guid(),
        title: z.string(),
        kind: z.enum(libraryItemKinds),
        fileName: z.string(),
        contentType: z.string(),
        sizeBytes: z.number().nullish(),
        durationSeconds: z.number().nullish(),
        pageCount: z.number().nullish()
      })
    )
    .nullish(),
  /** LRN-LIB step 4 (mig 600): the quiz whose pass completes a video / file lesson (rule 'quiz') */
  linkedQuizId: z.guid().nullish(),
  /** 600: an assignment lesson's grading settings (the instructions are the body) */
  assignment: z
    .object({
      assignmentId: z.guid(),
      maxScore: z.number(),
      passScore: z.number().nullish(),
      dueAtUtc: isoDate.nullish(),
      submissionMode: z.enum(submissionModes)
    })
    .nullish(),
  /**
   * LRN-LIB step 5 (mig 601): 'html' = rich text, already sanitised by the service — still DOMPurify it before
   * rendering. Images inside are <img data-library-item="…"> with no src: load each through the lesson.
   */
  bodyFormat: z.enum(lessonBodyFormats).default('text')
});

/** v2 D6 (mig 603): a learner's private note on a lesson (My notes). */
export const lessonNoteSchema = z.object({
  id: z.guid(),
  lessonId: z.guid(),
  positionSeconds: z.number().int().nullish(),
  body: z.string(),
  createdAtUtc: isoDate,
  updatedAtUtc: isoDate
});
export type LessonNote = z.infer<typeof lessonNoteSchema>;

const lessonAnswerSchema = z.object({ body: z.string(), byName: z.string().nullish(), atUtc: isoDate });

/** v2 D6 (mig 603): a lesson question as learners see it (asker shortened, isMine for the caller's own). */
export const lessonQuestionSchema = z.object({
  id: z.guid(),
  lessonId: z.guid(),
  askerName: z.string(),
  isMine: z.boolean(),
  body: z.string(),
  positionSeconds: z.number().int().nullish(),
  createdAtUtc: isoDate,
  answer: lessonAnswerSchema.nullish()
});
export type LessonQuestion = z.infer<typeof lessonQuestionSchema>;

/** v2 D6 (mig 603): a question in the admin inbox — full names, the lesson, hidden or not. */
export const courseQuestionSchema = z.object({
  id: z.guid(),
  lessonId: z.guid(),
  lessonTitle: z.string(),
  userId: z.guid(),
  askerName: z.string().nullish(),
  body: z.string(),
  positionSeconds: z.number().int().nullish(),
  createdAtUtc: isoDate,
  answer: lessonAnswerSchema.nullish(),
  hidden: z.boolean()
});
export type CourseQuestion = z.infer<typeof courseQuestionSchema>;

/** LRN-LIB step 4 (mig 600): LessonProgressDto — what the player has reported for one lesson. */
export const lessonProgressSchema = z.object({
  lessonId: z.guid(),
  playedSeconds: z.number(),
  durationSeconds: z.number().nullish(),
  maxPage: z.number().nullish(),
  pageCount: z.number().nullish(),
  percent: z.number(),
  completed: z.boolean()
});
export type LessonProgress = z.infer<typeof lessonProgressSchema>;

/** EnrollmentDto — UserId comes from the JWT server-side, never sent by the client */
export const enrollmentSchema = z.object({
  id: z.guid(),
  courseId: z.guid(),
  userId: z.guid(),
  status: z.enum(enrollmentStatuses),
  progressPct: z.number(),
  enrolledAtUtc: isoDate,
  completedAtUtc: isoDate.nullish(),
  droppedAtUtc: isoDate.nullish(),
  // LRN-RD (mig 578) / LRN-EVT (mig 579)
  source: z.enum(enrollmentSources).default('self'),
  /** The event (or order) that granted the course. */
  sourceRef: z.guid().nullish(),
  lastActivityAtUtc: isoDate.nullish(),
  /** End of an event-sourced enrollment's access (event end + 30 days); null = no end. */
  accessUntilUtc: isoDate.nullish(),
  /**
   * LRN-NAMES: the learner's display name (full name ?? email ?? username) and e-mail, resolved from Identity by
   * Learning. Filled only by GET /enrollments/all; null elsewhere and when Identity is unreachable — fall back
   * to the id, never join Identity on the client.
   */
  userDisplayName: z.string().nullish(),
  userEmail: z.string().nullish(),
  /** NAMES-SWEEP: the course title, read by Learning on GET /enrollments/all. Null elsewhere or for a deleted course. */
  courseTitle: z.string().nullish()
});

/** EnrollmentProgressDto — GET /learning/enrollments/{id}/progress (CourseProgress, server-side). */
export const enrollmentProgressSchema = z.object({
  enrollmentId: z.guid(),
  courseId: z.guid(),
  status: z.enum(enrollmentStatuses),
  progressPct: z.number(),
  countedLessons: z.number().int(),
  completedLessons: z.number().int(),
  nextLessonId: z.guid().nullish(),
  completedLessonIds: z.array(z.guid()),
  lockedLessonIds: z.array(z.guid()),
  /** Live sessions that are not this learner's to attend (they did not come through an event). */
  skippedLessonIds: z.array(z.guid()),
  sequential: z.boolean(),
  lastActivityAtUtc: isoDate.nullish()
});
export type EnrollmentProgress = z.infer<typeof enrollmentProgressSchema>;

/** LessonCompletionResultDto — fresh progress snapshot after a complete call */
export const lessonCompletionResultSchema = z.object({
  enrollmentId: z.guid(),
  lessonId: z.guid(),
  alreadyCompleted: z.boolean(),
  completedLessons: z.number().int(),
  totalLessons: z.number().int(),
  progressPct: z.number(),
  enrollmentStatus: z.enum(enrollmentStatuses)
});

// P5 teacher model — kursa atanmış eğitmen. userRef OPAK (identity.users.id).
/** CoursePrerequisiteDto — a prerequisite course (with its title/status) (P5-LD). */
export const coursePrerequisiteSchema = z.object({
  prerequisiteCourseId: z.guid(),
  title: z.string(),
  status: z.string(),
  createdAtUtc: isoDate
});
export type CoursePrerequisite = z.infer<typeof coursePrerequisiteSchema>;

export const instructorRoles = ['instructor', 'assistant'] as const;

/** InstructorDto — GET /learning/courses/{id}/instructors */
export const instructorSchema = z.object({
  userRef: z.guid(),
  role: z.enum(instructorRoles),
  assignedAt: isoDate,
  /** LRN-NAMES: the instructor's display name, resolved from Identity by Learning; null = unresolved (show the id). */
  displayName: z.string().nullish()
});

/** MyCourseDto — GET /learning/courses/mine (teacher-scoped; Faz 6 teacher SPA) */
export const myCourseSchema = z.object({
  courseId: z.guid(),
  title: z.string(),
  status: z.enum(courseStatuses),
  role: z.enum(instructorRoles)
});

export const courseListSchema = pagedListSchema(courseSummarySchema);
export const moduleListSchema = pagedListSchema(moduleSchema);
export const lessonListSchema = pagedListSchema(lessonSchema);

/** LRN-LIB (mig 596): LibraryItemDto */
export const libraryItemSchema = z.object({
  id: z.guid(),
  kind: z.enum(libraryItemKinds),
  title: z.string(),
  description: z.string().nullish(),
  fileName: z.string(),
  contentType: z.string(),
  /** null only for items backfilled from older lesson uploads */
  sizeBytes: z.number().nullish(),
  scormVersion: z.string().nullish(),
  usageCount: z.number().int(),
  createdAtUtc: isoDate,
  updatedAtUtc: isoDate,
  /** 597: the uploader's name as their token carried it — null for older or backfilled items */
  createdByName: z.string().nullish(),
  /** 598: read at upload; null when not known */
  durationSeconds: z.number().nullish(),
  pageCount: z.number().nullish(),
  screenCount: z.number().nullish(),
  /** 601: a thumbnail exists — GET /library/{id}/thumbnail */
  hasThumbnail: z.boolean().default(false),
  /**
   * 610 (v2 A1.7 / A5.1): ready | processing | failed. A video being prepared for streaming is "processing" (with
   * streamProgress 0..100); one that could not be is "failed" (streamError says why). Everything else is ready — and a
   * video ALWAYS plays (from its uploaded file until its stream is ready).
   */
  status: z.enum(['ready', 'processing', 'failed']).catch('ready').default('ready'),
  streamProgress: z.number().int().nullish(),
  streamError: z.string().nullish(),
  /** the renditions once the stream is ready, highest first (e.g. [1080, 720, 480]) */
  streamHeights: z.array(z.number().int()).nullish(),
  /** 612 (v2 B3.4): a document converted to PDF (viewable in the player) — GET /library/{id}/preview; why it failed */
  previewReady: z.boolean().default(false),
  previewError: z.string().nullish(),
  /** List only: the single course (and module · lesson for one use) the item is used in */
  usageSummary: z
    .object({
      courseCount: z.number().int(),
      courseTitle: z.string().nullish(),
      moduleTitle: z.string().nullish(),
      lessonTitle: z.string().nullish()
    })
    .nullish()
});
export type LibraryItem = z.infer<typeof libraryItemSchema>;

/**
 * 610 (v2 A5.1): LibraryStreamDto — GET /lessons/{id}/stream, /courses/{id}/trailer/stream, /library/{id}/stream.
 * \`url\` is the master playlist under a signed, expiring token, RELATIVE to the Learning API — null until the stream is
 * ready, when the player keeps using the uploaded file.
 */
export const libraryStreamSchema = z.object({
  status: z.enum(['ready', 'processing', 'failed']).catch('ready'),
  progress: z.number().int().nullish(),
  heights: z.array(z.number().int()).default([]),
  url: z.string().nullish(),
  expiresAtUtc: isoDate.nullish()
});
export type LibraryStream = z.infer<typeof libraryStreamSchema>;

/** 613 (v2 D1): StudySummaryDto — GET /learning/me/study?today=yyyy-MM-dd. Dates are the learner's LOCAL days. */
export const studySummarySchema = z.object({
  streakDays: z.number().int(),
  studiedToday: z.boolean(),
  todaySeconds: z.number().int(),
  weekSeconds: z.number().int(),
  weekStart: z.string(),
  weeklyGoalMinutes: z.number().int().nullish(),
  last7Days: z.array(z.object({ date: z.string(), seconds: z.number().int() })).default([])
});
export type StudySummary = z.infer<typeof studySummarySchema>;

/** 613 (v2 D1): StudyGoalDto — PUT /learning/me/study-goal. */
export const studyGoalSchema = z.object({ weeklyMinutes: z.number().int().nullish() });
export type StudyGoal = z.infer<typeof studyGoalSchema>;
export const libraryItemListSchema = pagedListSchema(libraryItemSchema);

/** One lesson that shows a library item. */
export const libraryItemUsageSchema = z.object({
  lessonId: z.guid(),
  lessonTitle: z.string(),
  lessonContentType: z.string(),
  courseId: z.guid(),
  courseTitle: z.string(),
  courseStatus: z.string(),
  moduleId: z.guid(),
  moduleTitle: z.string(),
  /** 599: handed out as an extra file rather than shown as the lesson's content */
  isExtraFile: z.boolean().default(false),
  /** 601: an image placed inside the lesson's rich text */
  isInText: z.boolean().default(false),
  /** 603: the course's trailer (no module or lesson) */
  isTrailer: z.boolean().default(false)
});
export type LibraryItemUsage = z.infer<typeof libraryItemUsageSchema>;

/** LRN-LIB step 5 (mig 601): one caption language of a library video. */
export const libraryCaptionSchema = z.object({
  id: z.guid(),
  language: z.string(),
  label: z.string(),
  sizeBytes: z.number(),
  updatedAtUtc: isoDate
});
export type LibraryCaption = z.infer<typeof libraryCaptionSchema>;

export const libraryItemDetailSchema = z.object({
  item: libraryItemSchema,
  usages: z.array(libraryItemUsageSchema),
  /** 601: a video's captions (empty for any other kind) */
  captions: z
    .array(libraryCaptionSchema)
    .nullish()
    .transform((v) => v ?? [])
});

/** LRN-LIB step 6 (mig 602): the standards a library package may follow — LibraryItem.scormVersion. */
export const packageStandards = ['1.2', '2004', 'xapi', 'cmi5'] as const;
/** xAPI and cmi5 packages report statements; their lesson completes through POST /lessons/{id}/package-check. */
export const reportsStatements = (standard: string | null | undefined) => standard === 'xapi' || standard === 'cmi5';

/** LRN-LIB step 6: PackageCheckDto — what the package has reported, and whether the lesson is now complete. */
export const packageCheckSchema = z.object({
  lessonId: z.guid(),
  reportedCompleted: z.boolean(),
  reportedPassed: z.boolean(),
  lessonCompleted: z.boolean()
});
export type PackageCheck = z.infer<typeof packageCheckSchema>;

/** LRN-LIB: a SCORM play link — playPath is relative to the Learning base address. */
export const scormLaunchSchema = z.object({
  playPath: z.string(),
  scormVersion: z.string(),
  expiresAtUtc: isoDate
});
export type ScormLaunch = z.infer<typeof scormLaunchSchema>;

/** #13 ScormTrackingDto — one learner's saved state for one activity (a SCORM lesson's id). */
export const scormTrackingSchema = z.object({
  activityRef: z.guid(),
  lessonStatus: z.string(),
  completionStatus: z.string().nullish(),
  successStatus: z.string().nullish(),
  scoreScaled: z.number().nullish(),
  scoreRaw: z.number().nullish(),
  location: z.string().nullish(),
  suspendData: z.string().nullish(),
  totalTimeSeconds: z.number(),
  attempts: z.number(),
  cmiData: z.string().nullish(),
  updatedAtUtc: isoDate.nullish()
});
export type ScormTracking = z.infer<typeof scormTrackingSchema>;
export const enrollmentListSchema = pagedListSchema(enrollmentSchema);
export const myCourseListSchema = pagedListSchema(myCourseSchema);

export type CourseSummary = z.infer<typeof courseSummarySchema>;
export type CourseDetail = z.infer<typeof courseDetailSchema>;
export type CourseModule = z.infer<typeof moduleSchema>;
export type Lesson = z.infer<typeof lessonSchema>;
export type Enrollment = z.infer<typeof enrollmentSchema>;
export type LessonCompletionResult = z.infer<typeof lessonCompletionResultSchema>;
export type Instructor = z.infer<typeof instructorSchema>;
export type MyCourse = z.infer<typeof myCourseSchema>;

/** CreateCourseCommand body */
export interface CreateCourseRequest {
  title: string;
  description?: string | null;
}

/** CreateLessonCommand body (moduleId travels in the URL) */
export interface CreateLessonRequest {
  title: string;
  contentType: (typeof lessonContentTypes)[number];
  contentRef?: string | null;
  body?: string | null;
  durationMinutes: number;
  /** LRN-RD — omitted = server default (false / true / join_or_recording for live). */
  isFreePreview?: boolean;
  isRequired?: boolean;
  completionRule?: LiveCompletionRule | ScormCompletionRule | TextCompletionRule | FileCompletionRule | VideoCompletionRule | AssignmentCompletionRule | null;
  /** LRN-LIB: a library item instead of a link (video / file); required for scorm. */
  libraryItemId?: string | null;
  /** LRN-LIB step 2 (598): file lessons showing a PDF — false hides the player's download button */
  allowDownload?: boolean;
  /** 598: file lessons showing audio */
  transcript?: string | null;
  /** 598: scorm lessons */
  openInNewWindow?: boolean;
  /** 612 (v2 B3.4): document lessons — omitted on create = view; on update = keep */
  documentView?: boolean;
  /** 600: rule 'quiz' */
  linkedQuizId?: string | null;
  /** 600: assignment lessons */
  assignmentMaxScore?: number | null;
  assignmentPassScore?: number | null;
  assignmentDueAt?: string | null;
  assignmentSubmissionMode?: SubmissionMode | null;
  /** 601: 'html' = rich text (text lessons only) */
  bodyFormat?: LessonBodyFormat;
}

/** LRN-RD course settings (Create/Update body; PUT needs replaceSettings: true to apply them). */
export interface CourseSettingsRequest {
  summary?: string | null;
  outcomes?: string | null;
  courseTypeId?: string | null;
  ageCategoryId?: string | null;
  programTypeIds?: string[] | null;
  language?: string | null;
  responsibleUserId?: string | null;
  certificateTemplateId?: string | null;
  isFree?: boolean | null;
  sequential?: boolean | null;
  /** v2 D4 (mig 603) */
  audience?: string | null;
  trailerLibraryItemId?: string | null;
  /** v2 E1 (mig 604): optional, unique per tenant (case-insensitive) */
  code?: string | null;
}

// ==============================|| LRN-EVT / LRN-LIVE (Engagement :5800) ||============================== //

/** EventCourseRowDto — one filled cell of an event's course grid (mig 579). No row = no course. */
export const eventCourseRowSchema = z.object({
  programTypeId: z.guid(),
  ageCategoryId: z.guid().nullish(),
  courseId: z.guid()
});
export const eventCourseGridSchema = z.object({ rows: z.array(eventCourseRowSchema) });
export type EventCourseRow = z.infer<typeof eventCourseRowSchema>;
export interface SetEventCoursesRequest {
  rows: { programTypeId: string; ageCategoryId: string | null; courseId: string }[];
}

/** EventByCourseDto — GET /engagement/events/by-course?courseIds=a,b (one row per event x course). */
export const eventByCourseSchema = z.object({
  courseId: z.guid(),
  eventId: z.guid(),
  title: z.string(),
  status: z.string(),
  startsAt: isoDate
});
export const eventByCourseListSchema = z.array(eventByCourseSchema);
export type EventByCourse = z.infer<typeof eventByCourseSchema>;

/** LiveSessionDto — admin view of one scheduled live lesson (mig 580). */
export const liveSessionSchema = z.object({
  id: z.guid(),
  eventId: z.guid(),
  courseId: z.guid(),
  lessonId: z.guid(),
  programTypeId: z.guid(),
  ageCategoryId: z.guid().nullish(),
  startsAtUtc: isoDate,
  endsAtUtc: isoDate,
  teacherRef: z.guid(),
  teacherName: z.string().nullish(),
  meetingUrl: z.string().nullish(),
  passcode: z.string().nullish(),
  hasRecording: z.boolean(),
  recordingSizeBytes: z.number().nullish(),
  recordingUploadedAtUtc: isoDate.nullish(),
  attendedCount: z.number().int(),
  /** False when the teacher is no longer one of the course's instructors. */
  teacherIsInstructor: z.boolean(),
  /** False when the course grid no longer maps this cell to this course — nobody's session. */
  cellMatches: z.boolean(),
  lessonTitle: z.string().nullish()
});
export const liveSessionListSchema = z.array(liveSessionSchema);
export type LiveSession = z.infer<typeof liveSessionSchema>;
export interface SaveLiveSessionRequest {
  lessonId: string;
  programTypeId: string;
  ageCategoryId: string | null;
  startsAtUtc: string;
  endsAtUtc: string;
  teacherRef: string;
  teacherName?: string | null;
  meetingUrl?: string | null;
  passcode?: string | null;
  lessonTitle?: string | null;
}

export const liveSessionStates = ['upcoming', 'open', 'ended'] as const;
export type LiveSessionState = (typeof liveSessionStates)[number];

/** MyLiveSessionDto — the student's view (GET /engagement/live-sessions/mine?courseId=). No link: Join returns it. */
export const myLiveSessionSchema = z.object({
  id: z.guid(),
  eventId: z.guid(),
  eventTitle: z.string(),
  courseId: z.guid(),
  lessonId: z.guid(),
  startsAtUtc: isoDate,
  endsAtUtc: isoDate,
  joinOpensAtUtc: isoDate,
  teacherName: z.string().nullish(),
  lessonTitle: z.string().nullish(),
  state: z.enum(liveSessionStates),
  attended: z.boolean(),
  hasRecording: z.boolean(),
  recordingWatched: z.boolean()
});
export const myLiveSessionListSchema = z.array(myLiveSessionSchema);
export type MyLiveSession = z.infer<typeof myLiveSessionSchema>;

/** LiveSessionJoinDto — POST /engagement/live-sessions/{id}/join (records attendance). */
export const liveSessionJoinSchema = z.object({
  sessionId: z.guid(),
  meetingUrl: z.string(),
  passcode: z.string().nullish()
});

/** TeachingSessionDto — GET /engagement/live-sessions/teaching (the caller is the teacher). */
export const teachingSessionSchema = z.object({
  id: z.guid(),
  eventId: z.guid(),
  eventTitle: z.string(),
  courseId: z.guid(),
  lessonId: z.guid(),
  programTypeId: z.guid(),
  ageCategoryId: z.guid().nullish(),
  startsAtUtc: isoDate,
  endsAtUtc: isoDate,
  meetingUrl: z.string().nullish(),
  passcode: z.string().nullish(),
  state: z.enum(liveSessionStates),
  lessonTitle: z.string().nullish()
});
export const teachingSessionListSchema = z.array(teachingSessionSchema);
export type TeachingSession = z.infer<typeof teachingSessionSchema>;

export const liveAttendanceSources = ['join', 'admin', 'recording'] as const;

/** LiveAttendanceRowDto — eligible: true/false, or null when the student's band could not be checked. */
export const liveAttendanceRowSchema = z.object({
  userRef: z.guid(),
  /** PARTICIPANT-NAME: the seat holder's CURRENT name (Identity full_name ?? email ?? username), filled by Engagement on every response — not a stored copy (event_registrations.registrant_name is retired). Null only when Identity could not answer. */
  registrantName: z.string().nullish(),
  eligible: z.boolean().nullish(),
  attended: z.boolean(),
  source: z.enum(liveAttendanceSources).nullish(),
  firstJoinedAtUtc: isoDate.nullish(),
  joinCount: z.number().int(),
  correctedAtUtc: isoDate.nullish(),
  recordingWatchedAtUtc: isoDate.nullish()
});
export const liveAttendanceSchema = z.object({
  sessionId: z.guid(),
  rows: z.array(liveAttendanceRowSchema)
});
export type LiveAttendanceRow = z.infer<typeof liveAttendanceRowSchema>;

// ---- Homework / assignments (Phase B B-Edu; migration 123) ----
export const assignmentStatuses = ['draft', 'published', 'closed'] as const;
export const submissionStatuses = ['submitted', 'graded', 'returned'] as const;

/** AssignmentDto — a course's homework assignment. */
export const assignmentSchema = z.object({
  id: z.guid(),
  courseId: z.guid(),
  title: z.string(),
  instructions: z.string().nullish(),
  maxScore: z.number(),
  dueAtUtc: isoDate.nullish(),
  status: z.enum(assignmentStatuses),
  publishedAtUtc: isoDate.nullish(),
  createdAtUtc: isoDate
});

/** SubmissionDto — a student's submission. studentRef/attachmentRef are opaque [STD-OWN]. */
export const submissionSchema = z.object({
  id: z.guid(),
  assignmentId: z.guid(),
  studentRef: z.guid(),
  content: z.string().nullish(),
  attachmentRef: z.string().nullish(),
  status: z.enum(submissionStatuses),
  score: z.number().nullish(),
  feedback: z.string().nullish(),
  gradedAtUtc: isoDate.nullish(),
  submittedAtUtc: isoDate
});

export const assignmentListSchema = pagedListSchema(assignmentSchema);
export const submissionListSchema = pagedListSchema(submissionSchema);

/** AttachmentUploadResultDto — POST /assignments/{id}/attachment cevabı; attachmentRef opak (A-4b) [STD-OWN]. */
export const attachmentUploadResultSchema = z.object({
  attachmentRef: z.string().min(1),
  fileName: z.string().min(1),
  sizeBytes: z.number(),
  contentType: z.string().min(1)
});

export type Assignment = z.infer<typeof assignmentSchema>;
export type Submission = z.infer<typeof submissionSchema>;
export type AttachmentUploadResult = z.infer<typeof attachmentUploadResultSchema>;
export type AssignmentStatus = (typeof assignmentStatuses)[number];

/** CreateAssignmentCommand body */
export interface CreateAssignmentRequest {
  courseId: string;
  title: string;
  instructions?: string | null;
  maxScore: number;
  dueAt?: string | null;
}

/** SubmitAssignmentCommand body (assignmentId travels in the URL) */
export interface SubmitAssignmentRequest {
  content?: string | null;
  attachmentRef?: string | null;
}

/** GradeSubmissionCommand body (submissionId travels in the URL) */
export interface GradeSubmissionRequest {
  score: number;
  feedback?: string | null;
}

/**
 * RecommendationDto — GET /learning/recommendations?limit= (FE-9.3 "Sana önerilen").
 * Data is a plain array (not paged); score is a 0..1 relevance weight, reason is a short
 * server-resolved label the UI shows as a chip. courseId links to the catalog detail route.
 */
export const recommendationSchema = z.object({
  courseId: z.guid(),
  title: z.string(),
  score: z.number(),
  reason: z.string()
});
export const recommendationListSchema = z.array(recommendationSchema);
export type Recommendation = z.infer<typeof recommendationSchema>;

// ==============================|| EXAM (:5700) ||============================== //

export const examStatuses = ['draft', 'published', 'closed'] as const;

/**
 * EXAM-RELEASE (migration 510) — the results-release POLICY (`exam.exams.results_visibility`).
 *
 * `immediate` is the default and what every exam did before the column existed: a mark reaches its
 * candidate the moment a grader enters it. `on_release` withholds the marks — and only the marks —
 * until `resultsReleaseAtUtc`.
 */
export const examResultsVisibilities = ['immediate', 'on_release'] as const;

/**
 * EXAM-DETAIL (mig 570) — what a CANDIDATE sees of their result. `score_only`: the server strips every
 * per-question mark, comment and AI note from the candidate's read, and refuses the paper once the attempt is
 * over (403 `exam-paper-hidden-after-attempt`). New exams default to score_only; pre-570 exams are `full`.
 */
export const examResultsDetails = ['full', 'score_only'] as const;
export type ExamResultsDetail = (typeof examResultsDetails)[number];
/** Unknown/absent (a pre-570 payload, a newer value) reads as `full` — what every older exam showed. */
const resultsDetailField = z.enum(examResultsDetails).catch('full');
export type ExamResultsVisibility = (typeof examResultsVisibilities)[number];

/**
 * EXAM-CALC (migration 518) — the calculator a paper PROVIDES on screen
 * (`exam.exams.calculator_mode`).
 *
 * `none` is the default and what every exam did before the column existed. `basic` is four
 * functions, percent, sign and a short tape; `scientific` adds brackets, powers, roots, logs and
 * trigonometry with a degrees/radians switch.
 *
 * ⚠ IT IS NOT AN INVIGILATION CONTROL. `none` says the platform provides nothing; it cannot stop a
 * device the platform never sees. The rule about what may sit on a desk belongs with the invigilator.
 */
/**
 * EXAM-RESUME (migration 543) — can a candidate go back to a question they have moved past?
 *
 * ⚠ `forward_only` IS ENFORCED BY THE SERVER: a save to a closed question and a move back to one both
 * 409 (`exam-question-closed`, `exam-position-not-allowed`). Hiding the Previous button is the UI's half
 * only — a reload or a second tab would otherwise undo it.
 */
export const examNavigationModes = ['free', 'forward_only'] as const;
export type ExamNavigationMode = (typeof examNavigationModes)[number];

export const examCalculatorModes = ['none', 'basic', 'scientific'] as const;
export type ExamCalculatorMode = (typeof examCalculatorModes)[number];

/**
 * EXAM-MATHANS (migration 523) — the on-screen maths keyboard a paper PROVIDES
 * (`exam.exams.keyboard_mode`).
 *
 * `none` is the default. `basic` is digits, the four operators and a decimal point; `full` adds
 * fractions, roots, powers, subscripts and Greek.
 *
 * ⚠ SEPARATE FROM THE CALCULATOR, not a mode of it. A calculator is for working something out, a
 * keyboard is for typing the answer — an algebra paper that forbids a calculator still has to let a
 * candidate on a tablet enter the fraction it is asking for.
 */
export const examKeyboardModes = ['none', 'basic', 'full'] as const;
export type ExamKeyboardMode = (typeof examKeyboardModes)[number];

/**
 * EXAM-RELEASE — have this exam's results been announced yet?
 *
 * ⚠ THE SERVER IS THE AUTHORITY, and this function is NOT how a candidate's page decides what to show:
 * `attempt.resultsReleased` is computed server-side and the marks are physically absent from a withheld
 * payload. This exists for the ADMIN side, which reads an exam's own configuration and has to render
 * "held" / "scheduled" / "published" from the stored pair.
 *
 * Mirrors `ExamResultsRelease.IsReleased` — including the inclusive boundary (results due at 09:00 are
 * readable AT 09:00) and the fail-closed default, where anything other than `immediate` without a past
 * moment counts as held.
 */
export function resultsAreReleased(
  visibility: string | null | undefined,
  releaseAtUtc: string | null | undefined,
  now: Date = new Date()
): boolean {
  if (visibility === 'immediate') return true;
  if (visibility !== 'on_release' || !releaseAtUtc) return false;
  return new Date(releaseAtUtc).getTime() <= now.getTime();
}

/** ExamSummaryDto */
export const examSummarySchema = z.object({
  id: z.guid(),
  title: z.string(),
  status: z.string(),
  durationMinutes: z.number().int(),
  passScore: z.number(),
  courseRef: z.guid().nullish(),
  opensAtUtc: isoDate.nullish(),
  closesAtUtc: isoDate.nullish(),
  createdAtUtc: isoDate,
  updatedAtUtc: isoDate,
  /**
   * How many questions the paper holds, and what it is worth in total.
   *
   * ⚠ `totalMarks` is NULL WHILE THE EXAM IS A DRAFT — no snapshot, so no total yet. It is summed from
   * the FROZEN snapshot marks, so it is exactly what a candidate can score, per-exam weights and all
   * (EXAM-WEIGHT). Render the null as "not set yet", never as 0.
   *
   * `nullish`/`default` throughout so a payload from an instance that predates these fields still
   * parses instead of blanking the whole list during a rolling deploy.
   */
  questionCount: z.number().int().optional().default(0),
  totalMarks: z.number().int().nullish(),
  /** NULL = unlimited attempts (EXAM-AUTH). */
  maxAttempts: z.number().int().nullish(),
  /**
   * EXAM-RELEASE (migration 510) — on the summary so the list can flag which cohorts are still being
   * held. See `examDetailSchema` for what the pair means and `resultsAreReleased()` for how to read it.
   */
  resultsVisibility: z.enum(examResultsVisibilities).optional().default('immediate'),
  resultsReleaseAtUtc: isoDate.nullish(),
  /**
   * EXAM-CALC (migration 518) — on the summary because this list is ALSO the candidate's list
   * (`ListAttemptableExamsQuery` returns the summary shape), and "basic calculator provided" has to
   * be readable before the paper is started.
   */
  calculatorMode: z.enum(examCalculatorModes).optional().default('none'),
  /** EXAM-MATHANS (migration 523) — on the summary for the same reason as the calculator. */
  keyboardMode: z.enum(examKeyboardModes).optional().default('none'),
  /** A4 — starting this paper needs the exam access code. Never the code itself. Absent = pre-A4 = no. */
  requiresAccessCode: z.boolean().optional().default(false),
  /** A5 — the supervision tier (none | standard | strict), so the card can say what is recorded before Start. */
  proctoringTier: z.string().optional().default('none'),
  /** A6 — starting needs an allowed network (the exam's IP allowlist). Never the ranges. Absent = no. */
  requiresAllowedNetwork: z.boolean().optional().default(false),
  /** A7 — the paper is sat in fullscreen, said on the card before Start. Absent = no. */
  requiresFullscreen: z.boolean().optional().default(false),
  /** EXTRA-TIME — THIS candidate's arrangement minutes on the paper (candidate list only; 0 elsewhere). */
  extraTimeMinutes: z.number().int().optional().default(0),
  /**
   * EXAM-PROG (migration 532) — the programme this paper belongs to, and optionally the age band.
   *
   * Opaque `definitions` ids: `programTypeId` names a value in group `program_types`, `ageCategoryId`
   * a row in `definitions.age_categories` that BELONGS to that programme. Resolve both to names with
   * `useDefinitionOptions('program_types')` / `useAgeCategoryOptions(programTypeId)`.
   *
   * ⚠ NULLISH ON THE SUMMARY EVEN THOUGH THE COLUMN IS NOT NULL. The candidate's list
   * (`ListAttemptableExamsQuery`) builds this same shape and deliberately leaves them unset — a
   * programme label classifies a paper for whoever manages it and decides nothing about who may sit
   * it (that is EXAM-AUD). Required here would blank the student's whole exam list.
   */
  programTypeId: z.guid().nullish(),
  ageCategoryId: z.guid().nullish(),
  /** EXAM-RESUME — "you can't go back" is said on the candidate's card before Start. Absent = free. */
  navigationMode: z.enum(examNavigationModes).optional().default('free'),
  /**
   * EXAM-SCHED — the CALLER is named on this paper's enrolment roll (candidate list only; false elsewhere).
   * The student page files a not-yet-open paper under "Your scheduled exams" only when this is true.
   * Absent = a server that predates it = an empty scheduled section, never a blank page.
   */
  enrolled: z.boolean().optional().default(false),
  /**
   * LATEST-ONLY: how many attempts the CALLER has made at this paper, every state counted (the server's own
   * limit rule). Set only on the candidate's list. The student's attempt list carries only the newest attempt
   * per exam, so the client cannot count this itself any more. Absent (an older server) reads 0.
   */
  attemptsUsed: z.number().int().optional().default(0),
  /** v2 F7 (mig 609): the exam's short code ("MAT-301"), when it has one. */
  code: z.string().nullish(),

  // ---- EXAM-COLS: the admin list's detail columns. Only ListExamsQuery fills them; every one is optional so
  // the candidate's list (same shape) and an older server parse unchanged.
  enrolledCount: z.number().int().optional(),
  enrollmentRequired: z.boolean().optional(),
  satCount: z.number().int().optional(),
  notYetSatCount: z.number().int().optional(),
  attemptCount: z.number().int().optional(),
  inProgressCount: z.number().int().optional(),
  awaitingMarkCount: z.number().int().optional(),
  openReviewCount: z.number().int().optional(),
  sittingCount: z.number().int().optional(),
  nextSittingAtUtc: z.string().nullish(),
  publishedAtUtc: z.string().nullish(),
  closedAtUtc: z.string().nullish(),
  /** full | score_only — a plain string so a new value cannot fail the list's parse. */
  resultsDetail: z.string().nullish(),
  reviewRequestsAllowed: z.boolean().optional(),
  awardMedals: z.boolean().optional(),
  retakeCooldownMinutes: z.number().int().nullish(),
  /** True when the caller may read marks (exam.attempts.grade) and the results below are filled. */
  marksVisible: z.boolean().optional(),
  /** Results over each student's LATEST FINISHED attempt (the Rankings rule). Null without the permission. */
  gradedCount: z.number().int().nullish(),
  passedCount: z.number().int().nullish(),
  passRatePct: z.number().nullish(),
  averageScorePct: z.number().nullish(),
  highestScorePct: z.number().nullish(),
  /** Students holding a medal; null when the exam awards none. */
  medalistCount: z.number().int().nullish()
});

/** ExamDetailDto */
export const examDetailSchema = z.object({
  id: z.guid(),
  title: z.string(),
  description: z.string().nullish(),
  status: z.string(),
  durationMinutes: z.number().int(),
  passScore: z.number(),
  /**
   * G1 (migration 167). The server has sent this since the column existed; this schema never declared
   * it, so zod stripped it and the SPA could not tell a proctored exam from an unproctored one.
   * `optional().default(false)` rather than required — the same shape `attemptSchema` uses for the same
   * field, so a payload from an older instance during a rolling deploy still parses instead of blanking
   * the whole page.
   */
  requiresProctoring: z.boolean().optional().default(false),
  /**
   * EXAM-AUTH (migration 502) — the attempt policy. NULL on both means the behaviour every exam had
   * before the columns existed: unlimited attempts, no wait. `nullish` rather than required so a payload
   * from a pre-502 instance during a rolling deploy still parses.
   */
  maxAttempts: z.number().int().nullish(),
  retakeCooldownMinutes: z.number().int().nullish(),
  courseRef: z.guid().nullish(),
  opensAtUtc: isoDate.nullish(),
  closesAtUtc: isoDate.nullish(),
  publishedAtUtc: isoDate.nullish(),
  closedAtUtc: isoDate.nullish(),
  questionCount: z.number().int(),
  createdAtUtc: isoDate,
  updatedAtUtc: isoDate,
  /**
   * EXAM-RELEASE (migration 510) — the POLICY and the MOMENT, two facts rather than one.
   *
   * ⚠ "ARE RESULTS OUT RIGHT NOW" IS NEITHER OF THEM, and it is deliberately not on the wire: this DTO
   * is cached server-side, so a stored boolean would be wrong from the second a scheduled moment passed.
   * Resolve it with `resultsAreReleased()`.
   *
   * `resultsReleaseAtUtc` NULL under `on_release` means HELD WITH NO DATE DECIDED — the state that
   * exists precisely because a lone nullable timestamp could not express it.
   *
   * `optional().default('immediate')` so a payload from a pre-510 instance during a rolling deploy
   * parses as what it in fact does, rather than blanking the whole page.
   */
  resultsVisibility: z.enum(examResultsVisibilities).optional().default('immediate'),
  resultsReleaseAtUtc: isoDate.nullish(),
  /**
   * EXAM-CALC (migration 518) — what this paper provides. NOT what a candidate currently sitting it
   * has: that is on the attempt (`attemptSchema.calculatorMode`) and can differ, because changing
   * this setting deliberately does not reach a paper already in progress.
   */
  calculatorMode: z.enum(examCalculatorModes).optional().default('none'),
  /** EXAM-MATHANS (migration 523) — what the paper provides, not what a sitting candidate has. */
  keyboardMode: z.enum(examKeyboardModes).optional().default('none'),
  /**
   * EXAM-PROG (migration 532) — the classification the settings form edits and reads back.
   *
   * `programTypeId` is REQUIRED server-side (the column is NOT NULL, like an event's since 236), but
   * it is nullish here so a payload from a pre-532 instance still parses during a rolling deploy
   * rather than blanking the exam page. `ageCategoryId` is genuinely optional: null means every band
   * of the programme, which is also the only answer available for a programme that defines none.
   */
  programTypeId: z.guid().nullish(),
  ageCategoryId: z.guid().nullish(),
  /**
   * EXAM-ENROL (migration 538) — does an EMPTY roll mean "nobody" for this paper, or "no restriction"?
   *
   * ⚠ NULLISH AND FALSY-BY-DEFAULT, deliberately. A response from an older Exam service omits it, and
   * the safe reading of a missing value is the pre-538 one: the paper is open. Reading absence as
   * "restricted" would make the Enrollments tab announce that nobody can sit a paper everybody can.
   *
   * ⚠ ROWS ON THE ROLL RESTRICT EITHER WAY. This flag decides only what an EMPTY roll means.
   */
  enrollmentRequired: z.boolean().nullish().transform((v) => v ?? false),
  /** EXAM-DETAIL (mig 570): see examResultsDetails. */
  resultsDetail: resultsDetailField,
  /** EXAM-REVIEW-OPT (mig 574): may students send a new review request. Off unless the server says on. */
  reviewRequestsAllowed: z.boolean().optional().default(false),
  /** MEDAL (mig 587): are medals awarded on this exam (see ./medals). Off unless the server says on. */
  awardMedals: z.boolean().optional().default(false),
  /** v2 F7 (mig 609): optional, unique in the tenant; changed through PUT …/code at any stage. */
  code: z.string().nullish()
});

/**
 * EXAM-AUD (migration 503) — one audience rule on an exam.
 *
 * ⚠ `audienceMode`, NOT `mode`, all the way down to the column. `mode` is a built-in ordered-set
 * aggregate in PostgreSQL, so the server names it `audience_mode`; shortening it here would be a rename
 * at the wire that reads as a tidy-up.
 *
 * ⚠ `value` IS AN OPAQUE ID — a definitions.definitions.id for country/gender, an identity.schools.id for
 * school. Exam never resolves it [STD-OWN]; the SPA resolves labels from the catalogues it already loads.
 *
 * NOT PAGED: an exam's rule set is small and is written as a whole. An EMPTY array is the meaningful
 * answer "unconstrained", not a missing one.
 */
export const examAudienceRuleSchema = z.object({
  dimension: z.enum(['country', 'gender', 'school']),
  audienceMode: z.enum(['include', 'exclude']),
  value: z.string()
});
export const examAudienceListSchema = z.array(examAudienceRuleSchema);
export type ExamAudienceRule = z.infer<typeof examAudienceRuleSchema>;

/**
 * EXAM-ENROL (migration 536) — one person on an exam's named roll.
 *
 * ⚠ AN EMPTY LIST IS THE ANSWER "no restriction", not a missing one, and the FIRST row makes the exam
 * private — the exam's audience rules stop being evaluated from that moment. The tab says so; nothing
 * in this shape can.
 *
 * EXAM-NAMES: `userDisplayName` (profile name ?? email ?? username — `userOptionLabel`), `userEmail` and
 * `userUsername` are added by the list endpoint from Identity; null = unresolved, so fall back to the id.
 * The AGE BAND is not on the row — the SPA still asks Identity for that (no batch source in Exam).
 *
 * `attemptCount` is NOT a gate — removal is allowed with attempts on record, and the attempts survive
 * it. It exists so the confirmation dialog can say what is about to happen.
 */
export const examEnrollmentSchema = z.object({
  userId: z.guid(),
  createdAt: isoDate,
  createdBy: z.guid().nullish(),
  attemptCount: z.number().int(),
  /** EXAM-NOTIFY (mig 568): when this student was last emailed about this exam; null = never. */
  notifiedAtUtc: isoDate.nullish(),
  /** EXAM-NAMES: from Identity, added by the list endpoint. null = unresolved. */
  userDisplayName: z.string().nullish(),
  userEmail: z.string().nullish(),
  userUsername: z.string().nullish(),
  /**
   * ENROL-RESULTS: the grader's columns. Filled only when the caller holds exam.attempts.grade
   * (`marksVisible`); absent/null for anyone else and from an older server.
   */
  marksVisible: z.boolean().optional().default(false),
  /** The NEWEST attempt (any state); null = no attempt. */
  latestAttemptId: z.guid().nullish(),
  /** started | submitted | graded — a plain string, so a new state cannot fail the roll's parse. */
  latestStatus: z.string().nullish(),
  /** Graded attempts only. */
  latestScorePct: z.number().nullish(),
  latestPassed: z.boolean().nullish(),
  /** The medal the ranking awards (the Medals tab's answer); null = none / the exam awards none. */
  medalId: z.guid().nullish(),
  medalTitle: z.string().nullish(),
  medalColor: z.string().nullish(),
  medalOrder: z.number().int().nullish()
});
export const examEnrollmentListSchema = pagedListSchema(examEnrollmentSchema);
export type ExamEnrollment = z.infer<typeof examEnrollmentSchema>;

/**
 * EXAM-NOTIFY — POST /exam/exams/{id}/enrollments/notify, body `{ userIds } | { all: true }` + optional
 * `message` (plain text, ≤ 2000). Email + in-app from the tenant's `exam.enrollment_notice` template.
 * Every id must be on the roll or NOTHING is sent (422 `exam-enrollment-not-enrolled`, `userIds` lists them).
 * `previouslyNotified` = how many of the recipients had been emailed before this send.
 */
export const EXAM_NOTIFY_MAX_MESSAGE = 2000;
export const EXAM_NOTIFY_MAX_RECIPIENTS = 2000;
export const examEnrollmentNotifyResultSchema = z.object({
  examId: z.guid(),
  sent: z.number().int(),
  previouslyNotified: z.number().int()
});
export type ExamEnrollmentNotifyResult = z.infer<typeof examEnrollmentNotifyResultSchema>;

/** What an add/remove actually did — "added 3 of 40" is the sentence the tab reports. */
export const examEnrollmentMutationSchema = z.object({
  examId: z.guid(),
  added: z.number().int(),
  alreadyEnrolled: z.number().int(),
  removed: z.number().int(),
  totalItems: z.number().int()
});
export type ExamEnrollmentMutation = z.infer<typeof examEnrollmentMutationSchema>;

/**
 * EXAM-ENROL: the event that uses a given exam, from Engagement.
 *
 * ⚠ EVERY FIELD IS NULLABLE BECAUSE "no event uses this paper" IS THE COMMON ANSWER, and the server
 * returns 200 with empty fields rather than 404 — it cannot tell an unused exam from an id that names
 * no exam at all, since exam_id is opaque on its side [STD-OWN].
 */
export const eventByExamSchema = z.object({
  /**
   * ⚠ WHICH EXAM THIS ROW ANSWERS FOR. The batch endpoint returns only the exams that ARE used, so a
   * caller matching by POSITION lines the answers up against the wrong questions the moment one exam
   * in the page has no event — which is most of them.
   */
  examId: z.guid(),
  eventId: z.guid().nullish(),
  title: z.string().nullish(),
  status: z.string().nullish(),
  programTypeId: z.guid().nullish(),
  ageCategoryId: z.guid().nullish()
});
export type EventByExam = z.infer<typeof eventByExamSchema>;
export const eventByExamListSchema = z.array(eventByExamSchema);

// ---- EXAM-SIT (migrations 505/506): scheduled sittings + the attendance register ----

export const sittingStatuses = ['scheduled', 'open', 'closed', 'cancelled'] as const;
export type SittingStatus = (typeof sittingStatuses)[number];

/**
 * ⚠ NAMED `sittingAttendanceStatuses`, NOT `attendanceStatuses` — engagement.ts ALREADY EXPORTS THAT
 * NAME for course-session attendance, and the api-client re-exports both modules flat, so the shorter
 * name is a genuine collision (it broke Attendance.tsx the moment it was added). The two vocabularies
 * are not interchangeable either: this one has no `late`, that one does. Keeping the ambiguous name
 * for neither is the same call the server made when two services both had a CreateTemplateVersionCommand.
 *
 * ⚠ NO `late`, unlike the course-attendance register this deliberately echoes. An attempt's deadline
 * runs from the moment it STARTS, so a candidate who arrives twenty minutes in still gets their full
 * duration and is simply present. Mirrors ck_sitting_candidates_status.
 */
export const sittingAttendanceStatuses = ['expected', 'present', 'absent', 'excused'] as const;
export type SittingAttendanceStatus = (typeof sittingAttendanceStatuses)[number];

/** Statuses an invigilator may set by hand — `expected` is the ABSENCE of a decision, not one. */
export const markableSittingAttendanceStatuses = ['present', 'absent', 'excused'] as const;

/**
 * SittingDto.
 *
 * ⚠ `hasStartCode` IS A FLAG, NOT THE CODE. The value has its own endpoint and its own permission
 * (`exam.sittings.invigilate`) precisely so it does not ride along in every payload that renders a
 * sitting. Do not add it here.
 *
 * ⚠ `remainingSeats` MAY BE NEGATIVE, and that is information rather than a bug: capacity can be
 * lowered under an existing roster on purpose (a room really does shrink). Render the negative — it
 * says an over-subscription is outstanding. Clamping it to 0 hides a decision somebody has to make.
 * `null` means uncapped.
 */
export const sittingSchema = z.object({
  id: z.guid(),
  examId: z.guid(),
  title: z.string(),
  status: z.enum(sittingStatuses),
  startsAtUtc: isoDate,
  endsAtUtc: isoDate,
  location: z.string().nullish(),
  capacity: z.number().int().nullish(),
  bookedCount: z.number().int(),
  remainingSeats: z.number().int().nullish(),
  hasStartCode: z.boolean(),
  createdAtUtc: isoDate,
  updatedAtUtc: isoDate
});
export const sittingListSchema = pagedListSchema(sittingSchema);
export type Sitting = z.infer<typeof sittingSchema>;

/**
 * SittingCandidateDto — one row of the register.
 *
 * ⚠ `markedBy: null` MEANS THE SYSTEM DECIDED, not "unknown". A candidate who starts their attempt is
 * marked present automatically, and there is no human to name. Render that case as the system, never
 * as a missing value.
 *
 * EXAM-NAMES: `userDisplayName` is added by GET register (after its cache) from Identity's GetUserNames
 * gRPC; null = unresolved (and on the PUT response), so fall back to the id.
 */
export const sittingCandidateSchema = z.object({
  userId: z.guid(),
  attendanceStatus: z.enum(sittingAttendanceStatuses),
  attemptId: z.guid().nullish(),
  markedAtUtc: isoDate.nullish(),
  markedBy: z.guid().nullish(),
  /**
   * EXTRA-TIME — the paper this seat produced, so the invigilator sees who is still writing and until when.
   * The deadline already includes any arrangement; it is deliberately not broken out.
   */
  attemptStatus: z.string().nullish(),
  attemptDeadlineAtUtc: isoDate.nullish(),
  attemptExtendedMinutes: z.number().int().optional().default(0),
  /** EXAM-NAMES: profile name ?? email ?? username; null = unresolved. */
  userDisplayName: z.string().nullish()
});

/** NOT PAGED: a register is one room's worth of people and is read whole. */
export const sittingRegisterSchema = z.object({
  sittingId: z.guid(),
  sittingStatus: z.enum(sittingStatuses),
  candidates: z.array(sittingCandidateSchema)
});
export type SittingCandidate = z.infer<typeof sittingCandidateSchema>;
export type SittingRegister = z.infer<typeof sittingRegisterSchema>;

/**
 * MySittingDto — the CANDIDATE's own seat (GET /exam/sittings/mine).
 *
 * ⚠ A DIFFERENT SHAPE FROM `sittingSchema`, and deliberately smaller: no capacity, no booked count, no
 * register — those are the room's business and the other candidates'. `requiresStartCode` is a flag and
 * there is NO field for the code itself; the value only ever reaches an invigilator, and adding a
 * property here "to help the student" would turn a room control into something every candidate can read
 * off their own schedule a week early.
 */
export const mySittingSchema = z.object({
  sittingId: z.guid(),
  examId: z.guid(),
  examTitle: z.string(),
  title: z.string(),
  status: z.enum(sittingStatuses),
  startsAtUtc: isoDate,
  endsAtUtc: isoDate,
  location: z.string().nullish(),
  requiresStartCode: z.boolean(),
  attendanceStatus: z.enum(sittingAttendanceStatuses),
  attemptId: z.guid().nullish()
});
export const mySittingListSchema = pagedListSchema(mySittingSchema);
export type MySitting = z.infer<typeof mySittingSchema>;

export const sittingStartCodeSchema = z.object({
  sittingId: z.guid(),
  startCode: z.string().nullish()
});

export const examListSchema = pagedListSchema(examSummarySchema);

export type ExamSummary = z.infer<typeof examSummarySchema>;
export type ExamDetail = z.infer<typeof examDetailSchema>;

/**
 * EXAM-DUP — POST /exam/exams/{id}/duplicate (body `{ title? }`) → the new DRAFT plus what happened to the
 * paper. Every source question is in exactly one of linked / remarked / recreated / dropped:
 *   linked     — the same bank question, unchanged since publish (or any question of a draft source);
 *   remarked   — same content, but its bank mark moved; the PUBLISHED mark is pinned as an override;
 *   recreated  — edited or deleted since publish; rebuilt from the published version as a new bank question;
 *   dropped    — a draft source's question that has since been deleted.
 * `needApproval` ⊆ recreated: AI-written questions start unapproved and block the copy's publish.
 * `newBankId/Name` only when the original bank was gone and one was made for the copy.
 * Copied: settings, integrity settings (not the access code), audience rules, paper. Not copied: dates,
 * enrolled candidates, sittings, results.
 */
export const examDuplicateResultSchema = z.object({
  exam: examDetailSchema,
  paper: z.object({
    linked: z.number().int(),
    remarked: z.number().int(),
    recreated: z.number().int(),
    dropped: z.number().int(),
    needApproval: z.number().int(),
    newBankId: z.guid().nullish(),
    newBankName: z.string().nullish()
  })
});
export type ExamDuplicateResult = z.infer<typeof examDuplicateResultSchema>;

// ---- Question banks + questions (S-18 + P5-EX-1 ordering/multi_select) ----
// EXAM-MATHANS (migration 523): 'math_answer' — the candidate types an expression rather than
// picking one. It carries an answer key (the accepted LaTeX) but is NOT auto-marked: a person
// compares the two rendered formulas, because whether ½ and 0.5 are the same answer depends on what
// the question is testing.
// v2 F1 (migration 605): fill_blank (one blank, accepted answers), numeric (value ± margin), matching (left "l*" /
// right "r*" items, answerKeys = "lX=rY" pairs, all-or-nothing) and file_upload (one file, marked by a person).
export const questionTypes = [
  'multiple_choice',
  'true_false',
  'open_ended',
  'ordering',
  'multi_select',
  'math_answer',
  'fill_blank',
  'numeric',
  'matching',
  'file_upload'
] as const;

/** v2 F3 (migration 605): draft -> pending (sent for approval) -> approved; only approved questions join a paper. */
export const questionApprovalStatuses = ['draft', 'pending', 'approved'] as const;
export type QuestionApprovalStatus = (typeof questionApprovalStatuses)[number];

/** BankDto */
export const questionBankSchema = z.object({
  id: z.guid(),
  name: z.string(),
  description: z.string().nullish(),
  questionCount: z.number().int(),
  createdAtUtc: isoDate,
  updatedAtUtc: isoDate
});

export const questionOptionSchema = z.object({ key: z.string(), text: z.string() });

/**
 * EXAM-IMG (migration 539) — one stem image, shown ABOVE the question text, in list order.
 *
 * ⚠ NO URL AND NO STORAGE KEY. The bytes are private and come through an authorised read chosen by
 * AUDIENCE, never by this object: the author reads `/exam/question-images/{id}`, the candidate
 * `/exam/attempts/{attemptId}/images/{id}`, the marker `/exam/grading/attempts/{attemptId}/images/{id}`.
 * The last two only answer for an image on THAT attempt's paper.
 *
 * width/height are the picture's real pixels, so a renderer can reserve the box before the bytes land.
 * `alt` may be empty on a draft (a question is approved only once every image is described).
 */
export const questionImageSchema = z.object({
  id: z.guid(),
  alt: z.string().catch(''),
  width: z.number().int(),
  height: z.number().int()
});
export type QuestionImage = z.infer<typeof questionImageSchema>;

/**
 * `images` on every question-shaped schema. Defaults to [] so a payload from a pre-539 instance during
 * a rolling deploy still parses — a list schema that fails blanks the whole page, not one field.
 */
const questionImagesField = z.array(questionImageSchema).optional().default([]);

/** UploadedQuestionImageDto — POST /exam/question-images (multipart). Send `id` back in the question save. */
export const uploadedQuestionImageSchema = z.object({
  id: z.guid(),
  contentType: z.string(),
  byteSize: z.number().int(),
  width: z.number().int(),
  height: z.number().int()
});
export type UploadedQuestionImage = z.infer<typeof uploadedQuestionImageSchema>;

/**
 * The server's upload limits, mirrored. ⚠ Change with QuestionImageLimits (Zercode.Exam) and the
 * migration 539 CHECKs — a client advertising a limit the server does not enforce turns a clear
 * refusal into a 400 nobody can act on.
 */
export const QUESTION_IMAGE_MAX_BYTES = 3 * 1024 * 1024;
export const QUESTION_IMAGE_MAX_COUNT = 4;
export const QUESTION_IMAGE_MAX_ALT = 300;
export const QUESTION_IMAGE_ACCEPT = 'image/png,image/jpeg,image/webp,image/gif';

/**
 * QuestionDto. answerKey = single key (multiple_choice) or 'true'|'false'; answerKeys =
 * ordered sequence (ordering) / correct set (multi_select); open_ended has neither.
 */
export const questionSchema = z.object({
  id: z.guid(),
  bankId: z.guid(),
  type: z.enum(questionTypes),
  text: z.string(),
  options: z.array(questionOptionSchema).nullish(),
  answerKey: z.string().nullish(),
  answerKeys: z.array(z.string()).nullish(),
  points: z.number().int(),
  rubric: z.string().nullish(), // P5-EX-4: open_ended grading rubric (AI-suggestion source)
  // EXAM-TAX (migrations 497/498): opaque definitions.definitions.id values from the exam_subjects,
  // exam_levels, exam_difficulty and exam_cognitive_levels groups. IDS, NOT LABELS — the label is
  // tenant-editable and translated, so the SPA resolves it from the Definitions catalogue it already
  // loads (useDefinitionLabelsById) rather than having the server freeze one tenant's wording onto
  // the wire. `nullish` on all four: an item may be untagged, which is the state a bank starts in.
  subjectDefinitionId: z.guid().nullish(),
  levelDefinitionId: z.guid().nullish(),
  difficultyDefinitionId: z.guid().nullish(),
  cognitiveLevelDefinitionId: z.guid().nullish(),
  aiGenerated: z.boolean(),
  approvedBy: z.guid().nullish(),
  approvedAtUtc: isoDate.nullish(),
  createdAtUtc: isoDate,
  updatedAtUtc: isoDate,
  images: questionImagesField, // EXAM-IMG (migration 539)
  /**
   * EXAM-XLSX (migration 567): the Excel import that created this item; null = written in the editor.
   * Provenance only — an imported item needs no approval. `nullish` so a pre-567 payload still parses.
   */
  importId: z.guid().nullish(),
  // v2 F1: numeric's ± margin (answerKey holds the value)
  tolerance: z.number().nullish(),
  // v2 F2: authoring guidance — the explanation is shown in a question-by-question result, never during an attempt
  explanation: z.string().nullish(),
  learningOutcome: z.string().nullish(),
  suggestedSeconds: z.number().int().nullish(),
  // v2 F3: `catch` so a pre-605 server (no field) reads as approved — every question then counted as approved
  approvalStatus: z.enum(questionApprovalStatuses).catch('approved')
});

/** v2 F3: what a bulk approval step did. */
export const questionBulkApprovalResultSchema = z.object({
  changed: z.number().int(),
  skipped: z.number().int(),
  missingImageText: z.number().int().default(0)
});
export type QuestionBulkApprovalResult = z.infer<typeof questionBulkApprovalResultSchema>;

/** v2 F1: an uploaded file_upload answer (never its storage key). */
export const answerFileSchema = z.object({
  id: z.guid(),
  examQuestionId: z.guid(),
  fileName: z.string(),
  contentType: z.string(),
  byteSize: z.number(),
  updatedAtUtc: isoDate
});
export type AnswerFile = z.infer<typeof answerFileSchema>;

/**
 * ExamQuestionDto — one question AS ATTACHED TO AN EXAM (`GET`/`PUT /exams/{id}/questions`).
 *
 * ⚠ `id` IS THE LINK ROW, NOT THE QUESTION. `exam.exam_questions.id` is this question's place on this
 * paper; `questionId` is the bank item. A paper is saved by sending `questionId` values back, in order.
 *
 * ⚠ NO ANSWER KEY, EVER. This is the shape the candidate's runner also reads, and the server never puts
 * the key on it — marking compares against the publish-time snapshot, server-side.
 *
 * ⚠ NOT PAGED, unlike every other list in this file: the endpoint returns a bare ordered array, because
 * a paper is small, fixed and ordered, and a page boundary through it would mean nothing.
 *
 * Declared HERE rather than beside the other exam schemas because it reads `questionTypes`, which is
 * declared above — placing it up there is a use-before-declaration the bundler resolves to `undefined`.
 */
/**
 * ExamQuestionDto — one row of a paper.
 *
 * ⚠ TWO POINT FIELDS, AND THEY ARE NOT REDUNDANT (EXAM-WEIGHT, migration 507).
 * `points` is the EFFECTIVE mark and is what every existing reader wants: on a draft it is
 * `pointsOverride ?? the bank value`, and once published it is the frozen `snapshot_points` the grade
 * was actually computed against. `pointsOverride` is the AUTHOR'S INTENT — null means "this exam
 * inherits whatever the bank says", which is every pre-507 row.
 *
 * The distinction matters on screen: "inherited 1" and "set to 1" look identical in the effective
 * value and behave differently the next time somebody corrects the bank question.
 *
 * `nullish` rather than required so a payload from a pre-507 instance during a rolling deploy still
 * parses instead of blanking the whole paper.
 */
export const examQuestionSchema = z.object({
  id: z.guid(),
  questionId: z.guid(),
  sortOrder: z.number().int(),
  type: z.enum(questionTypes),
  text: z.string(),
  options: z.array(questionOptionSchema).nullish(),
  points: z.number().int(),
  pointsOverride: z.number().int().nullish(),
  /**
   * The bank item's own points — what clearing the override falls back to.
   *
   * ⚠ NOT DERIVABLE from the other two: once an override is set, `points` IS the override and the
   * bank's number appears nowhere else in the row, which is why the server sends it. NULL on a
   * PUBLISHED paper, where the snapshot is frozen and "inherits" describes nothing.
   */
  bankPoints: z.number().int().nullish(),
  /** EXAM-IMG (migration 539): from the SNAPSHOT on a published paper, live from the bank on a draft. */
  images: questionImagesField,
  /**
   * v2 F7 (mig 609): the difficulty tag (an exam_difficulty definition id) — frozen at publish on a published paper,
   * live from the bank on a draft. The runner resolves the name.
   */
  difficultyDefinitionId: z.guid().nullish()
});
export const examQuestionListSchema = z.array(examQuestionSchema);
export type ExamPaperQuestion = z.infer<typeof examQuestionSchema>;

// ==============================|| EXAM-PREVIEW — an author sits their own paper ||=================== //
//
//   GET  /exam/exams/{id}/preview        the paper, freshly drawn, with its conditions   [NoCache]
//   POST /exam/exams/{id}/preview/mark   the real marking rules over preview answers; writes nothing
//
// Both need exam.exams.view AND exam.questions.view. NOTHING is recorded: no attempt, no quota, no events,
// no certificate. The questions arrive in the drawn order (sortOrder = shown position, options already
// shuffled — an ordering question never in answer order) and carry NO answer key; the mark response is the
// first and only place the correct answers appear.

export const examPreviewSchema = z.object({
  examId: z.guid(),
  title: z.string(),
  status: z.string(),
  durationMinutes: z.number().int(),
  passScore: z.number(),
  totalMarks: z.number().int(),
  navigationMode: z.enum(examNavigationModes),
  calculatorMode: z.enum(examCalculatorModes),
  keyboardMode: z.enum(examKeyboardModes),
  proctoringTier: z.string(),
  requireFullscreen: z.boolean(),
  randomizeQuestions: z.boolean(),
  randomizeChoices: z.boolean(),
  questions: z.array(examQuestionSchema)
});
export type ExamPreview = z.infer<typeof examPreviewSchema>;

export const previewMarkStatuses = ['correct', 'wrong', 'unanswered', 'manual'] as const;

export const examPreviewResultSchema = z.object({
  scorePct: z.number(),
  passed: z.boolean(),
  passScore: z.number(),
  earnedPoints: z.number(),
  totalPoints: z.number().int(),
  answeredCount: z.number().int(),
  pendingManualCount: z.number().int(),
  items: z.array(
    z.object({
      examQuestionId: z.guid(),
      type: z.string(),
      points: z.number().int(),
      status: z.enum(previewMarkStatuses),
      /** null for a question a person marks. */
      awardedPoints: z.number().nullish(),
      /** Option keys (mc/tf one, multi_select/ordering all, in order); null for open_ended / math_answer. */
      correctAnswer: z.array(z.string()).nullish()
    })
  )
});
export type ExamPreviewResult = z.infer<typeof examPreviewResultSchema>;

export const questionBankListSchema = pagedListSchema(questionBankSchema);
export const questionListSchema = pagedListSchema(questionSchema);

// ==============================|| EXAM-XLSX — import questions into a bank from Excel ||============ //
//
//   POST /exam/banks/{bankId}/questions/import/template  JSON → the .xlsx (postBlob)
//   POST /exam/banks/{bankId}/questions/import/preview   multipart file + references → QuestionImportPreview
//   POST /exam/banks/{bankId}/questions/import           multipart file + references + skipRows → QuestionImportDto
//   GET  /exam/banks/{bankId}/imports                    → QuestionImportDto[] (newest first, ≤ 50)
//   POST /exam/imports/{id}/undo                         → { import, removedQuestions }
//
// ⚠ ALL-OR-NOTHING: the import refuses (422, `rowErrors`) if any row is bad, creating nothing. The preview
// returns the same errors in a 200 body, so the dialog normally never sees that 422.
// ⚠ THE SAME `references` TRAVELS WITH ALL THREE POSTS. Exam cannot read Definitions [STD-OWN]; the template's
// dropdowns are built from it and the upload resolves cell text against it — a different list on upload than on
// download turns every tagged row into `tag_unknown`.

/** Mirrors QuestionImportWorkbook (Zercode.Exam). ⚠ Change together — see QUESTION_IMAGE_MAX_BYTES. */
export const QUESTION_IMPORT_MAX_BYTES = 2 * 1024 * 1024;
export const QUESTION_IMPORT_MAX_ROWS = 500;

export interface QuestionImportChoice {
  id: string;
  name: string;
}

/** The template's visible words, in the AUTHOR's language — copy lives in the SPA's catalogues. */
export interface QuestionImportTemplateLabels {
  sheetName: string;
  type: string;
  question: string;
  marks: string;
  /** "Option" — the sheet shows "Option A" … "Option J". */
  option: string;
  correctAnswer: string;
  markingGuide: string;
  subject: string;
  level: string;
  difficulty: string;
  cognitiveLevel: string;
  helpSheetName: string;
  helpLines: string[];
  /**
   * The comment on the Option A header. Multiple choice has no answer cell: its FIRST option is the
   * correct one (2026-10-05), and a letter in Correct answer is refused (`answer_first_option`).
   */
  firstOptionNote?: string;
}

/** The dropdown words and the catalogues; identical on the template request and both uploads. */
export interface QuestionImportReferences {
  /** Type code → the label the Type dropdown shows (and the upload reads back). */
  typeNames: Record<string, string>;
  trueWord: string;
  falseWord: string;
  subjects: QuestionImportChoice[];
  levels: QuestionImportChoice[];
  difficulties: QuestionImportChoice[];
  cognitiveLevels: QuestionImportChoice[];
}

/** Values written down every row of the template; the author may still change any cell. */
export interface QuestionImportDefaults {
  subjectDefinitionId?: string | null;
  levelDefinitionId?: string | null;
  difficultyDefinitionId?: string | null;
  cognitiveLevelDefinitionId?: string | null;
}

/** Whole-FILE rejections (422 `fileError`). Kept as data so the dialog can word each one. */
export const questionImportFileErrors = [
  'file_unreadable',
  'file_no_sheet',
  'file_no_rows',
  'file_too_many_rows',
  'file_not_template',
  /** A `skipRows` entry names no question in this file — the client is looking at a different file. */
  'skip_row_unknown',
  /** Every row was skipped. */
  'nothing_to_import'
] as const;

/**
 * One refused cell. `row` is the number AS EXCEL SHOWS IT; `column` is a stable code (`type`, `question`,
 * `marks`, `options`, `option_a`…`option_j`, `answer`, `marking_guide`, `subject`, `level`, `difficulty`,
 * `cognitive_level`). `code` stays a plain string so a code from a newer server still renders.
 */
export const questionImportRowErrorSchema = z.object({
  row: z.number().int(),
  column: z.string(),
  code: z.string(),
  value: z.string().nullish()
});

/** Non-blocking: `duplicate_in_bank` names `questionId`; `duplicate_in_file` names the earlier `otherRow`. */
export const questionImportWarningSchema = z.object({
  row: z.number().int(),
  code: z.string(),
  questionId: z.guid().nullish(),
  otherRow: z.number().int().nullish()
});

/** A row that WILL become a question — the fields the editor's create takes. */
export const questionImportPreviewRowSchema = z.object({
  row: z.number().int(),
  type: z.enum(questionTypes),
  text: z.string(),
  options: z.array(questionOptionSchema).nullish(),
  answerKey: z.string().nullish(),
  answerKeys: z.array(z.string()).nullish(),
  points: z.number().int(),
  rubric: z.string().nullish(),
  subjectDefinitionId: z.guid().nullish(),
  levelDefinitionId: z.guid().nullish(),
  difficultyDefinitionId: z.guid().nullish(),
  cognitiveLevelDefinitionId: z.guid().nullish()
});

export const questionImportPreviewSchema = z.object({
  totalRows: z.number().int(),
  rows: z.array(questionImportPreviewRowSchema),
  errors: z.array(questionImportRowErrorSchema),
  warnings: z.array(questionImportWarningSchema)
});

/** QuestionImportDto. `questionCount` is what the import created (a fact); `liveQuestionCount` what is left. */
export const questionImportSchema = z.object({
  id: z.guid(),
  bankId: z.guid(),
  fileName: z.string().nullish(),
  questionCount: z.number().int(),
  skippedCount: z.number().int(),
  liveQuestionCount: z.number().int(),
  createdBy: z.guid().nullish(),
  createdAtUtc: isoDate,
  undoneAtUtc: isoDate.nullish()
});
export const questionImportListSchema = z.array(questionImportSchema);
export const questionImportUndoResultSchema = z.object({
  import: questionImportSchema,
  removedQuestions: z.number().int()
});

export type QuestionImportRowError = z.infer<typeof questionImportRowErrorSchema>;
export type QuestionImportWarning = z.infer<typeof questionImportWarningSchema>;
export type QuestionImportPreviewRow = z.infer<typeof questionImportPreviewRowSchema>;
export type QuestionImportPreview = z.infer<typeof questionImportPreviewSchema>;
export type QuestionImport = z.infer<typeof questionImportSchema>;

/** The row report out of the import's 422 (it rides in the problem's extensions). [] for any other failure. */
export function questionImportRowErrors(problem: ProblemDetails): QuestionImportRowError[] {
  const parsed = z.array(questionImportRowErrorSchema).safeParse(problem.rowErrors);
  return parsed.success ? parsed.data : [];
}

/** The whole-file rejection code out of a 422, when that is what happened. */
export function questionImportFileError(problem: ProblemDetails): string | null {
  return typeof problem.fileError === 'string' ? problem.fileError : null;
}

export type QuestionBank = z.infer<typeof questionBankSchema>;
export type ExamQuestion = z.infer<typeof questionSchema>;
export type QuestionOption = z.infer<typeof questionOptionSchema>;
export type QuestionType = (typeof questionTypes)[number];

// ---- Attempt grading (P5-EX-4: AI essay-grade suggestion + manual grading) ----
export const answerGradeStatuses = ['pending', 'pending_manual', 'graded'] as const;

/** AttemptDto (fields the grading UI needs). */
export const attemptSchema = z.object({
  id: z.guid(),
  examId: z.guid(),
  userId: z.guid(),
  status: z.string(),
  startedAtUtc: isoDate,
  deadlineAtUtc: isoDate,
  submittedAtUtc: isoDate.nullish(),
  gradedAtUtc: isoDate.nullish(),
  scorePct: z.number().nullish(),
  passed: z.boolean().nullish(),
  // G1 (mig 167) → FE-4: does this attempt's exam require proctoring? The student never carries
  // exam.exams.read, so the proctoring session lifecycle is keyed off the attempt itself. Optional
  // + default(false) keeps every existing attempt payload (grading queue/detail) parseable.
  requiresProctoring: z.boolean().optional().default(false),
  /**
   * EXAM-NAME: the exam this attempt was for.
   *
   * ⚠ IT COMES FROM THE ATTEMPT, and there is no client-side substitute. A candidate holds no
   * `exam.exams.view`, and the attemptable list only shows exams whose window is OPEN — so joining
   * "my attempts" to "exams I can sit" finds nothing for anything finished, which is exactly why the
   * list used to read "Deneme 01a0ca78".
   *
   * Defaults to empty so a pre-EXAM-NAME payload still parses; treat the empty string as "unknown"
   * rather than assuming a title is always there.
   */
  examTitle: z.string().optional().default(''),
  /**
   * EXAM-RELEASE (migration 510) — has this exam's marking been ANNOUNCED?
   *
   * ⚠ IT IS THE ONLY THING THAT DISTINGUISHES "not marked yet" FROM "marked, not published yet". When
   * results are held the server REMOVES `scorePct`, `passed`, every `awardedPoints` and every piece of
   * grader feedback from the candidate's payload — so a client that reads only the nulls tells a
   * candidate their paper has not been looked at, which is untrue and reads as a fault.
   *
   * ⚠ NEVER RECOMPUTE IT CLIENT-SIDE for a candidate. The marks are physically absent, not merely
   * hidden; this flag is the server's own answer and `resultsReleaseAtUtc` is when it changes.
   *
   * Defaults to TRUE here and FALSE on the server, and the asymmetry is deliberate: a pre-510 instance
   * sends neither field and its results genuinely are immediate, so defaulting to false would tell
   * every candidate in a rolling deploy that their results are being withheld.
   */
  resultsReleased: z.boolean().optional().default(true),
  /** When results are due. NULL = no date announced, or already out. */
  resultsReleaseAtUtc: isoDate.nullish(),
  /**
   * EXAM-CALC (migration 518) — the calculator THIS CANDIDATE was given, copied onto the attempt when
   * they started.
   *
   * ⚠ THE RUNNER READS THIS, NEVER THE EXAM. An author switching a live paper to `none` must not
   * empty a panel somebody is mid-sum in, so the attempt's copy is the authority for the whole sitting.
   *
   * Defaults to `none` — the opposite asymmetry from `resultsReleased` above, and for the same kind
   * of reason: a payload that does not mention a calculator comes from an instance that has none, and
   * the cheap failure is a missing tool somebody reports rather than one appearing on a paper that
   * forbids it.
   */
  calculatorMode: z.enum(examCalculatorModes).optional().default('none'),
  /**
   * EXAM-MATHANS (migration 523) — the keyboard THIS CANDIDATE was given.
   *
   * ⚠ Read from the attempt, never the exam, for the same reason as `calculatorMode`: an author
   * editing a live paper must not take the keys out of somebody's hands mid-question.
   */
  keyboardMode: z.enum(examKeyboardModes).optional().default('none'),
  /**
   * A3 — the SERVER's clock when this was built, on the candidate's own reads only (null on the
   * grader's face, which can be cached). The runner counts down against it, not the device clock.
   */
  serverNowUtc: isoDate.nullish(),
  /** A7 — from the attempt's own snapshot: the runner covers the paper while it is not fullscreen. Absent = no. */
  requireFullscreen: z.boolean().optional().default(false),
  /** EXTRA-TIME — how much of the deadline is the arrangement, and how much invigilators added. Both already in it. */
  extraTimeMinutes: z.number().int().optional().default(0),
  extendedMinutes: z.number().int().optional().default(0),
  /**
   * EXAM-RESUME (migration 543). `navigationMode` is the ATTEMPT's (its snapshot), never the exam's
   * today. Positions are 0-based into the order THIS candidate was shown: the runner opens at
   * `currentPosition`, and on a forward-only paper everything before `furthestPosition` is closed.
   * Absent (a pre-543 payload) = free, at question 1.
   */
  navigationMode: z.enum(examNavigationModes).optional().default('free'),
  currentPosition: z.number().int().optional().default(0),
  furthestPosition: z.number().int().optional().default(0),
  lastSeenAtUtc: isoDate.nullish(),
  /** How often, and for how many minutes, an admin reopened this paper after the timer handed it in. */
  reopenCount: z.number().int().optional().default(0),
  reopenedMinutes: z.number().int().optional().default(0),
  /** EXAM-DETAIL (mig 570): full = draw the breakdown; score_only = the server already removed it. */
  resultsDetail: resultsDetailField,
  /** EXAM-REVIEW-OPT (mig 574): may the candidate send a NEW review request (the exam's setting, read live). */
  reviewRequestsAllowed: z.boolean().optional().default(false),
  /**
   * QUIZ (mig 586): from the ATTEMPT row. `untimed` = the runner shows no countdown and never auto-submits
   * (the deadline is only the server's cap on an abandoned attempt). `courseRef`/`lessonRef` mark a course quiz
   * and say where "back" goes. Absent on an exam attempt.
   */
  untimed: z.boolean().optional().default(false),
  courseRef: z.guid().nullish(),
  lessonRef: z.guid().nullish(),
  /** v2 F7 (mig 609): the exam's code for the runner header, read live with the title. */
  examCode: z.string().nullish(),
  /**
   * EXAM-OVR (mig 641): the score was adjusted after an integrity review AND the admin wrote the student a note — when,
   * and the note. Both absent otherwise, including an override WITHOUT a note: the student then sees the new score as an
   * ordinary result (product call 2026-10-20), so the result page must never infer an adjustment from anything else.
   */
  scoreAdjustedAtUtc: isoDate.nullish(),
  scoreAdjustmentNote: z.string().nullish()
});

/**
 * EXAM-REVIEW (migration 512) — a candidate's request that their marked paper be looked at again.
 *
 * ⚠ THERE IS NO OUTCOME FIELD, and its absence is the design rather than an omission. Nothing in Exam
 * can currently change a mark once it is entered (`GradeAnswerCommand` refuses anything that is not
 * `pending_manual`), so a grader answers in WRITING. A UI that offered "uphold / change" would present
 * an action the server cannot perform.
 */
export const reviewRequestStatuses = ['open', 'resolved', 'withdrawn'] as const;
export type ReviewRequestStatus = (typeof reviewRequestStatuses)[number];

/** ReviewRequestDto — the candidate's own view of one request. */
export const reviewRequestSchema = z.object({
  id: z.guid(),
  attemptId: z.guid(),
  reason: z.string(),
  status: z.enum(reviewRequestStatuses),
  /** The grader's written answer. NULL until `status` is `resolved` — the three move together. */
  resolution: z.string().nullish(),
  resolvedAtUtc: isoDate.nullish(),
  createdAtUtc: isoDate
});
export const reviewRequestListSchema = z.array(reviewRequestSchema);
export type ReviewRequest = z.infer<typeof reviewRequestSchema>;

/**
 * ReviewQueueItemDto — the grader's queue row.
 *
 * ⚠ `scorePct` IS ON THIS ROW ON PURPOSE. Withholding (EXAM-RELEASE) governs the CANDIDATE's view of a
 * mark, not the record — and triaging an appeal without seeing the mark being appealed means opening
 * every one of them.
 */
export const reviewQueueItemSchema = z.object({
  id: z.guid(),
  attemptId: z.guid(),
  examId: z.guid(),
  examTitle: z.string(),
  candidateId: z.guid(),
  reason: z.string(),
  scorePct: z.number().nullish(),
  createdAtUtc: isoDate
});
export const reviewQueueListSchema = pagedListSchema(reviewQueueItemSchema);
export type ReviewQueueItem = z.infer<typeof reviewQueueItemSchema>;

/** GradingQueueItemDto — a submitted attempt awaiting manual grading. */
export const gradingQueueItemSchema = z.object({
  attemptId: z.guid(),
  examId: z.guid(),
  userId: z.guid(),
  submittedAtUtc: isoDate,
  pendingManualCount: z.number().int(),
  /** A5 — a STRICT attempt whose supervision showed trouble; a flag for a human, never a mark change. */
  needsIntegrityReview: z.boolean().optional().default(false),
  /** GRADE-TABS: the paper's name — the grading tab identifies a paper by exam and end time, never by an id. */
  examTitle: z.string().optional().default('')
});
export const gradingQueueListSchema = pagedListSchema(gradingQueueItemSchema);

/** GradingAnswerDto — grader view of one answer (text + rubric + advisory AI suggestion). */
export const gradingAnswerSchema = z.object({
  examQuestionId: z.guid(),
  type: z.string(),
  questionText: z.string(),
  rubric: z.string().nullish(),
  answerText: z.string(),
  gradeStatus: z.enum(answerGradeStatuses),
  awardedPoints: z.number().nullish(),
  points: z.number().int(),
  aiSuggestedCorrect: z.boolean().nullish(),
  aiRationale: z.string().nullish(),
  aiSuggestedAtUtc: isoDate.nullish(),
  /**
   * EXAM-MATHANS (migration 523) — the accepted expression for a math_answer, as raw LaTeX, taken
   * from the publish snapshot. Null for every other type.
   *
   * ⚠ NOT AN AUTOMATIC MARK. Nothing on either side compares it with the candidate's answer; it is
   * what the MARKER is shown beside what the candidate wrote, which is the whole reason the type
   * requires an answer key at all.
   */
  acceptedAnswer: z.string().nullish(),
  /**
   * A2 (migration 522) — where the item sits on the PAPER (1-based; rows arrive in this order) and
   * where THIS candidate was shown it. `candidatePosition` is null when the attempt was not shuffled,
   * i.e. the two are the same. Optional so a pre-522 payload still parses.
   */
  paperPosition: z.number().int().optional().default(0),
  candidatePosition: z.number().int().nullish(),
  /** EXAM-IMG (migration 539): the figures the candidate saw, from the snapshot. */
  images: questionImagesField,
  /** GRADE-TABS: false = the candidate left this question blank (a synthesised row; never gradable). */
  answered: z.boolean().optional().default(true),
  /** v2 F1 file_upload: the uploaded file's id — bytes via /grading/attempts/{attemptId}/files/{id}. */
  answerFileId: z.guid().nullish(),
  /** BE-3.2: the grader's feedback on this answer, already on the wire. */
  feedback: z.string().nullish()
});

/**
 * A4 (migration 524) — the integrity conditions an attempt was SAT UNDER, frozen at its start. What a
 * disputed mark asks about. Only whether an IP allowlist applied, not the list itself.
 */
export const attemptConditionsSchema = z.object({
  settingsVersion: z.number().int(),
  proctoringTier: z.string(),
  randomizeQuestions: z.boolean(),
  randomizeChoices: z.boolean(),
  accessCodeRequired: z.boolean(),
  ipPolicy: z.string(),
  requireFullscreen: z.boolean(),
  /** EXTRA-TIME: the EFFECT only — arrangement minutes, and invigilators' total. Never a percentage or reason. */
  extraTimeMinutes: z.number().int().optional().default(0),
  extendedMinutes: z.number().int().optional().default(0)
});
export type AttemptConditions = z.infer<typeof attemptConditionsSchema>;

/**
 * A5 — the grader's reading of an attempt's supervision. `sessionStarted: false` on a proctored paper
 * means the client never opened one; gaps are silences the SERVER measured, not client reports.
 */
export const proctoringSummarySchema = z.object({
  proctoringTier: z.string(),
  sessionStarted: z.boolean(),
  violationCount: z.number().int(),
  gapCount: z.number().int(),
  longestGapSeconds: z.number().int().nullish(),
  needsIntegrityReview: z.boolean()
});
export type ProctoringSummary = z.infer<typeof proctoringSummarySchema>;

/**
 * A6 — one address the candidate saved from OUTSIDE the allowlist. Saves are accepted, never refused
 * (a Wi-Fi drop to mobile data must not lose work), so this is evidence for the grader, not a verdict.
 */
export const offNetworkSavesSchema = z.object({
  clientIp: z.string(),
  firstSeenAtUtc: z.string(),
  lastSeenAtUtc: z.string(),
  saveCount: z.number().int()
});
export type OffNetworkSaves = z.infer<typeof offNetworkSavesSchema>;

export const attemptNetworkSchema = z.object({
  /** null for an attempt started before migration 527. */
  startIp: z.string().nullish(),
  offNetwork: z.array(offNetworkSavesSchema)
});
export type AttemptNetwork = z.infer<typeof attemptNetworkSchema>;

export const gradingAttemptDetailSchema = z.object({
  attempt: attemptSchema,
  answers: z.array(gradingAnswerSchema),
  /** A4: null for an attempt from before migration 524 — it recorded no conditions. */
  satUnder: attemptConditionsSchema.nullish(),
  /** A5: what the supervision actually showed; null when the attempt was not proctored. */
  proctoring: proctoringSummarySchema.nullish(),
  /** A6: where the attempt was sat from; null unless the exam has an IP allowlist. */
  network: attemptNetworkSchema.nullish(),
  /** EXAM-OMR (mig 631): the optical sheet a paper attempt came from; null online. */
  paper: z
    .object({
      bookletLabel: z.string().nullish(),
      /** Booklet order: A–E, '-' blank, '*' two or more marks. */
      sheetAnswers: z.string(),
      importId: z.string().uuid(),
      fileName: z.string().nullish(),
      satOn: z.string(),
      importedAtUtc: isoDate,
      importedBy: z.string().uuid().nullish()
    })
    .nullish()
});

/** EssayGradeSuggestionDto — result of POST .../answers/{eqId}/suggest-grade [STD-AI]. */
export const essayGradeSuggestionSchema = z.object({
  examQuestionId: z.guid(),
  suggestedCorrect: z.boolean(),
  rationale: z.string(),
  suggestedAtUtc: isoDate
});

/** AttemptGradingResultDto — result of manual grade (attempt + remaining pending count). */
export const attemptGradingResultSchema = z.object({
  attempt: attemptSchema,
  pendingManualCount: z.number().int()
});

// ---- Student exam-taking (P6): answer-key-SAFE question + attempt detail/answer ----
/** ExamQuestionDto (student view) — NO answer key; options present for choice/order types. */
export const examTakingQuestionSchema = z.object({
  id: z.guid(),
  questionId: z.guid(),
  sortOrder: z.number().int(),
  type: z.enum(questionTypes),
  text: z.string(),
  options: z.array(questionOptionSchema).nullish(),
  points: z.number().int(),
  /**
   * EXAM-TOPIC: the item's subject — an opaque definitions id from the `exam_subjects` group — so a
   * result can be broken down by topic.
   *
   * ⚠ READ LIVE FROM THE BANK, not frozen into the publish snapshot: a subject tag is a filing
   * decision, not marking evidence, so correcting one re-buckets past results instead of leaving them
   * filed under a mistake. NULL for an untagged item, which appears in no topic.
   */
  subjectDefinitionId: z.guid().nullish(),
  /** EXAM-IMG (migration 539): stem images from the publish snapshot; bytes via /attempts/{id}/images/{imageId}. */
  images: questionImagesField,
  /** v2 F7 (mig 609): the difficulty tag frozen at publish (exam_difficulty id); the runner resolves the name. */
  difficultyDefinitionId: z.guid().nullish()
});
/** ExamQuestionListResult — { items } (not paged). */
export const examTakingQuestionListSchema = z.object({ items: z.array(examTakingQuestionSchema) });

/** AttemptAnswerDto — one answer's grade state (+ advisory AI fields from P5-EX-4). */
export const attemptAnswerSchema = z.object({
  examQuestionId: z.guid(),
  gradeStatus: z.enum(answerGradeStatuses),
  awardedPoints: z.number().nullish(),
  answeredAtUtc: isoDate,
  aiSuggestedCorrect: z.boolean().nullish(),
  aiRationale: z.string().nullish(),
  aiSuggestedAtUtc: isoDate.nullish(),
  // BE-3.2 (J3) → FE-8d: grader's free-text feedback for this answer. Student sees it on the
  // result screen only when present (null when the grader left none).
  feedback: z.string().nullish(),
  // v2 F2: the question's explanation — only on a released, question-by-question result
  explanation: z.string().nullish()
});

/** AttemptDetailDto — attempt + per-answer status (student's own attempt). */
export const attemptDetailSchema = z.object({
  attempt: attemptSchema,
  answers: z.array(attemptAnswerSchema),
  /**
   * EXAM-DETAIL (mig 570): marks earned and the paper's total, summed on the SERVER — a score_only result has
   * no per-answer marks to add up. Null while results are withheld (and from a pre-570 server).
   */
  earnedPoints: z.number().nullish(),
  totalPoints: z.number().int().nullish()
});

export type ExamTakingQuestion = z.infer<typeof examTakingQuestionSchema>;
export type AttemptAnswer = z.infer<typeof attemptAnswerSchema>;
export type AttemptDetail = z.infer<typeof attemptDetailSchema>;

/** ItemStatDto — per-question classical item stats (difficulty p, discrimination D). */
export const itemStatSchema = z.object({
  examQuestionId: z.guid(),
  sortOrder: z.number().int(),
  responseCount: z.number().int(),
  correctCount: z.number().int(),
  difficultyPValue: z.number(),
  discriminationIndex: z.number().nullable()
});

/** ExamItemAnalysisDto — GET /exam/grading/exams/{examId}/item-analysis (grader psychometrics). */
export const examItemAnalysisSchema = z.object({
  examId: z.guid(),
  attemptsAnalyzed: z.number().int(),
  items: z.array(itemStatSchema)
});

/**
 * EXAM-RESULTS — the cohort of one exam, in aggregate.
 *
 * ⚠ THE SCORE STATISTICS ARE NULLABLE AND THAT IS THE POINT. `null` means nothing has been marked
 * yet, which is what an exam looks like the hour after it is sat; rendering it as 0 tells an author
 * their cohort failed. Never coalesce these to a number.
 *
 * ⚠ IT IS AN AGGREGATE, NOT A PAGE. The median cannot be computed from a page of attempts, and a mean
 * computed from one is quietly wrong — so the server does the arithmetic over the whole cohort and
 * this payload carries no candidate identities at all.
 */
export const scoreBucketSchema = z.object({
  lowerPct: z.number().int(),
  /** Inclusive on the last bucket only, so a perfect paper has somewhere to go. */
  upperPct: z.number().int(),
  count: z.number().int()
});

/**
 * Attendance across every sitting of the exam.
 *
 * ⚠ `seats === 0` IS THE COMMON CASE, not an empty room: most exams have no sittings at all and
 * candidates sit them inside the exam's own window. Render it as "not applicable".
 *
 * ⚠ THERE IS NO `late`, deliberately (mig 505) — an attempt's deadline runs from when it starts, so a
 * candidate who arrives twenty minutes in still gets the full duration and is simply present.
 */
export const examAttendanceSchema = z.object({
  seats: z.number().int(),
  expected: z.number().int(),
  present: z.number().int(),
  absent: z.number().int(),
  excused: z.number().int()
});

export const examResultsSummarySchema = z.object({
  examId: z.guid(),
  /** Attempts that exist at all, whatever their state — who turned up. */
  sat: z.number().int(),
  /** The denominator for every score statistic. */
  graded: z.number().int(),
  awaitingMark: z.number().int(),
  inProgress: z.number().int(),
  passed: z.number().int(),
  passRatePct: z.number().nullish(),
  meanScorePct: z.number().nullish(),
  medianScorePct: z.number().nullish(),
  histogram: z.array(scoreBucketSchema),
  attendance: examAttendanceSchema
});

export type ExamResultsSummary = z.infer<typeof examResultsSummarySchema>;
export type ScoreBucket = z.infer<typeof scoreBucketSchema>;

export type Attempt = z.infer<typeof attemptSchema>;
export type GradingQueueItem = z.infer<typeof gradingQueueItemSchema>;
export type GradingAnswer = z.infer<typeof gradingAnswerSchema>;
export type GradingAttemptDetail = z.infer<typeof gradingAttemptDetailSchema>;
export type ItemStat = z.infer<typeof itemStatSchema>;
export type ExamItemAnalysis = z.infer<typeof examItemAnalysisSchema>;
export type EssayGradeSuggestion = z.infer<typeof essayGradeSuggestionSchema>;
export type AttemptGradingResult = z.infer<typeof attemptGradingResultSchema>;

// ---- Proctoring (A-2; RecordOnly pilot) — grader review surface -----------------------------
// ProctoringSessionDto / ProctoringViolationDto / ProctoringSessionDetailDto (ProctoringFeatures.cs).
// The grader read is GET /api/v1/exam/grading/attempts/{attemptId}/proctoring (exam.attempts.grade):
// one session per attempt + its violations (fraud flags). Vision/enforcement is E-7 [STD-DRIFT].
export const proctoringSessionStatuses = ['active', 'ended'] as const;
export const proctoringSeverities = ['info', 'warning', 'critical'] as const;

/** ProctoringSessionDto — session summary for one attempt. */
export const proctoringSessionSchema = z.object({
  attemptId: z.guid(),
  status: z.string(),
  provider: z.string(),
  startedAtUtc: isoDate,
  lastHeartbeatAtUtc: isoDate.nullish(),
  endedAtUtc: isoDate.nullish(),
  violationCount: z.number().int()
});

/** ProctoringViolationDto — one recorded violation (a "fraud flag"). action is empty on the grader read. */
export const proctoringViolationSchema = z.object({
  id: z.guid(),
  type: z.string(),
  severity: z.string(),
  detail: z.string().nullish(),
  occurredAtUtc: isoDate,
  action: z.string()
});

/** ProctoringSessionDetailDto — grader review (session + violations, newest first). */
export const proctoringSessionDetailSchema = z.object({
  session: proctoringSessionSchema,
  violations: z.array(proctoringViolationSchema)
});

export type ProctoringSession = z.infer<typeof proctoringSessionSchema>;
export type ProctoringViolation = z.infer<typeof proctoringViolationSchema>;
export type ProctoringSessionDetail = z.infer<typeof proctoringSessionDetailSchema>;
export type ProctoringSeverity = (typeof proctoringSeverities)[number];

/**
 * FE-4 (student write): body for POST /api/v1/exam/attempts/{id}/proctoring/violations
 * (ReportViolationRequest). The Idempotency-Key is applied automatically by createApiClient on
 * the write — do NOT put it in the body [STD-API-4]. RecordOnly pilot: the provider Noops the
 * decision; the violation is still recorded on the session (vision/enforcement is E-7) [STD-DRIFT].
 */
export interface ReportViolationRequest {
  type: string;
  severity: ProctoringSeverity;
  detail?: string | null;
}

/** CreateExamCommand body */
export interface CreateExamRequest {
  title: string;
  description?: string | null;
  durationMinutes: number;
  passScore: number;
  courseRef?: string | null;
  opensAtUtc?: string | null;
  closesAtUtc?: string | null;
  /**
   * EXAM-PROG (migration 532) — REQUIRED, and typed that way on purpose: exam.exams.program_type_id
   * is NOT NULL (like an event's since 236), so a caller that omits it gets a 422. Opaque id from
   * the `program_types` group.
   */
  programTypeId: string;
  /** EXAM-PROG — the age band, or null for every band of the programme. MUST belong to programTypeId;
   *  nothing server-side can check that, so whoever builds this body owns the pair. */
  ageCategoryId?: string | null;
  /** v2 F7 (mig 609): optional exam code, unique in the tenant (409 when taken). */
  code?: string | null;
}

// ==============================|| ENGAGEMENT (:5800) ||============================== //

// DG-3 (mig 205): eventType/languageCode ARTIK OPAK definition ID (guid) — eski opak kodun yerini aldı.
// Değerler Definitions servisinin 'event_types'/'languages' gruplarından gelir; FE id'ye göre saklar ve
// id→ad çözer (definition.id) [STD-OWN]. Tümü nullish (backend Guid?).
/**
 * EVT-MODE (mig 308) — how much an event wants one information block. Mirrors EventInfoModes and the
 * ck_events_*_mode CHECKs.
 *
 * THREE STATES, not a boolean: "collect it" and "insist on it" are different decisions. 'required'
 * BLOCKS NOTHING — the tabs only exist after registration, so it means "staff can see who is missing".
 */
export const eventInfoModes = ['off', 'optional', 'required'] as const;
export type EventInfoMode = (typeof eventInfoModes)[number];

/** The single question a tab asks: is this block collected on this event at all? */
export function collectsInfo(mode: string | null | undefined): boolean {
  // Fail CLOSED on anything unrecognised, matching the server's EventInfoModes.Collects — an unknown
  // mode must not behave like 'optional' and show a tab the admin never enabled.
  return mode === 'optional' || mode === 'required';
}

export const eventStatuses = ['draft', 'published', 'archived'] as const;
export type EventStatus = (typeof eventStatuses)[number];

/**
 * EVT-STATUS — the lifecycle transition map, mirroring the server's `EventLifecycle.CanTransition`
 * (Engagement/Domain/EngagementEntities.cs). The BACKEND remains the authority; this exists so the UI
 * does not offer a move the API will refuse with a 409.
 *
 * ⚠ `published → draft` IS allowed (product decision, 2026-09-21): an event published by mistake had
 * no route back except archiving, which says "finished" rather than "not ready". It costs exactly
 * what archiving costs — registration, invitations, ticket offers, teacher assignment and the
 * reminder sweeps all close, and the event leaves the student/teacher listings, which only ever ask
 * for `published`. Existing registrations are NOT deleted; the gates are read-side. The confirm
 * dialog has to say so.
 *
 * ⚠ `archived → draft` is ABSENT ON PURPOSE and the server refuses it — the way out of the archive is
 * to publish again. Adding it here would only produce an option whose one outcome is a 409.
 *
 * ⚠ SELECTING THE CURRENT STATUS IS NOT IN THIS MAP EITHER, and that is not the same thing: the
 * endpoint treats "make the status what it already is" as an idempotent no-op (200), not a violation.
 * A picker should disable the current value because there is nothing to do, not because it would fail.
 *
 * ⚠ ADDING A FOURTH STATUS IS A TWO-REPO CHANGE. `eventStatuses` above feeds `z.enum` in both event
 * schemas, so a value the server invents and this file has not learned makes the whole parse THROW —
 * the page blanks rather than degrading.
 */
export const eventTransitions: Readonly<Record<EventStatus, readonly EventStatus[]>> = {
  draft: ['published'],
  published: ['archived', 'draft'],
  archived: ['published']
};

/** EVT-STATUS body for POST /api/v1/engagement/events/{id}/status [STD-API-2]. */
export interface SetEventStatusRequest {
  status: EventStatus;
}

/** EventSummaryDto — EV alanları (DG-3 mig 205) forward/back-uyumlu (nullish). */
/**
 * REG-2AX (mig 439) — the two coarse statuses the server now computes, in the order a person asks
 * them: "has any money arrived?" and "is this person in?".
 *
 * ⚠ THE SERVER OWNS THE RULE. Both are `GENERATED ALWAYS AS … STORED` columns in the tenant DB, so the
 * client RENDERS them and must never re-derive them from `paymentState`/`amountPaid`. Re-deriving is
 * how the badge and the row it sits on end up disagreeing — a second definition of one truth, which is
 * the failure this pair was introduced to remove.
 *
 * ⚠ `registered` IS NOT ACCESS. The content gate is unchanged and still needs FULL payment
 * (`paymentState` in not_required|paid), so a part-paid registrant reads "Registered" next to content
 * that is still locked. Any screen showing this badge has to say so, or it recreates the contradiction
 * mig 406 closed from the other side.
 *
 * ⚠ AND `not_registered_yet` STILL HOLDS A SEAT. Its `status` is `awaiting_payment`, which is in
 * `seatHoldingRegistrationStatuses` and counts against capacity. Never treat the label as free
 * inventory.
 */
/*
 * REG-REFUND (mig 711) — `refunded` in BOTH: every penny went back and the registration was cancelled for it. The
 * one value a CANCELLED row carries (any other cancelled row is still null). It holds NO seat. A partial refund and
 * a refund being collected again never produce it.
 *
 * ⚠ DEPLOY THIS BEFORE (or with) the Engagement build that sends it: an older client parses `refunded` against the
 * two-value enum and throws for the whole document.
 */
/*
 * REG-PREFUND (mig 723) — `partially_refunded`, payment column ONLY: paid in full, then part of the money went back
 * and the seat was kept, so the registration stays `registered`. Only on a fully paid row — a refund on a plan still
 * being paid reads `partially_paid`, because the debt is the thing to show there. Same deploy order as `refunded`:
 * this client before the Engagement build that sends it.
 */
export const registrationPaymentStatuses = ['awaiting_payment', 'partially_paid', 'paid', 'partially_refunded', 'refunded'] as const;
/**
 * REG-PREFUND (mig 723) — nothing left to pay: `paid`, or paid in full and then partly refunded. For the PAYER's
 * screens, where a partial refund changes nothing they have to do; staff screens show the two apart.
 */
export const isPaidInFull = (status: string | null | undefined): boolean =>
  status === 'paid' || status === 'partially_refunded';

export const coarseRegistrationStatuses = ['not_registered_yet', 'registered', 'refunded'] as const;

/**
 * ⚠ NULLISH, NOT A REQUIRED ENUM, and for two independent reasons — either alone would blank a page.
 *
 *   1. The server returns NULL off the seat path (waitlisted / cancelled): the lifecycle is the answer
 *      there, and a strict enum would throw on the null.
 *   2. A server predating mig 439 does not send the fields at all, and cached responses hold the OLD
 *      DTO shape for up to the query TTL after a deploy.
 *
 * `.parse` failures are not local: one bad field throws for the whole document, so a required enum
 * here empties the entire roster rather than one cell.
 */
const coarsePaymentStatusField = z.enum(registrationPaymentStatuses).nullish();
const coarseRegistrationStatusField = z.enum(coarseRegistrationStatuses).nullish();

/**
 * REG-AVAIL — the server's answer to "may this caller register for this event at all?", from the
 * SAME rules RegisterForEventCommand enforces (Engagement RegistrationAvailabilityEvaluator). Disable
 * Register from this; never re-derive it from profile fields — the rule set grows server-side, and a
 * client copy would say "yes" to a rule it has never heard of.
 *
 * `reasons` are machine codes (see REGISTRATION_BLOCK_REASONS). Kept `z.string()` rather than an enum
 * ON PURPOSE: a new server rule must not fail the whole event list's parse — an unknown code renders
 * the generic message instead.
 *
 * The field itself is nullish: null = not evaluated (anonymous caller), or a server older than REG-AVAIL.
 * Both read as "no restriction known" — the register call remains the authority either way.
 */
export const registrationAvailabilitySchema = z.object({
  canRegister: z.boolean(),
  reasons: z.array(z.string()).default([])
});
export type RegistrationAvailability = z.infer<typeof registrationAvailabilitySchema>;

/** REG-AVAIL reason codes the server sends today (Engagement RegistrationBlockReasons). */
export const REGISTRATION_BLOCK_REASONS = {
  schoolMissing: 'school_missing',
  schoolNotActive: 'school_not_active',
  /** The school was proposed at signup and is awaiting an administrator's approval. */
  schoolPending: 'school_pending',
  profileUnavailable: 'profile_unavailable',
  /** PURCH-SW (mig 624): the event admits students only through a teacher/partner-assigned seat. */
  personalPurchaseDisabled: 'personal_purchase_disabled',
  /** PURCH-SW (mig 624): the event's ticket sales are paused. */
  salesPaused: 'sales_paused'
} as const;

/** PURCH-SW (mig 624): the 409 a teacher/partner purchase, offer or seat increase gets while sales are paused. */
export const EVENT_SALES_PAUSED_ERROR_TYPE = 'engagement-event-sales-paused';

/** The 403 type the register command and single ticket assignment refuse with; `reasons` rides the body. */
export const REGISTRATION_UNAVAILABLE_ERROR_TYPE = 'engagement-registration-unavailable';

/**
 * The reason codes of a REG-AVAIL 403, or null when `problem` is some other error. The type arrives as
 * a full URI (`https://docs.zercode.com/errors/engagement-registration-unavailable`), hence `endsWith`.
 * A matching problem with no usable `reasons` yields `[]` — still a REG-AVAIL refusal, generic message.
 */
export function registrationUnavailableReasons(
  problem: { type?: string; status?: number; reasons?: unknown } | null | undefined
): string[] | null {
  if (!problem || problem.status !== 403 || !problem.type?.endsWith(REGISTRATION_UNAVAILABLE_ERROR_TYPE)) {
    return null;
  }
  return Array.isArray(problem.reasons) ? problem.reasons.filter((r): r is string => typeof r === 'string') : [];
}

export const eventSummarySchema = z.object({
  id: z.guid(),
  title: z.string(),
  eventTypeDefinitionId: z.guid().nullish(), // opak definition id (event_types)
  /**
   * PRG-MULTI (mig 491): the event's programme SET — opaque definition ids (group program_types).
   * The server always sends at least one.
   *
   * ⚠ TOLERANT ON PURPOSE (`nullish` → `[]`). This replaced a single `programTypeId`, so a response
   * cached before the deploy — or a stale server during a rollout — carries the OLD field and none of
   * this one. A required array would make `.parse` throw for the WHOLE list, and a list schema that
   * throws blanks the entire page rather than one cell. An empty array degrades to "no programme
   * shown", which is visible and harmless.
   */
  programTypeIds: z.array(z.guid()).nullish().transform((v) => v ?? []),
  currency: z.string().default('TRY'), // 3-letter ISO 4217 for the event's price lists
  status: z.enum(eventStatuses),
  startsAtUtc: isoDate,
  endsAtUtc: isoDate,
  location: z.string().nullish(),
  capacity: z.number().int().nullish(),
  displayZone: z.string().nullish(),
  languageDefinitionId: z.guid().nullish(), // opak definition id (languages)
  passingRatio: z.number().nullish(),
  responsibleUserRef: z.guid().nullish(),
  organizerId: z.guid().nullish(), // opak company uuid (type='organizer')
  isOnline: z.boolean().optional().default(false),
  meetingUrl: z.string().nullish(),
  // Caller's own registration state on this event (null = not registered). Lets the list render the
  // right chip/button on a fresh load without a session-local overlay.
  myRegistrationStatus: z.string().nullish(),
  // EVT-PAY: cagiranin kendi kaydinin kimligi + odeme durumu projeksiyonu. Ogrenci odeme adimini
  // bunlarla kurar (sayfa yenilendikten sonra da). Absent -> odeme gerekmiyor varsayilir; gercek
  // kapi SUNUCUDADIR, bu alan yalniz arayuzu yonlendirir.
  myRegistrationId: z.guid().nullish(),
  myPaymentState: z
    .enum(['not_required', 'awaiting_plan', 'plan_active', 'awaiting_approval', 'partially_paid', 'paid', 'defaulted'])
    .nullish()
    .transform((v) => v ?? 'not_required'),
  // REG-2AX (mig 439) — the caller's own coarse pair.
  //
  // ⚠ `myCoarseRegistrationStatus`, NOT `myRegistrationStatus`: that name is taken by the LIFECYCLE
  // (registered | awaiting_payment | waitlisted | cancelled | attended) which three SPAs read, and the
  // server field is named the same way for the same reason.
  myPaymentStatus: coarsePaymentStatusField,
  myCoarseRegistrationStatus: coarseRegistrationStatusField,
  // CNT-PCT (mig 458): içerik AÇILDIYSA damgalanır — tahsil edilen para etkinliğin
  // funding_min_percent'ine (varsayılan %20) ulaştığı an. Dolu = içerik sekmesi, DOSYALAR ve kendi
  // seyahat/vize kaydı okunabilir.
  //
  // ⚠ NULL "KAPALI" DEMEK DEĞİLDİR ve tek başına okunursa ücretsiz etkinlikleri yanlışlıkla kilitli
  // gösterir: sunucudaki kapı İKİ tanığı OR'lar ve eskisi (borç yok = not_required|paid) ZEMİN olarak
  // durur — ödenecek bir şey olmayan kayda damga hiç yazılmaz. İstemci de ikisini OR'lamalı.
  myContentUnlockedAt: isoDate.nullish(),
  myWaitlistPosition: z.number().int().nullish(),
  // EV-COVER (mig 240): kapak görseli var mı — byte'lar GET /events/{id}/cover'dan gelir (auth'lı blob).
  hasCoverImage: z.boolean().optional().default(false),
  // PRC (alıcı-çözümü v1): aktif dönemin VARSAYILAN bilet fiyatlarından min "başlangıç" tutarı (currency birimi); yoksa null.
  priceFrom: z.number().nullish(),
  // Etkinliğin ücretli bileti yok (saf RSVP) → ücretsiz.
  isFree: z.boolean().optional().default(false),
  // TCH-TKT/P2: teacher-purchased tickets that have not become a seat-holding registration yet ALSO
  // hold seats, so availability is NOT `capacity - registeredCount` any more. Both fields are computed
  // SERVER-side with the same EventCapacity.IsFull the register handler uses — do NOT re-derive
  // fullness in the FE: doing so is what showed "Register" on a full event and silently waitlisted the
  // student.
  reservedCount: z.number().int().nullish().transform((v) => v ?? 0),
  isFull: z.boolean().nullish().transform((v) => v ?? false),
  /** Seats left (capacity − seat-holding registrations − reservations); null = unlimited. Server-computed. */
  seatsRemaining: z.number().int().nullish(),
  // TCH-TKT: a ticket the caller's TEACHER bought and assigned to them, still unused (the list twin of
  // the detail field). Present => the student can register WITHOUT paying; the events grid shows an
  // "your teacher reserved a ticket" badge from it. The query is [NoCache] server-side because this
  // field is caller-specific.
  myAssignedTicketId: z.guid().nullish(),
  // TCH-INV (mig 292): a pending OPEN invitation for the caller — the kind that reserves NO seat.
  // Kept separate from myAssignedTicketId because the copy differs: "your ticket is ready" vs "you
  // were invited". Server-side [NoCache], like every caller-specific field here.
  myOpenInvitationId: z.guid().nullish(),
  /** REG-AVAIL: may the CALLER register at all, and why not — see registrationAvailabilitySchema. */
  myRegistrationAvailability: registrationAvailabilitySchema.nullish(),
  // PURCH-SW (mig 624): who may buy. personalPurchaseDisabled = a student cannot register/buy for themselves
  // (only a teacher/partner-assigned seat admits them); salesPaused = no new purchase of any kind. Defaulted
  // false so an older server keeps today's behaviour. The student's own answer is already folded into
  // myRegistrationAvailability.reasons; the flags are for the admin form and the teacher's Buy button.
  personalPurchaseDisabled: z.boolean().optional().default(false),
  salesPaused: z.boolean().optional().default(false),
});

/** EventDetailDto */
export const eventDetailSchema = z.object({
  id: z.guid(),
  title: z.string(),
  description: z.string().nullish(),
  eventTypeDefinitionId: z.guid().nullish(), // opak definition id (event_types) — DG-3 mig 205
  /** PRG-MULTI (mig 491): the event's programme SET. Tolerant for the same reason as the summary. */
  programTypeIds: z.array(z.guid()).nullish().transform((v) => v ?? []),
  currency: z.string().default('TRY'), // 3-letter ISO 4217 for the event's price lists
  status: z.enum(eventStatuses),
  startsAtUtc: isoDate,
  endsAtUtc: isoDate,
  location: z.string().nullish(),
  capacity: z.number().int().nullish(),
  displayZone: z.string().nullish(),
  languageDefinitionId: z.guid().nullish(), // opak definition id (languages) — DG-3 mig 205
  passingRatio: z.number().nullish(),
  responsibleUserRef: z.guid().nullish(),
  organizerId: z.guid().nullish(), // opak company uuid (type='organizer')
  isOnline: z.boolean().optional().default(false),
  meetingUrl: z.string().nullish(),
  // DG-3: tags stay string[] but each element is now a definition ID (was a code); group `event_tags`.
  tags: z.array(z.string()).optional().default([]),
  // Audience filters (EV audience epic) — all OPTIONAL, empty array = unrestricted (open to everyone).
  // DG-3: countries/ageIntervals/genders values are now definition IDs (Definitions groups
  // `country`/`age_intervals`/`genders`); schools = company uuids (type='school', UNCHANGED).
  // Forward/back-compat (nullish→[]).
  countries: z.array(z.string()).nullish().transform((v) => v ?? []),
  schools: z.array(z.string()).nullish().transform((v) => v ?? []),
  ageIntervals: z.array(z.string()).nullish().transform((v) => v ?? []),
  genders: z.array(z.string()).nullish().transform((v) => v ?? []),
  // EV-AUD-EXCL (mig 487) — the EXCLUDE side of the same dimensions. Empty = nobody excluded.
  // A dimension carries EITHER list, never both: the server rejects both-non-empty with 422.
  // ⚠ nullish→[] is load-bearing here, not boilerplate: these keys are absent from every response
  // a pre-487 server sends, and a bare z.array() would throw and blank the whole page rather than
  // one field (see the nullable-column zod sweep).
  // There is no excludedAgeIntervals — the age_interval dimension is inert server-side.
  excludedCountries: z.array(z.string()).nullish().transform((v) => v ?? []),
  excludedSchools: z.array(z.string()).nullish().transform((v) => v ?? []),
  excludedGenders: z.array(z.string()).nullish().transform((v) => v ?? []),
  registeredCount: z.number().int(),
  waitlistedCount: z.number().int(), // #3 bekleme listesi
  // Caller's own registration state on this event (null = not registered).
  myRegistrationStatus: z.string().nullish(),
  // EVT-PAY: cagiranin kendi kaydinin kimligi + odeme durumu projeksiyonu. Ogrenci odeme adimini
  // bunlarla kurar (sayfa yenilendikten sonra da). Absent -> odeme gerekmiyor varsayilir; gercek
  // kapi SUNUCUDADIR, bu alan yalniz arayuzu yonlendirir.
  myRegistrationId: z.guid().nullish(),
  myPaymentState: z
    .enum(['not_required', 'awaiting_plan', 'plan_active', 'awaiting_approval', 'partially_paid', 'paid', 'defaulted'])
    .nullish()
    .transform((v) => v ?? 'not_required'),
  // REG-2AX (mig 439) — the caller's own coarse pair.
  //
  // ⚠ `myCoarseRegistrationStatus`, NOT `myRegistrationStatus`: that name is taken by the LIFECYCLE
  // (registered | awaiting_payment | waitlisted | cancelled | attended) which three SPAs read, and the
  // server field is named the same way for the same reason.
  myPaymentStatus: coarsePaymentStatusField,
  myCoarseRegistrationStatus: coarseRegistrationStatusField,
  // CNT-PCT (mig 458): içerik AÇILDIYSA damgalanır — tahsil edilen para etkinliğin
  // funding_min_percent'ine (varsayılan %20) ulaştığı an. Dolu = içerik sekmesi, DOSYALAR ve kendi
  // seyahat/vize kaydı okunabilir.
  //
  // ⚠ NULL "KAPALI" DEMEK DEĞİLDİR ve tek başına okunursa ücretsiz etkinlikleri yanlışlıkla kilitli
  // gösterir: sunucudaki kapı İKİ tanığı OR'lar ve eskisi (borç yok = not_required|paid) ZEMİN olarak
  // durur — ödenecek bir şey olmayan kayda damga hiç yazılmaz. İstemci de ikisini OR'lamalı.
  myContentUnlockedAt: isoDate.nullish(),
  // CNT-PCT: içeriği açmak için HÂLÂ ödenmesi gereken tutar — "materyalleri açmak için ₺X daha".
  // Açık kayıtta ve ücretsiz/biletsiz kayıtta null.
  //
  // ⚠ YALNIZ DETAYDA (özet şemada yok, bilerek): eşik etkinlik başına çözülür ve listede sayfa başına
  // N okuma demekti. Kart için "açık mı" yeter. Yüzdeyi İSTEMCİDE çözmeye kalkmayın — sunucu TUTAR
  // gönderir, çünkü etkinlik bazlı geçersiz kılmayı (mig 353) ikinci bir yerde türetmek olurdu.
  myPayToUnlockContent: z.number().nullish(),
  myWaitlistPosition: z.number().int().nullish(),
  createdAtUtc: isoDate,
  updatedAtUtc: isoDate,
  // EV-COVER (mig 240): kapak görseli var mı — byte'lar GET /events/{id}/cover'dan gelir (auth'lı blob).
  hasCoverImage: z.boolean().optional().default(false),
  // PRC (alıcı-çözümü v1): aktif dönemin VARSAYILAN bilet fiyatlarından min "başlangıç" tutarı (currency birimi); yoksa null.
  priceFrom: z.number().nullish(),
  // Etkinliğin ücretli bileti yok (saf RSVP) → ücretsiz.
  isFree: z.boolean().optional().default(false),
  // CF (mig 252): the event has a consent form → the register flow MUST show it and collect consent
  // (the server 422s otherwise). Bytes come from GET /events/{id}/consent-form (authorized blob).
  hasConsentForm: z.boolean().optional().default(false),
  // Which revision is current; a consent records the revision it was given against.
  consentFormUploadedAtUtc: isoDate.nullish(),
  // EVT-PAY (mig 271): son taksit etkinlik baslangicindan en az bu kadar gun ONCE odenmis olmali.
  // NULL = servis varsayilani (7 gun) — form bos gosterir ve bos gonderirse varsayilan korunur.
  paymentDeadlineDays: z.number().int().nullish(),
  // FUND (mig 353): "ödendi seç" eşiği — biletin TAHSİL EDİLEN birim fiyatının yüzdesi.
  // NULL = platform varsayılanı (%20); form boş gösterir ve boş gönderirse varsayılan korunur.
  // ⚠ Boş ile 20 AYNI ŞEY DEĞİL: ikincisi "biri 20 seçti" demektir ve varsayılan değişirse takip etmez.
  fundingMinPercent: z.number().int().nullish(),
  // INST-SELL (mig 277): dahili "Genel" fiyat listesinin taksit anahtarı — Fiyat Listesi sekmesindeki
  // Genel satırı bunu gösterir (o listenin kendi satırı yoktur). false = yalnız tek çekim önerilir.
  // Optional+default: FE, alanı taşımayan bir BE'den önce yayına alınabilir ve "açık" güvenli varsayımdır
  // (kapalı varsaymak, taksidi hiç sunmayan bir UI gösterirdi).
  defaultInstallmentsEnabled: z.boolean().optional().default(true),
  // CUR (mig 286): SEÇİLEN para biriminin OPAK definition id'si (grup 'currencies'). `currency` ise
  // ÖDEME HATTININ kullandığı ISO-4217 KODUdur (ödenebilirlik olayı → commerce.orders → Stripe) —
  // ikisi birlikte yaşar, biri diğerinin yerini ALMAZ. Form seçimi bu id'den geri okur.
  // EVT-MODE (mig 308): how much this event wants each information block — 'off' | 'optional' |
  // 'required'. The student shell builds its tab list from these instead of a hardcoded array.
  //
  // NOT z.enum(): an older/newer server must not fail the parse over a value this build does not know.
  // Optional+default keeps the FE deployable ahead of the BE, and the defaults are the SAFE assumption —
  // they reproduce today's behaviour (both tabs shown, no passport expectation) rather than hiding a tab
  // because a field was missing.
  travelInfoMode: z.string().optional().default('optional'),
  visaInfoMode: z.string().optional().default('optional'),
  passportMode: z.string().optional().default('off'),
  // AUTO-REG (mig 606): the student is registered without completing a registration themselves (a funded
  // assigned seat registers its holder; a free event registers in one click). Defaulted false so an older
  // server keeps today's behaviour. Such an event collects nothing — the server enforces it (422).
  autoRegister: z.boolean().optional().default(false),
  currencyDefinitionId: z.guid().nullish(),
  // TCH-TKT (mig 281): öğretmenin ÇAĞIRANA atadığı, ÖDENMİŞ ve kullanılmamış bilet. Kayıt formu bunu
  // "hazır bilet" olarak gösterir ve ödeme adımını ATLAR. null/absent = davet yok.
  // Sunucu bu alanları YALNIZ atama assigned + batch paid iken doldurur; FE ayrıca kontrol etmez —
  // "bedava kayıt" sinyalinin tek kaynağı sunucudur.
  myAssignedTicketId: z.guid().nullish(),
  myAllocationId: z.guid().nullish(),
  // TCH-TKT/P2: teacher-purchased tickets that have not become a seat-holding registration yet ALSO
  // hold seats, so availability is NOT `capacity - registeredCount` any more. Both fields are computed
  // SERVER-side with the same EventCapacity.IsFull the register handler uses — do NOT re-derive
  // fullness in the FE: doing so is what showed "Register" on a full event and silently waitlisted the
  // student.
  reservedCount: z.number().int().nullish().transform((v) => v ?? 0),
  isFull: z.boolean().nullish().transform((v) => v ?? false),
  /** Seats left (capacity − seat-holding registrations − reservations); null = unlimited. Server-computed. */
  seatsRemaining: z.number().int().nullish(),
  // TKT-CAP (mig 288): the ticket TYPES' attendee limits must sum to at most the event capacity.
  // `ticketLimitsTotal` is that sum (unlimited ticket types count 0) and `ticketLimitsUnallocated`
  // is what is left to hand out (null = unlimited capacity ⇒ no rule). Both are server-computed by
  // the same summer the ticket endpoints validate against — the admin form shows them as the cap,
  // but the authority stays the 422 (`allowed`).
  ticketLimitsTotal: z.number().int().nullish().transform((v) => v ?? 0),
  ticketLimitsUnallocated: z.number().int().nullish(),
  /** TCH-INV (mig 292): a pending OPEN invitation for the caller (reserves no seat). */
  myOpenInvitationId: z.guid().nullish(),
  /** REG-AVAIL: may the CALLER register at all, and why not — see registrationAvailabilitySchema. */
  myRegistrationAvailability: registrationAvailabilitySchema.nullish(),
  // PURCH-SW (mig 624): who may buy. personalPurchaseDisabled = a student cannot register/buy for themselves
  // (only a teacher/partner-assigned seat admits them); salesPaused = no new purchase of any kind. Defaulted
  // false so an older server keeps today's behaviour. The student's own answer is already folded into
  // myRegistrationAvailability.reasons; the flags are for the admin form and the teacher's Buy button.
  personalPurchaseDisabled: z.boolean().optional().default(false),
  salesPaused: z.boolean().optional().default(false),
});

export const eventListSchema = pagedListSchema(eventSummarySchema);

/**
 * YOUR-EVENTS — GET /api/v1/engagement/events/mine: every event the caller is signed up for
 * (registered | awaiting_payment | waitlisted, not yet started) plus teacher-reserved tickets, each as the
 * catalogue's own summary + the open STEPS the server derived (payment, required travel/visa, ticket).
 *
 * ⚠ `kind` and `target` are STRINGS, not enums, on purpose: the server adds kinds additively (consent and
 * questions are next), and a strict enum here would make `.parse` throw for the WHOLE list during a
 * rolling deploy — the box would vanish rather than miss one row. Readers normalise with
 * {@link MY_EVENT_ACTION_KINDS} and drop what they don't know.
 */
export const MY_EVENT_ACTION_KINDS = [
  'payment-plan',
  'installment',
  'payment-stopped',
  'travel',
  'visa',
  'ticket',
  // phase 2 — consent and questions resolved on the event page, passport on the profile
  'consent',
  'questions',
  'passport'
] as const;
export type MyEventActionKind = (typeof MY_EVENT_ACTION_KINDS)[number];

export const myEventActionSchema = z.object({
  kind: z.string(),
  target: z.string(),
  dueAtUtc: isoDate.nullish(),
  amount: z.number().nullish(),
  currency: z.string().nullish(),
  sequenceNo: z.number().int().nullish(),
  count: z.number().int().nullish(),
  /** passport only: 'incomplete' | 'expires' (complete, but not valid on the event's last day). */
  reason: z.string().nullish()
});

export const myEventSchema = z.object({
  event: eventSummarySchema,
  actions: z.array(myEventActionSchema).nullish().transform((v) => v ?? []),
  /** Steps done / steps that apply (seat, payment, required travel, required visa); null for waitlist/ticket. */
  progress: z.object({ done: z.number().int(), total: z.number().int() }).nullish(),
  /** The instant the whole order must be paid by; null when nothing is owed. */
  paymentDueByUtc: isoDate.nullish(),
  /** Commerce's plan position; null = no plan or Commerce did not answer (the box then shows no installment step). */
  installments: z.object({ planStatus: z.string(), count: z.number().int(), settledCount: z.number().int() }).nullish()
});

export const myEventListSchema = z.array(myEventSchema);
export type MyEventAction = z.infer<typeof myEventActionSchema>;
export type MyEvent = z.infer<typeof myEventSchema>;

/** Narrows a wire kind to one this client renders; unknown kinds return null and are dropped. */
export function knownMyEventActionKind(kind: string): MyEventActionKind | null {
  return (MY_EVENT_ACTION_KINDS as readonly string[]).includes(kind) ? (kind as MyEventActionKind) : null;
}

// ------------------------------ EVENT COMMERCE (Engagement :5800) -------------------------------
// An event groups commerce products by product type, bundles them into tickets, and prices tickets per
// adopted Commerce pricing list. productTypeId is an OPAQUE definition id (group product_types);
// productRef is an OPAQUE commerce.products uuid (never resolved cross-schema [STD-OWN]). camelCase
// wire, Zod-parsed at the seam. Endpoints under /api/v1/engagement/events/{id}/...

/**
 * EventProductGroupDto — an event groups its products by product type. Each group is an explicit
 * product_types definition (productTypeId) holding attached products (productRef → commerce.products).
 * GET /events/{id}/product-groups returns a bare array (ordered by sortOrder).
 */
export const eventProductGroupSchema = z.object({
  id: z.guid(),
  productTypeId: z.guid(),
  sortOrder: z.number().int(),
  // A required group with ≥1 product must be satisfied by every ticket.
  isRequired: z.boolean().default(false),
  products: z.array(
    z.object({
      id: z.guid(), // eventProductId
      productRef: z.guid(),
      sortOrder: z.number().int(),
      /**
       * PRD-LBL (mig 298): attendee-facing labels, denormalized onto event_products at attach time.
       *
       * ⚠ THE STUDENT SPA HAS NO OTHER SOURCE FOR THESE. The product name lives in commerce.products
       * and `commerce.products.view` is not granted to the student role, so `productRef` is
       * unresolvable there — render `displayName`, never the ref. `''` means the label was never
       * captured (a pre-298 row the migration could not resolve); fall back to the group name.
       *
       * Defaulted so a pre-298 response still parses.
       */
      displayName: z.string().default(''),
      displayDescription: z.string().nullish(),
      features: z.array(z.string()).default([])
    })
  )
});
export const eventProductGroupListSchema = z.array(eventProductGroupSchema);

/** PRD-LBL: POST/PUT body for a product's attendee-facing labels. SET semantics — send all three. */
export interface EventProductLabelsRequest {
  displayName?: string | null;
  displayDescription?: string | null;
  features?: string[];
}

/**
 * PRD-ORD: body for all THREE reorder routes — `PUT /events/{id}/product-groups/order`,
 * `PUT /events/{id}/product-groups/{groupId}/products/order` and `PUT /events/{id}/tickets/order`.
 *
 * ⚠ FULL PERMUTATION, NOT A PARTIAL SORT. `orderedIds` must contain exactly the ids currently in the
 * set; anything missing/extra/duplicated is a **409**, on purpose — a row left out would keep its old
 * sort_order and land in the middle of the server's normalized 1..n. So send the whole list, and on
 * 409 refetch rather than retry (the client's list was stale).
 *
 * The two product routes respond with the affected list ALREADY in the new order. The TICKET route
 * deliberately does not: it returns `{id, name, sortOrder}` only, because the full ticket DTO carries
 * live availability that only the list query computes — refetch `/tickets` (it is uncached) instead.
 */
export interface ReorderEventCatalogRequest {
  orderedIds: string[];
}

/** PRD-ORD: what `PUT /events/{id}/tickets/order` echoes back — identity and the new order, nothing else. */
export interface EventTicketOrderRow {
  id: string;
  name: string;
  sortOrder: number;
}

/** PRD-LBL caps — mirror the server validators and ck_event_products_labels (mig 298). */
export const eventProductLabelLimits = {
  displayName: 200,
  displayDescription: 1000,
  features: 20,
  featureLength: 120
} as const;

/** PRD-LBL: one product row inside a group (the type alias for the group itself lives below). */
export type EventProduct = z.infer<typeof eventProductGroupSchema>['products'][number];

/**
 * EventTicketDto — a named bundle picking one product per (selected) group. GET /events/{id}/tickets
 * returns a bare array. A ticket must have a selection for every REQUIRED group that has ≥1 product;
 * optional (and required-but-empty) groups may be omitted. Writes send only {groupId, eventProductId}.
 */
export const eventTicketSchema = z.object({
  id: z.guid(),
  /**
   * TKT-PRG (mig 492): the programme this ticket belongs to — an opaque definitions id (group
   * `program_types`), always one the EVENT serves.
   *
   * ⚠ IT IS PART OF THE TICKET'S IDENTITY, not a label. Two tickets on one event may differ ONLY by
   * this field — identical products, two prices, one per programme — which is the setup a joint
   * event needs and could not express before. So a tickets table that omits this column shows two
   * rows a reader cannot tell apart.
   *
   * ⚠ TOLERANT (`nullish` → ''), like every other field added to a live schema here: a response
   * cached before the deploy has no such key, and a required field would throw for the whole
   * document — blanking the entire tickets tab rather than one cell.
   */
  programTypeId: z.guid().nullish().transform((v) => v ?? ''),
  name: z.string(),
  /**
   * TAX (mig 383): KDV rate as a percentage (20 = %20).
   *
   * ⚠ The ticket PRICE is TAX-INCLUSIVE — this splits it, it does not add to it. Commerce never reads
   * this column; the rate rides the payable event and is frozen onto the order line, so editing it
   * changes only purchases made AFTERWARDS.
   *
   * `.default(0)` so a pre-383 server response still parses — a failing ticket schema blanks the whole
   * tickets tab, not one field.
   */
  taxRate: z.number().default(0),
  /** TAX-REF (mig 556): the Odoo sales tax (billing.odoo_settings id) the rate was copied from. */
  taxSettingId: z.string().nullish(),
  sortOrder: z.number().int(),
  // Capacity limits (null = unlimited; >= 1 when set).
  maxAttendees: z.number().int().nullish(),
  maxPerOrder: z.number().int().nullish(),
  /**
   * Seat-holding registrations for this ticket (confirmed, not waitlisted).
   *
   * ⚠ NOT AVAILABILITY — informational only (the admin's "28 / 40"). It does NOT count seats a
   * teacher has bought but not yet handed out, so `maxAttendees - soldCount` reports a free seat on
   * a ticket the server will silently WAITLIST. Use {@link eventTicketSchema.shape.seatsRemaining}
   * and `isFull` instead; they come from the same rule the register handler applies.
   */
  soldCount: z.number().int().default(0),
  /**
   * TKT-AVAIL: server-computed availability (registrations + reserved teacher allocations).
   * `seatsRemaining` is null when the ticket type is unlimited, and `isFull` is then always false.
   *
   * Both are nullish so a response from a server that predates TKT-AVAIL still parses — `isFull` then
   * reads false, which matches the old (optimistic) behaviour rather than hiding every ticket.
   * `.nullish().transform()` (not `.optional().default()`) to match the event schemas above and to
   * tolerate an explicit `isFull: null`, which a default alone would reject.
   */
  seatsRemaining: z.number().int().nullish(),
  isFull: z
    .boolean()
    .nullish()
    .transform((v) => v ?? false),
  /**
   * TKT-CODE (mig 299): short human handle shown beside the price ("KPD-ET"). A LABEL, never a key —
   * do not match, sort or look up by it; `id` is the identity. null/absent = no code.
   * Unique per event, case-insensitively, and the typed case is preserved for display.
   */
  code: z.string().nullish(),
  // TKT-DETAIL (mig 243): plain-text description + whether a PDF document exists (bytes at .../document).
  description: z.string().nullish(),
  hasDocument: z.boolean().optional().default(false),
  /**
   * TKT-ROLE (mig 332): the PARTICIPANT role codes this ticket is open to.
   *
   * ⚠ EMPTY MEANS EVERYONE, not nobody — mig 332 ships with no backfill, so every existing ticket
   * arrives with an empty array and must keep behaving as it does today. Render "anyone can see this"
   * for `[]`, never "no one".
   *
   * Values are `rbac.roles.code`, the same strings the JWT carries. Defaults to `[]` so a response
   * from a server predating TKT-ROLE still parses as unrestricted.
   *
   * The STUDENT-facing list (`/ticket-prices`) is already filtered server-side, so this field exists
   * for the ADMIN editor; a participant never needs to read it.
   */
  roleCodes: z.array(z.string()).nullish().transform((v) => v ?? []),
  /**
   * TKT-GEO (mig 495): the country definition ids this ticket is CLOSED to.
   *
   * ⚠ EMPTY MEANS EVERYONE SEES IT, not nobody — the exclude twin of `roleCodes` above, and mig 495
   * ships with no backfill for exactly that reason. Render "no country restriction" for `[]`.
   *
   * ⚠ AND A USER WITH NO COUNTRY IS NOT EXCLUDED. A restriction cannot be proven about a value that
   * was never recorded, which is the rule `AudienceEligibility` already applies to the EVENT-level
   * country filter; the ticket rule matches it deliberately. The admin editor has to SAY this — the
   * assumption is always the opposite — so the copy lives beside the picker, not here.
   *
   * Values are opaque `definitions.definitions.id` (group `country`), the same ids the event's own
   * `countries`/`excludedCountries` carry. The PARTICIPANT surfaces are filtered server-side, so this
   * field exists for the ADMIN editor; a participant never needs to read it.
   *
   * `.nullish().transform()` like every other field added to a live schema here: a response cached
   * before the deploy has no such key, and a required field would throw for the whole document —
   * blanking the entire tickets tab rather than one cell.
   */
  excludedCountryIds: z.array(z.guid()).nullish().transform((v) => v ?? []),
  selections: z.array(
    z.object({
      groupId: z.guid(),
      eventProductId: z.guid(),
      productRef: z.guid()
    })
  ),
  /**
   * PRD-GRP phase 3 (mig 639): the Commerce product group this ticket sells (opaque id), and the
   * snapshot of its products taken when the group was added. null / [] = an older ticket, described by
   * `selections`. Tolerant like every field added to this live schema.
   */
  productGroupRef: z.guid().nullish(),
  items: z
    .array(
      z.object({
        productRef: z.guid(),
        productTypeId: z.guid(),
        displayName: z.string(),
        displayDescription: z.string().nullish(),
        sortOrder: z.number().int(),
        /** PRD-GRP phase 5: the short bullet list under the product on the ticket card. */
        features: z
          .array(z.string())
          .nullish()
          .transform((v) => v ?? [])
      })
    )
    .nullish()
    .transform((v) => v ?? [])
});
export const eventTicketListSchema = z.array(eventTicketSchema);

/**
 * INST-PRE — EventTicketPricesDto: GET /events/{id}/ticket-prices.
 *
 * The registration form's payment step needs the ticket price BEFORE registering, and no order
 * exists yet (Commerce creates it asynchronously from the payable event). eventTicketSchema carries
 * no amount, so this is the only source. The server resolves the amount for the APPLICABLE pricing
 * period using the same rule as the catalogue and as the charge itself — the period rule is
 * deliberately not reimplemented here, or the preview and the charge would drift apart.
 *
 * `amount: 0` = a free ticket (PRC-ONLY: free is an explicit 0 now). `amount: null` only ever comes with
 * `offered: false`: no amount resolves for THIS caller (priced nowhere, or only on price lists they do not
 * match), so it must NOT be read as free. Only an exempt caller (admin, bulk buyer) ever receives such a
 * row; a participant never sees the ticket at all.
 * `installmentsAllowed` is INST-SELL (mig 277) — false => show a single payment only. The real gate
 * is server-side; this is UX.
 */
export const eventTicketResolvedPriceSchema = z.object({
  ticketId: z.guid(),
  ticketName: z.string(),
  sortOrder: z.number().int(),
  amount: z.number().nullish(),
  // Default true: a server that predates the field only ever meant "free" by a null amount.
  offered: z.boolean().optional().default(true),
  /**
   * INST-LIST (mig 577): the price list that priced THIS ticket for the caller (null = General). Passed to
   * Commerce's instalment preview so the form shows that list's options. Opaque — never displayed.
   */
  pricingListId: z.guid().nullish(),
  /** INST-LIST: that list's instalment switch, per ticket (the event-level flag can only report one). */
  installmentsAllowed: z.boolean().nullish(),
  /**
   * PCOM P3: the partner tier this amount came from — set only for partner staff buying at their partner's
   * prices. The price follows the tier the partner has reached at the moment of purchase.
   */
  partnerTier: z.number().int().nullish()
});

export const eventTicketPricesSchema = z.object({
  eventId: z.guid(),
  currency: z.string().default('TRY'),
  paymentDueByUtc: isoDate.nullish(),
  installmentsAllowed: z.boolean().optional().default(true),
  items: z.array(eventTicketResolvedPriceSchema),
  /**
   * OFR-REQ: may THIS caller propose a price — i.e. should the buy dialog show "Teklif ver"?
   *
   * ⚠ NOT an authorization result and NOT re-derivable here. The server answers three things at once
   * (purchase permission + partner-staff persona + the tenant having an ACTIVE approval chain); the
   * last one especially cannot be known client-side, and without it the button would only ever lead to
   * a 422. `.default(false)` so a server that predates the field hides the action instead of offering
   * one that fails.
   */
  canOffer: z.boolean().optional().default(false)
});

/**
 * EventPricingListDto — a Commerce pricing list ADDED to an event (ids only; the name is resolved from
 * the Commerce pricing-lists catalog). GET /events/{id}/pricing-lists returns a bare array.
 */
export const eventPricingListSchema = z.object({
  pricingListId: z.guid(),
  sortOrder: z.number().int(),
  // INST-SELL (mig 277): may tickets priced by this list be sold in installments? false => the payer
  // is offered a single payment only. Optional+default true so an older BE (no field) reads as "on",
  // which matches the column default and never hides installments that the server would still offer.
  // NOTE: conditional lists do not price a registration yet, so this switch is stored but dormant —
  // the server reports that as `affectsPaymentToday: false` on every write.
  installmentsEnabled: z.boolean().optional().default(true)
});
export const eventPricingListListSchema = z.array(eventPricingListSchema);

/**
 * EventTicketPriceDto — a per-ticket amount within an event pricing list (amount null = unset). GET
 * /events/{id}/pricing-lists/{pricingListId}/prices returns ALL event tickets (bare array); the PUT
 * SETs the provided rows (amount null clears).
 */
export const eventTicketPriceSchema = z.object({
  ticketId: z.guid(),
  ticketName: z.string(),
  sortOrder: z.number().int(),
  amount: z.number().nullable()
});
export const eventTicketPriceListSchema = z.array(eventTicketPriceSchema);

/**
 * EventPricingPeriodDto — a named date window that scopes ticket prices. startDate/endDate are ISO
 * date strings ("YYYY-MM-DD"); endDate >= startDate; periods within an event may not overlap.
 * GET /events/{id}/pricing-periods returns a bare array (ordered by sortOrder).
 */
export const eventPricingPeriodSchema = z.object({
  id: z.guid(),
  name: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  sortOrder: z.number().int()
});
export const eventPricingPeriodListSchema = z.array(eventPricingPeriodSchema);

export type EventProductGroup = z.infer<typeof eventProductGroupSchema>;
export type EventTicket = z.infer<typeof eventTicketSchema>;
export type EventTicketPrices = z.infer<typeof eventTicketPricesSchema>;
export type EventPricingList = z.infer<typeof eventPricingListSchema>;
export type EventTicketPrice = z.infer<typeof eventTicketPriceSchema>;
export type EventPricingPeriod = z.infer<typeof eventPricingPeriodSchema>;

// ------------------------------ event registration questions (EV questions epic) ---------------
// Admin-authored custom questions rendered in the student registration form. `type` is a FIXED
// UI-widget union (distinct from the exam `questionTypes`); `options` is populated ONLY for
// select/multiselect (empty otherwise). Captured answers are surfaced back on the registration
// roster so an admin can review each registrant's responses. camelCase wire, Zod-parsed at the seam.
export const eventQuestionTypes = ['text', 'textarea', 'number', 'select', 'multiselect', 'toggle', 'date', 'profile'] as const;
export type EventQuestionType = (typeof eventQuestionTypes)[number];

// PROF (mig 244): profile-field keys a 'profile' question can bind to (must match backend ProfileFields).
// mig 331: 'jobPosition' REMOVED — the profile column it read is gone. Must stay in step with the
// backend's ProfileFields.All [STD-DRIFT].
//
// AGECAT-PT (mig 471/473): 'englishAgeCategory' / 'mathAgeCategory' REMOVED — an age band belongs to a
// PROGRAM TYPE, and a tenant can add one at any moment, so the key cannot be a compile-time member of
// this list. It is PARAMETRISED instead (see below). Migration 473 rewrote the stored keys, so no live
// question was left bound to a name nothing resolves.
export const eventProfileFields = [
  'name', 'passportNumber', 'phone', 'birthDate', 'parentGuardianEmail', 'parentGuardianPhone',
  'nationality', 'gender', 'preferredLanguage'
] as const;
export type EventProfileField = (typeof eventProfileFields)[number];

/**
 * AGECAT-PT: prefix of a per-program-type age-category question key — `ageCategory:{programTypeId}`.
 *
 * ⚠ MUST STAY IN STEP WITH `ProfileFields.AgeCategoryPrefix` ON THE BACKEND [STD-DRIFT]. The server
 * validates by SHAPE (prefix + guid), not membership — it cannot resolve the program type across the
 * service boundary [STD-OWN] — so a key built wrongly here is STORED rather than rejected, and then
 * resolves to an empty answer forever: the admin keeps seeing the question, the student keeps
 * answering nothing, and nothing anywhere reports an error. Build keys with the helper below, never
 * by string concatenation at the call site.
 */
export const EVENT_PROFILE_AGE_CATEGORY_PREFIX = 'ageCategory:';

/** The profileField key binding a question to one program type's age band. */
export function eventProfileFieldForProgramType(programTypeId: string): string {
  return `${EVENT_PROFILE_AGE_CATEGORY_PREFIX}${programTypeId}`;
}

/**
 * The program type an age-category question key names, or null when `field` is not one.
 * A malformed key yields null rather than a partial id, so a caller cannot half-resolve it.
 */
export function programTypeIdFromEventProfileField(field: string | null | undefined): string | null {
  if (!field || !field.startsWith(EVENT_PROFILE_AGE_CATEGORY_PREFIX)) return null;
  const id = field.slice(EVENT_PROFILE_AGE_CATEGORY_PREFIX.length);
  return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(id) ? id : null;
}

export const eventQuestionOptionSchema = z.object({ value: z.string(), label: z.string() });
export type EventQuestionOption = z.infer<typeof eventQuestionOptionSchema>;

/** EventQuestionDto — options [] unless type ∈ {select,multiselect}; ordered client-side by orderNo. */
export const eventQuestionSchema = z.object({
  id: z.guid(),
  label: z.string(),
  type: z.enum(eventQuestionTypes),
  isRequired: z.boolean(),
  isActive: z.boolean(),
  orderNo: z.number().int(),
  options: z.array(eventQuestionOptionSchema).nullish().transform((v) => v ?? []),
  // PROF (mig 244): bound profile-field key when type='profile'; null otherwise.
  profileField: z.string().nullish()
});
export const eventQuestionListSchema = pagedListSchema(eventQuestionSchema);
export type EventQuestion = z.infer<typeof eventQuestionSchema>;

/** POST/PUT body for an event question (create/update). options [] unless select/multiselect. */
export interface EventQuestionWriteRequest {
  label: string;
  type: EventQuestionType;
  isRequired: boolean;
  isActive: boolean;
  orderNo: number;
  options: EventQuestionOption[];
  profileField?: string | null;
}

// ------------------------------ EVN: internal event notes (mig 208) -----------------------------
// Staff-only free-text notes on an event. Gated by DEDICATED permissions (engagement.event_notes.
// view / .manage). Not author-locked: anyone with manage can edit/delete any note. authorName /
// updatedByName are DENORMALIZED at write time on the server (JWT 'name'); the client only displays
// them [STD-OWN]. camelCase wire, Zod-parsed at the seam.
export const eventNoteSchema = z.object({
  id: z.guid(),
  body: z.string(),
  createdBy: z.guid().nullish(), // opaque identity uuid (author); display uses authorName
  authorName: z.string().nullish(),
  updatedByName: z.string().nullish(), // last editor's name; null until first edit
  createdAtUtc: isoDate,
  updatedAtUtc: isoDate
});
export const eventNoteListSchema = pagedListSchema(eventNoteSchema);
export type EventNote = z.infer<typeof eventNoteSchema>;

/** POST/PUT body for an internal event note (engagement.event_notes.manage). */
export interface EventNoteWriteRequest {
  body: string;
}

// ------------------------------ EVC: registered-attendee event content (mig 249) ----------------
// The event's rich (HTML) body shown to REGISTERED attendees — distinct from `description`, which is
// the public blurb on the product page. Read is GATED SERVER-SIDE: only a seat-holding registrant
// (registered|attended) or an engagement.events.update admin gets it; anyone else gets 403. The HTML
// is stored raw and MUST be DOMPurify-sanitized before render (same contract as description).
// `contentHtml` is '' when the admin has not authored anything yet (not a 404).
export const eventContentSchema = z.object({
  contentHtml: z.string(),
  updatedAtUtc: isoDate.nullish()
});
export type EventContent = z.infer<typeof eventContentSchema>;

/** PUT body for the event content (engagement.events.update). Empty string clears the content. */
export interface EventContentWriteRequest {
  contentHtml: string;
}

// ------------------------------ ECF: event content files (mig 250) ------------------------------
// Files attached to the registered-attendee content. METADATA only — the storage key is never on the
// wire [STD-OWN]; bytes come from GET /events/{id}/content/files/{fileId}, gated by the SAME rule as
// the content body. Allowed types are tenant reference data (the `filetypes` definitions group, whose
// value `code` is the extension); the admin UI reads that group to build its accept list, while the
// server independently refuses executables/scripts and enforces 10MB/file and 20 files/event.
export const eventContentFileSchema = z.object({
  id: z.guid(),
  /**
   * DOC-MEDIA (mig 726): the three file fields are null on a LINK-ONLY item — check `hasFile`, never one
   * of these on its own.
   */
  fileName: z.string().nullish(),
  contentType: z.string().nullish(),
  sizeBytes: z.number().nullish(),
  /**
   * DOC-MEDIA (mig 726): false = a link-only item with no bytes (the download endpoint 404s). Defaulted to
   * `true` so a pre-726 server — where every item IS a file and the field does not exist — still parses.
   */
  hasFile: z.boolean().optional().default(true),
  /** DOC-MEDIA: display title; null on a file that never got one — show `fileName` (see `contentItemTitle`). */
  title: z.string().nullish(),
  /**
   * DOC-MEDIA: the media link AS TYPED. ⚠ NEVER an iframe src — run it through `mediaLinkOf`, which
   * builds the player address from literals or hands back a vetted outbound href.
   */
  mediaUrl: z.string().nullish(),
  sortOrder: z.number(),
  createdAtUtc: isoDate,
  /**
   * ECF-AGE (mig 476): the age categories this file is open to — EMPTY means unrestricted, which is
   * what every pre-476 file is and stays. Opaque `definitions.age_categories` ids; the admin UI
   * resolves their names from the bands of the EVENT'S program type [STD-OWN].
   *
   * ⚠ THE LIST A STUDENT RECEIVES IS ALREADY FILTERED SERVER-SIDE — a file their band does not open is
   * not in the response at all, and the download endpoint re-checks independently. So this field is
   * for the ADMIN tab's badges, never for deciding what to render to a student: filtering here would
   * be a second, weaker copy of a rule that already ran.
   *
   * `.nullish().transform` mirrors `options` on event questions — a server that omits the field (an
   * older build mid-rollout) yields an unrestricted file, which is the pre-476 behaviour rather than
   * a crash.
   */
  ageCategoryIds: z.array(z.guid()).nullish().transform((v) => v ?? [])
});
export const eventContentFileListSchema = z.array(eventContentFileSchema);
export type EventContentFile = z.infer<typeof eventContentFileSchema>;

/** DOC-MEDIA — POST /events/{eventId}/content/links body: a link-only item. */
export interface EventContentLinkCreateRequest {
  title: string;
  url: string;
  /** The full set, as on the age-categories PUT — empty = every registered attendee. */
  ageCategoryIds: string[];
}

/**
 * DOC-MEDIA — PUT /events/{eventId}/content/files/{fileId}/link body. `url: null` removes the link
 * (a file item only — a link-only item answers 422 and must be deleted instead). `title: null` on a
 * file item means "show the file name".
 */
export interface EventContentLinkRequest {
  title: string | null;
  url: string | null;
}

/** Server-side caps for media links (mirrored from EventContentFileRules, mig 726). */
export const eventContentLinkLimits = { maxUrlLength: 2048, maxTitleLength: 200 } as const;

/** What a content item is called: its own title, else its file name. */
export function contentItemTitle(item: Pick<EventContentFile, 'title' | 'fileName'>): string {
  return item.title?.trim() || item.fileName || '';
}

/**
 * What a media link IS, decided at render — the same contract as the public site's video section.
 *
 * - `video`: a YouTube / Vimeo / Dailymotion link `parseVideoUrl` could read. The player address is
 *   built from `ref` by `siteVideoEmbedSrc`; the typed string never reaches an iframe.
 * - `link`: any other http(s) address — shown as an "Open link" button, never framed.
 * - `null`: not something we can show (empty, `javascript:`, unparseable). Renders NOTHING rather than
 *   a dead button; the server refuses those at write, so this is the "older data / rolling deploy" net.
 *
 * ⚠ A user-info link (`https://www.youtube.com@evil.example`) is refused here as well as on the server:
 * it reads as one site and goes to another, which is exactly the shape a "Watch on YouTube" button must
 * not carry.
 */
export type MediaLink = { kind: 'video'; ref: SiteVideoRef; href: string; host: string } | { kind: 'link'; href: string; host: string };

export function mediaLinkOf(raw: string | null | undefined): MediaLink | null {
  if (!raw) return null;
  const href = safeExternalUrl(raw);
  if (!href) return null;
  const url = new URL(href);
  if (url.username || url.password) return null;
  const host = url.hostname.replace(/^www\./, '');
  const ref = parseVideoUrl(href);
  return ref ? { kind: 'video', ref, href, host } : { kind: 'link', href, host };
}

/**
 * ECF-AGE — PUT /events/{eventId}/content/files/{fileId}/age-categories body.
 *
 * ⚠ THE FULL SET, ALWAYS. Unlike the profile placements (where absent means "leave alone"), an empty
 * array here CLEARS the restriction and makes the file visible to every registered attendee again —
 * that is the only way back to unrestricted, so it must not be confused with "no change".
 */
export interface EventContentFileAgeCategoriesRequest {
  ageCategoryIds: string[];
}

// ==============================|| EVT-EXAM (mig 535) — an online event's exam grid ||============= //

/**
 * One cell: a programme type, an age band, and the exam chosen for it.
 *
 * ⚠ `ageCategoryId: null` IS A REAL ROW, not a missing one — it means "this programme, all ages", and
 * it is the ONLY row a programme with no age categories gets. Three of the demo tenant's five
 * programmes are in that state, and without this row their group is empty and the publish gate can
 * never be satisfied.
 *
 * ⚠ `examId: null` is what the gate reads. Every null is a reason the event cannot be published.
 */
export const eventExamRowSchema = z.object({
  programTypeId: z.guid(),
  ageCategoryId: z.guid().nullish(),
  examId: z.guid().nullish()
});
export type EventExamRow = z.infer<typeof eventExamRowSchema>;

/**
 * GET /events/{id}/exams.
 *
 * `missingCount` comes from the server rather than being counted here on purpose: the publish gate
 * counts it the same way, and two counters that can drift is how a screen says "ready" over a 409.
 */
export const eventExamGridSchema = z.object({
  rows: z.array(eventExamRowSchema),
  missingCount: z.number().int(),
  /**
   * Exam ids already bound to ANOTHER event, so the picker stops offering them.
   *
   * An exam serves one event (ux_event_exams_exam, mig 535). Offering one that is spoken for earns a
   * 409 on save; this is how the choice is never presented. Defaulted so a payload from a pre-change
   * instance still parses during a rolling deploy instead of blanking the tab.
   */
  takenElsewhere: z.array(z.guid()).optional().default([])
});
export type EventExamGrid = z.infer<typeof eventExamGridSchema>;

/**
 * PUT /events/{id}/exams body — the WHOLE grid.
 *
 * ⚠ A COMBINATION YOU OMIT IS DELETED. That is the point rather than a hazard: when a programme
 * leaves the event, the SPA stops sending its rows and they must go, or the publish gate blocks
 * forever on combinations the event no longer has.
 */
export interface SetEventExamsRequest {
  rows: EventExamRow[];
}

/** Server-side hard caps for content attachments (mirrored from EventContentFileRules). */
export const eventContentFileLimits = { maxFileBytes: 10 * 1024 * 1024, maxFilesPerEvent: 20 } as const;

// ------------------------------ TRV: attendee travel legs (migs 259/260) ------------------------------
// A registered attendee's ARRIVAL and DEPARTURE travel plan for one event, filled in by the attendee on
// the student event page. One record per direction — the two take an identical parameter set, so the wire
// shape is a pair of identical legs rather than arrival*/departure* field pairs.
//
// The five definition ids are OPAQUE Definitions refs. TWO of them are CHILDREN of
// transportationTypeDefinitionId via the definitions engine's parentId — transporterCompanyDefinitionId
// (the carrier) and locationDefinitionId (the airport/station/port, migs 263/264) — so both pickers narrow
// once a type is chosen. The server does NOT verify either pairing (it cannot read the definitions
// schema), so both cascades are the client's responsibility [STD-OWN].
//
// terminalDefinitionId is NOT nested under the location (a deliberate deferral, mig 263 header), so a
// terminal value carries no indication of which location it belongs to.
//
// `hasTicket` is a FLAG, not a URL: the ticket (PDF or image) is an authorized blob fetched from
// GET .../travel/{direction}/ticket via client.getBlob; the storage key never reaches the client.

export const travelDirections = ['arrival', 'departure'] as const;
export type TravelDirection = (typeof travelDirections)[number];

export const eventTravelLegSchema = z.object({
  direction: z.enum(travelDirections),
  /** DateOnly on the wire (yyyy-MM-dd). */
  travelDate: z.string().nullish(),
  /** TimeOnly on the wire (HH:mm[:ss]). */
  travelTime: z.string().nullish(),
  transportationTypeDefinitionId: z.guid().nullish(),
  transporterCompanyDefinitionId: z.guid().nullish(),
  /** LOC (migs 263/264): airport/station/port — also a CHILD of the transportation type. */
  locationDefinitionId: z.guid().nullish(),
  terminalDefinitionId: z.guid().nullish(),
  countryDefinitionId: z.guid().nullish(),
  flightNumber: z.string().nullish(),
  hasTravelInsurance: z.boolean(),
  hasTicket: z.boolean(),
  updatedAtUtc: isoDate.nullish()
});
export type EventTravelLeg = z.infer<typeof eventTravelLegSchema>;

/** GET /events/{id}/travel — both legs; a leg never filled in comes back all-null (not a 404). */
export const eventTravelSchema = z.object({
  arrival: eventTravelLegSchema,
  departure: eventTravelLegSchema
});
export type EventTravel = z.infer<typeof eventTravelSchema>;

/**
 * PUT /events/{id}/travel/{direction} body. Every field nullable on the wire and the server validates
 * FORMAT only — the "all fields required" rule is enforced by the form (Save stays disabled until the
 * leg is complete), deliberately not by a server completeness check.
 */
export interface EventTravelLegWriteRequest {
  travelDate?: string | null;
  travelTime?: string | null;
  transportationTypeDefinitionId?: string | null;
  transporterCompanyDefinitionId?: string | null;
  locationDefinitionId?: string | null;
  terminalDefinitionId?: string | null;
  countryDefinitionId?: string | null;
  flightNumber?: string | null;
  hasTravelInsurance: boolean;
}

/** Server-side hard caps for the travel ticket (mirrored from EventTravelRules). */
export const eventTravelTicketLimits = {
  maxBytes: 10 * 1024 * 1024,
  accept: 'application/pdf,image/jpeg,image/png,image/webp'
} as const;

// ------------------------------ VIS: attendee visa record (migs 261/262) ------------------------------
// A registered attendee's visa record for one event, filled in by the attendee alongside their travel
// legs. Unlike travel there is NO direction — one record per (event, attendee).
//
// `requiresVisa` is an ANSWER, not a UI flag: false means "asked and answered no". The detail fields stay
// on the record when the box is un-ticked (the server keeps them deliberately, see mig 262) — the form
// only HIDES them, so re-ticking restores what the attendee typed instead of losing it.
//
// visaTypeDefinitionId is an OPAQUE Definitions ref (group `visa_types`); the server validates format
// only and cannot check set membership [STD-OWN].
//
// `hasDocument` is a FLAG, not a URL: the scan (PDF or image) is an authorized blob fetched from
// GET .../visa/document via client.getBlob; the storage key never reaches the client.

export const eventVisaSchema = z.object({
  requiresVisa: z.boolean(),
  visaTypeDefinitionId: z.guid().nullish(),
  /** DateOnly on the wire (yyyy-MM-dd). */
  startsOn: z.string().nullish(),
  endsOn: z.string().nullish(),
  hasDocument: z.boolean(),
  updatedAtUtc: isoDate.nullish()
});
export type EventVisa = z.infer<typeof eventVisaSchema>;

/**
 * PUT /events/{id}/visa body. The details are nullable on the wire and the server enforces exactly ONE
 * rule — endsOn >= startsOn. "Required while the box is ticked" is a form rule (Save stays disabled),
 * deliberately not a server completeness check, so a document can be uploaded before the dates are known.
 */
export interface EventVisaWriteRequest {
  requiresVisa: boolean;
  visaTypeDefinitionId?: string | null;
  startsOn?: string | null;
  endsOn?: string | null;
}

/** Server-side hard caps for the visa document (mirrored from EventVisaRules). */
export const eventVisaDocumentLimits = {
  maxBytes: 10 * 1024 * 1024,
  accept: 'application/pdf,image/jpeg,image/png,image/webp'
} as const;

// ------------------------------ RPT: event travel manifest (mig 265) ------------------------------
// GET /events/{id}/travel-legs — every SEAT-HOLDING attendee's legs for one event, FLAT and paged.
// Cancelled registrations keep their travel row server-side, so the endpoint filters them out; a
// caller does not need to.
//
// Flat on purpose: the manifest GROUPING is the client's job (one flight = many passengers), and a
// grouped wire shape would mean a second projection for export, which wants the flat rows anyway.
//
// `hasTicket` is a FLAG; bytes come from GET .../travel/{direction}/ticket?userRef= [STD-OWN].

export const eventTravelReportRowSchema = z.object({
  userRef: z.guid(),
  /** PARTICIPANT-NAME: the seat holder's CURRENT name (Identity full_name ?? email ?? username), filled by Engagement on every response — not a stored copy (event_registrations.registrant_name is retired). Null only when Identity could not answer. */
  registrantName: z.string().nullish(),
  registrationStatus: z.string(),
  direction: z.enum(travelDirections),
  /** DateOnly on the wire (yyyy-MM-dd). */
  travelDate: z.string().nullish(),
  /** TimeOnly on the wire (HH:mm[:ss]). */
  travelTime: z.string().nullish(),
  transportationTypeDefinitionId: z.guid().nullish(),
  transporterCompanyDefinitionId: z.guid().nullish(),
  locationDefinitionId: z.guid().nullish(),
  terminalDefinitionId: z.guid().nullish(),
  countryDefinitionId: z.guid().nullish(),
  flightNumber: z.string().nullish(),
  hasTravelInsurance: z.boolean(),
  hasTicket: z.boolean(),
  updatedAtUtc: isoDate.nullish()
});
export const eventTravelReportListSchema = pagedListSchema(eventTravelReportRowSchema);
export type EventTravelReportRow = z.infer<typeof eventTravelReportRowSchema>;

/** The Definitions group the visa type picker reads (mig 261) — flat, no parent cascade. */
export const visaTypeGroupKey = 'visa_types';

// EV reminders epic — per-event reminder RULES authored by an admin (the backend fires them). Each
// rule targets a tenant EMAIL template and/or a tenant NOTIFICATION (in-app/push) template BY KEY,
// on a schedule: fire every intervalValue intervalUnit, stopping latestValue latestUnit before the
// event. At least one template key is required (enforced client-side + on the server). camelCase
// wire, Zod-parsed at the seam.
export const intervalUnits = ['hour', 'day'] as const;
export type IntervalUnit = (typeof intervalUnits)[number];

/** EventReminderDto — a single reminder rule for an event. Either template key may be null, but not both. */
export const eventReminderSchema = z.object({
  id: z.guid(),
  emailTemplateKey: z.string().nullish().transform((v) => v ?? null),
  notificationTemplateKey: z.string().nullish().transform((v) => v ?? null),
  intervalValue: z.number().int(),
  intervalUnit: z.enum(intervalUnits),
  latestValue: z.number().int(),
  latestUnit: z.enum(intervalUnits),
  isActive: z.boolean()
});
export const eventReminderListSchema = pagedListSchema(eventReminderSchema);
export type EventReminder = z.infer<typeof eventReminderSchema>;

/** POST/PUT body for an event reminder rule. At least one of the two template keys must be non-null. */
export interface EventReminderWriteRequest {
  emailTemplateKey: string | null;
  notificationTemplateKey: string | null;
  intervalValue: number;
  intervalUnit: IntervalUnit;
  latestValue: number;
  latestUnit: IntervalUnit;
  isActive: boolean;
}

/** A captured answer to an event question. value shape follows the question `type`. */
export type EventQuestionAnswerValue = string | string[] | boolean | number;
export const eventQuestionAnswerSchema = z.object({
  questionId: z.string(),
  value: z.union([z.string(), z.array(z.string()), z.boolean(), z.number()])
});
export type EventQuestionAnswer = z.infer<typeof eventQuestionAnswerSchema>;

/**
 * YOUR-EVENTS (phase 2) — GET /engagement/events/{id}/registrations/mine/requirements: what the caller's
 * OWN registration still needs after registering. Also the response of POST …/mine/consent and
 * PUT …/mine/answers. 404 = the caller has no live registration on the event.
 */
export const myRegistrationRequirementsSchema = z.object({
  registrationId: z.guid(),
  consentFormRequired: z.boolean(),
  /** latest consent for (event, me); null = never consented */
  consentedAtUtc: isoDate.nullish(),
  /** the form revision now on the event */
  consentFormUploadedAtUtc: isoDate.nullish(),
  answers: z.array(eventQuestionAnswerSchema).nullish().transform((v) => v ?? []),
  /** active REQUIRED questions with no answer */
  missingRequiredQuestionIds: z.array(z.string()).nullish().transform((v) => v ?? [])
});
export type MyRegistrationRequirements = z.infer<typeof myRegistrationRequirementsSchema>;

/** Where a registration came from — mirrors RegistrationSources (DB ck_event_registrations_source). */
export const eventRegistrationSources = ['self', 'teacher_invited'] as const;

/**
 * RegistrationDto — one event registration (roster row); userRef is opaque (P5-EN). #3: +waitlisted.
 *
 * `awaiting_payment` (mig 406) is a SEAT-HOLDING status, exactly like `registered`: the registrant took
 * a seat out of inventory and owes money for it. A registration reads `registered` only once
 * payment_state settles to `not_required` or `paid` — the same rule that unlocks event content, so the
 * status and the locked content can no longer contradict each other.
 *
 * ⚠ STRICT ENUM, so this list must gain a new server value BEFORE the server can emit it: a value
 * missing here throws inside `.parse` and blanks whatever page read it (the roster, the register
 * response). That is why the frontend ships ahead of the migration.
 */
export const eventRegistrationStatuses = ['registered', 'awaiting_payment', 'cancelled', 'attended', 'waitlisted'] as const;

/**
 * Statuses that HOLD A SEAT — the client-side mirror of the server's `RegistrationStatuses.SeatHolding`.
 *
 * ⚠ EXPORTED SO NOBODY SPELLS IT OUT AGAIN. Five places in three SPAs had written
 * `status === 'registered' || status === 'attended'` by hand, and adding `awaiting_payment` to the
 * server broke every one of them silently — most visibly the student event page, which decided the
 * holder had no seat and offered the WAITLIST message instead of the payment panel, so a student who
 * owed money had no way left to pay it. The backend hit the identical problem in nine query WHERE
 * clauses; both sides now read one list.
 *
 * ⚠ HOLDING A SEAT IS NOT THE SAME AS BEING PAID UP, and it never means "may read the content": the
 * content gate is seat-holding AND payment-settled, and `awaiting_payment` deliberately fails the
 * second half.
 */
export const seatHoldingRegistrationStatuses = ['registered', 'awaiting_payment', 'attended'] as const;

/** Does this registration status hold a seat? Nullish (not registered) is false. */
export function holdsSeat(status: string | null | undefined): boolean {
  return status != null && (seatHoldingRegistrationStatuses as readonly string[]).includes(status);
}

/**
 * REG-CANCEL — payment states in which a participant may still cancel their OWN registration; the
 * client-side mirror of the server's `RegistrationCancelPolicy.CancellableStates`.
 *
 * ⚠ AN ALLOW-LIST, NOT A DENY-LIST, and for the same reason the server's is: a payment state added
 * later must close the button (annoying, and visible) rather than open it (a student releasing a seat
 * they paid for). Spelling out `!== 'paid'` anywhere is the bug this export exists to prevent — the
 * blocking set is four values, not one.
 *
 * ⚠ THIS IS UX, NOT ENFORCEMENT. The server decides, and it decides on TWO inputs: this state AND the
 * collected amount (`event_registrations.amount_paid`, mig 409), which the client is not told. The
 * states below are also a PROJECTION of Commerce and lag it by an outbox hop, so a student who paid
 * seconds ago can still see an enabled button. Both gaps end the same way — a 409 whose body must be
 * rendered — so never treat a hidden button as the guarantee.
 */
export const cancellablePaymentStates = ['not_required', 'awaiting_plan', 'plan_active'] as const;

/**
 * May this registration still be cancelled by its owner, as far as the client can tell?
 * Nullish is treated as `not_required` — a free/ticketless registration, which is cancellable.
 */
export function canCancelRegistration(paymentState: string | null | undefined): boolean {
  return (cancellablePaymentStates as readonly string[]).includes(paymentState ?? 'not_required');
}
/**
 * EVR-PEOPLE — the person behind one Event Report row, resolved SERVER-SIDE (Engagement asks Identity
 * over gRPC) and delivered on the row by `GET /engagement/reports/registration-rows`.
 *
 * ⚠ WHY THE SERVER AND NOT THE BROWSER. The report used to join `/users?ids=` and `/schools?ids=` itself,
 * 100 guids per request — a ~3,970-character URL. Production IIS refuses query strings over 2,048
 * characters with a 404, so across events the School column went blank on live only.
 *
 * School and teacher arrive with their NAMES because nothing on the Engagement side can resolve those ids;
 * country, age bands and partner stay ids because the page already loads those catalogues whole.
 */
export const reportParticipantSchema = z.object({
  id: z.guid(),
  displayName: z.string(),
  email: z.string().nullish(),
  roles: z.array(z.string()).nullish().transform((v) => v ?? []),
  countryDefinitionId: z.guid().nullish(),
  schoolId: z.guid().nullish(),
  schoolName: z.string().nullish(),
  teacherUserId: z.guid().nullish(),
  teacherName: z.string().nullish(),
  ageCategories: z
    .array(z.object({ programTypeId: z.guid(), ageCategoryId: z.guid() }))
    .nullish()
    .transform((v) => v ?? []),
  partnerSelections: z
    .array(z.object({ programTypeId: z.guid(), companyId: z.guid() }))
    .nullish()
    .transform((v) => v ?? [])
});
export type ReportParticipant = z.infer<typeof reportParticipantSchema>;

export const eventRegistrationSchema = z.object({
  id: z.guid(),
  eventId: z.guid(),
  userRef: z.guid(),
  status: z.enum(eventRegistrationStatuses),
  registeredAtUtc: isoDate,
  cancelledAtUtc: isoDate.nullish(),
  // Ticket epic — the chosen ticket (null for plain/legacy registrations).
  ticketId: z.guid().nullish(),
  waitlistPosition: z.number().int().nullish(), // #3: dolu = kuyrukta (1-tabanlı sıra)
  // A-22b: kayıt-düzeyi koruma — roster'da yalnız engagement.safeguarding.read ile dolu; aksi halde null.
  /** PARTICIPANT-NAME: the seat holder's CURRENT name (Identity full_name ?? email ?? username), filled by Engagement on every response — not a stored copy (event_registrations.registrant_name is retired). Null only when Identity could not answer. */
  registrantName: z.string().nullish(),
  // EVT-PAY (mig 272): commerce taksit planı durumunun projeksiyonu. Etkinlik içeriği yalnız
  // not_required|paid iken açılır; roster'da "kim hâlâ ödemedi" görünümünü besler. Eski satırlarda
  // absent olabileceği için varsayılanı not_required (geri uyumlu, kapıyı GEVŞETMEZ — sunucu karar verir).
  paymentState: z
    .enum(['not_required', 'awaiting_plan', 'plan_active', 'awaiting_approval', 'partially_paid', 'paid', 'defaulted'])
    .nullish()
    .transform((v) => v ?? 'not_required'),
  // REG-2AX (mig 439) — the coarse pair; see registrationPaymentStatuses for why both are nullish.
  //
  // ⚠ NULL ON THE REGISTER RESPONSE TOO, and that is not a bug: the row is created in the same
  // SaveChanges, and EF does not read a stored generated column back after the INSERT. Draw the badge
  // from the next read (event detail / roster), never from the POST result.
  paymentStatus: coarsePaymentStatusField,
  // ⚠ `registrationStatus`, NOT `coarseRegistrationStatus` — THE ROSTER DTO SPELLS IT DIFFERENTLY FROM
  // EVERY OTHER ONE, and this schema had the other spelling for as long as mig 439 has existed.
  //
  // Zod STRIPS a key it does not declare, so the real field never arrived and the value read
  // `undefined` forever. The admin roster then fell through to its lifecycle fallback and printed
  // "Ödeme Bekleniyor" for a student whose own page said "Kayıtlısın" — one registration, two screens,
  // two answers, which is the exact bug mig 439 was created to end.
  //
  // ⚠ THE SYMPTOM POINTED AWAY FROM THE CAUSE. `paymentStatus` matches on both sides, so the Payment
  // column worked perfectly while the Status column beside it did not; that made it read as a display
  // decision rather than a field that never arrived. If one of a pair works and the other silently
  // does not, suspect the KEY before the logic.
  //
  // Why the server spells it three ways, all deliberate:
  //   RegistrationDto        -> registrationStatus        (this one; `status` is the lifecycle)
  //   EventDetailDto         -> myCoarseRegistrationStatus (`myRegistrationStatus` = the lifecycle)
  //   Teacher registration   -> coarseRegistrationStatus
  // Do not "harmonise" this field name without changing the wire it is copied from.
  registrationStatus: coarseRegistrationStatusField,
  chaperoneRef: z.guid().nullish(),
  emergencyContactName: z.string().nullish(),
  emergencyContactPhone: z.string().nullish(),
  // EV questions epic — the registrant's answers to the event's custom questions (empty when none).
  // Forward/back-compat: absent on legacy rows → []. The admin roster resolves labels via the
  // event's question list (questionId → label).
  answers: z.array(eventQuestionAnswerSchema).nullish().transform((v) => v ?? []),
  // CF (mig 253): this user's LATEST consent for the event; null = none on file (the event has no
  // consent form, or they registered before one was uploaded — consent is only captured at register time).
  consentedAtUtc: isoDate.nullish(),
  // VIS (mig 262): the attendee's visa record, roster-only. THREE-STATE on purpose:
  //   null  = never asked (no row) — the state an organiser still has to chase,
  //   false = "I do not need a visa" (asked and answered),
  //   true  = needs one; the detail fields below are then meaningful.
  // Collapsing null and false into one boolean would hide exactly the difference the roster exists for.
  visaRequired: z.boolean().nullish(),
  visaTypeDefinitionId: z.guid().nullish(),
  /** DateOnly on the wire (yyyy-MM-dd). */
  visaStartsOn: z.string().nullish(),
  visaEndsOn: z.string().nullish(),
  /** A FLAG, not a URL — staff fetch the bytes from GET .../visa/document?userRef=. */
  visaHasDocument: z.boolean().nullish().transform((v) => v ?? false),
  // TCH-TKT (mig 282): how this person got in — 'self' (registered and paid for themselves) or
  // 'teacher_invited' (used a ticket their teacher bought and assigned). NOT the ticket product type
  // (that is ticketId). Written at register time and never recomputed; legacy rows → 'self'.
  source: z.enum(eventRegistrationSources).nullish().transform((v) => v ?? 'self'),
  // EVR-ALL: the event this registration belongs to, BY NAME — filled only by the cross-event report
  // (GET /engagement/reports/registrations), null on the per-event roster whose route already names it.
  // The Event column sorts and filters on this, so it rides on the row rather than in a side list.
  eventTitle: z.string().nullish(),
  // EVR-REGORD: the ticket batch whose seat this registration claimed (teacher_invited /
  // staff_allocation) — filled only by the cross-event report. A seat registration has no order of its
  // own; the money sits on the BATCH order, so this is the ref the report joins Commerce on. Null = not
  // a seat registration: its order, if any, is keyed on the registration id.
  ticketBatchId: z.guid().nullish(),
  // EVR-PEOPLE: the person behind the row — filled only by GET /engagement/reports/registration-rows, and
  // only for a caller who also holds identity.users.view. See `reportParticipantSchema`.
  participant: reportParticipantSchema.nullish(),
  /**
   * NAMES-SWEEP: the registrant's CURRENT display name (profile name ?? email ?? username) and email,
   * resolved by Engagement from Identity — filled by the per-event roster (GET /events/{id}/registrations)
   * only. Null = unresolved. ⚠ Do not join these from /users?ids= on the client.
   */
  userDisplayName: z.string().nullish(),
  userEmail: z.string().nullish(),
  /**
   * SEAT-INFO: the teacher roster's question/consent checks, computed by the SAME server rule
   * (TeacherCompletion) — filled by the per-event roster only. ⚠ `.nullish()` keeps the distinction the
   * seat chip needs: `consentComplete: null` = the event has no consent form (not asked), while ABSENT
   * (undefined) = an older server that does not send it (unknown — the chip will not claim "Completed").
   */
  questionsComplete: z.boolean().nullish(),
  missingRequiredAnswerCount: z.number().int().nullish(),
  consentComplete: z.boolean().nullish()
});
export const eventRegistrationListSchema = pagedListSchema(eventRegistrationSchema);

// ------------------------------ TEACHER EVENT PORTAL (Engagement :5800) --------------------------
// TCH-TKT (migs 281/282/284) — the teacher's own surfaces. All of these are scoped SERVER-SIDE to
// "my students" / "my quota"; the FE never sends a student-id list (that would be an IDOR).
// Endpoints under /api/v1/engagement/events/{eventId}/teacher/...

/**
 * One answer as the TEACHER sees it. `masked: true` means the value is a profile-sourced field
 * (passport number and the like) and the server did NOT send it — `value` is then null. Masking
 * happens server-side on purpose: masking in the browser would already have shipped the value.
 */
export const teacherAnswerSchema = z.object({
  questionId: z.string(),
  label: z.string().nullish(),
  value: z.union([z.string(), z.array(z.string()), z.boolean(), z.number()]).nullish(),
  masked: z.boolean().optional().default(false)
});

/**
 * TeacherRegistrationRowDto — Tab 2 row: one of MY students on this event.
 *
 * TCH-REG-PARITY: questions, consent and passport completeness come from the SERVER (passport because
 * this persona cannot see the fields). Travel and visa are judged in the browser by `completeness.ts`,
 * the rules the admin roster uses — a second server-side definition is what made the two screens
 * disagree about the same student.
 */
export const teacherRegistrationRowSchema = z.object({
  registrationId: z.guid(),
  userRef: z.guid(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  displayName: z.string(),
  status: z.enum(eventRegistrationStatuses),
  source: z.enum(eventRegistrationSources),
  paymentState: z
    .enum(['not_required', 'awaiting_plan', 'plan_active', 'awaiting_approval', 'partially_paid', 'paid', 'defaulted'])
    .nullish()
    .transform((v) => v ?? 'not_required'),
  // REG-2AX (mig 439) — see registrationPaymentStatuses.
  paymentStatus: coarsePaymentStatusField,
  coarseRegistrationStatus: coarseRegistrationStatusField,
  registeredAtUtc: isoDate,
  /**
   * ⚠ PRESENCE ONLY — both legs EXIST, not that they are filled in. Kept for wire compatibility; the
   * roster no longer reads it (TCH-REG-PARITY). Completeness is `isTravelLegComplete` over the
   * /teacher/travel-legs facts, the same rule the admin roster applies.
   */
  travelComplete: z.boolean(),
  /** 0/1/2 — lets the UI say "no return leg" instead of a bare cross. */
  travelLegCount: z.number().int(),
  /**
   * TCH-REG-PARITY: number, expiry, origin country and scan all present — `isPassportComplete`'s four
   * facts, computed by Identity because this persona may not see them. Only the flag reaches the wire.
   */
  passportComplete: z.boolean(),
  questionsComplete: z.boolean(),
  missingRequiredAnswerCount: z.number().int(),
  /** null = the event has no consent form, so the column is not applicable (render '—', not a cross). */
  consentComplete: z.boolean().nullish(),
  answers: z.array(teacherAnswerSchema).nullish().transform((v) => v ?? []),
  // ---- TCH-REG-PARITY: what the admin roster shows about the same registration. All nullish, so a
  // server predating them still parses (a failed parse blanks the whole tab, not one cell).
  middleName: z.string().nullish(),
  email: z.string().nullish(),
  /** OPAQUE definition id (group 'country') — the label comes from Definitions. */
  countryDefinitionId: z.guid().nullish(),
  /** Resolved by Identity — this SPA has no school catalogue it may cheaply read. */
  schoolName: z.string().nullish(),
  ticketId: z.guid().nullish(),
  ticketName: z.string().nullish(),
  cancelledAtUtc: isoDate.nullish(),
  /** WHEN consent was given; `consentComplete` only says whether. */
  consentedAtUtc: isoDate.nullish(),
  /** THREE-STATE: null = never answered, which is not the same as "no visa needed". */
  visaRequired: z.boolean().nullish(),
  visaTypeDefinitionId: z.guid().nullish(),
  /** DateOnly on the wire (yyyy-MM-dd). */
  visaStartsOn: z.string().nullish(),
  visaEndsOn: z.string().nullish(),
  visaHasDocument: z.boolean().nullish().transform((v) => v ?? false)
});
export const teacherRegistrationListSchema = pagedListSchema(teacherRegistrationRowSchema);

/** TeacherTravelRowDto — Tab 4 row. hasTicket is a FLAG; bytes come from the per-user endpoint. */
export const teacherTravelRowSchema = z.object({
  userRef: z.guid(),
  displayName: z.string(),
  direction: z.enum(['arrival', 'departure']),
  /** DateOnly / TimeOnly on the wire. */
  travelDate: z.string().nullish(),
  travelTime: z.string().nullish(),
  transportationTypeDefinitionId: z.guid().nullish(),
  transporterCompanyDefinitionId: z.guid().nullish(),
  locationDefinitionId: z.guid().nullish(),
  terminalDefinitionId: z.guid().nullish(),
  countryDefinitionId: z.guid().nullish(),
  flightNumber: z.string().nullish(),
  hasTravelInsurance: z.boolean().optional().default(false),
  hasTicket: z.boolean().optional().default(false)
});

/** TeacherVisaRowDto — Tab 5 row. requiresVisa is THREE-STATE: null = never asked. */
export const teacherVisaRowSchema = z.object({
  userRef: z.guid(),
  displayName: z.string(),
  requiresVisa: z.boolean().nullish(),
  visaTypeDefinitionId: z.guid().nullish(),
  startsOn: z.string().nullish(),
  endsOn: z.string().nullish(),
  hasDocument: z.boolean().optional().default(false)
});

/** Ticket instance lifecycle — mirrors TicketAllocationStatuses (DB CHECK). */
export const ticketAllocationStatuses = [
  'available',
  'assigned',
  'waiting_for_information',
  'completed',
  'released'
] as const;

/**
 * Batch payment projection — mirrors TicketBatchPaymentStates. No 'not_required': batches are priced.
 *
 * ⚠ OFR (mig 342) ADDED TWO MEMBERS, AND THIS UNION IS LOAD-BEARING. A batch state the enum does not
 * know fails the parse of `ticketBatchSchema`, which fails `teacherTicketsSchema`, which blanks the
 * WHOLE "Biletlerim" tab — not one row, not one field. So this list must be widened in the same change
 * that lets the server emit the new value, never after.
 *
 * `awaiting_offer` / `offer_rejected` both sit BEFORE the payment path: no Commerce order exists for
 * them (none was ever opened), which is why `canAssign` is false and no payment control may show.
 */
export const ticketBatchPaymentStates = [
  'awaiting_offer',
  'offer_rejected',
  'awaiting_plan',
  // PAY-DOC (mig 360): bir taksidin dekontu inceleniyor. 'plan_active'in ALT durumu — para gelmedi,
  // borc duruyor, ATAMA ACIK kalir. Bu deger burada YOKSA sunucu onu gonderdigi anda z.enum atar ve
  // parse alim satirini degil, onu iceren TUM listeyi dusurur: ekran bir hucre degil, bosalir.
  'awaiting_approval',
  'plan_active',
  // PAY-DOC (mig 361): en az bir taksit odendi, alim bitmedi (0 < odenen < toplam). 'plan_active'
  // yalniz "plan var mi" derdi; 3'te 1'i odenmis bir alim ile hic odenmemis olan ayni gorunuyordu.
  'partially_paid',
  'paid',
  'defaulted',
  // BATCH-CANCEL (mig 464): the BUYER cancelled an unpaid purchase. Seats released, order closed,
  // nothing owed. TERMINAL, and deliberately NOT 'defaulted' — that value means a payment FAILED, it
  // is the one chip rendered in the inverted collections style, and it is what a dunning report
  // selects on. Telling a partner who owed nothing and did the tidy thing that they defaulted would
  // put them on a list of people to chase.
  'cancelled'
] as const;

/** OFR: the offer's own lifecycle — mirrors OfferStatuses. Null on a plain (non-negotiated) purchase. */
export const offerStatuses = ['pending', 'approved', 'rejected', 'withdrawn', 'expired'] as const;

/**
 * BATCH-QTY (mig 354) — the ADMIN view of one ticket batch, from
 * `GET /api/v1/engagement/ticket-batches/{id}`. Distinct from `ticketBatchSchema`, which answers
 * "what is in MY quota" for the buyer; this one answers "what may I correct, and how far down".
 *
 * ⚠ `minQuantity` AND `canEditQuantity` ARE SERVER-COMPUTED. Do NOT re-derive either in the client.
 * The floor is the higher of two independent bounds — seats already assigned or funded (never
 * revoked, FUND D3) and the amount already collected — and reproducing that here would need the
 * funding percentage and the paid/assigned split. Any drift shows up as a form that happily submits
 * a request the server answers with 409.
 */
export const ticketBatchAdminSchema = z.object({
  id: z.guid(),
  eventId: z.guid(),
  ticketId: z.guid(),
  ticketName: z.string(),
  buyerUserRef: z.guid(),
  quantity: z.number().int(),
  unitAmount: z.number(),
  totalAmount: z.number(),
  currency: z.string(),
  /** Kept a plain string, NOT z.enum: a state the client does not know must not blank the card. */
  paymentState: z.string(),
  amountPaid: z.number(),
  paymentDueBy: isoDate.nullish(),
  assignedSeats: z.number().int(),
  fundedSeats: z.number().int(),
  minQuantity: z.number().int(),
  canEditQuantity: z.boolean(),
  /**
   * OFR-CAP (mig 370): this ORDER's seat-opening threshold and WHERE it comes from.
   *
   * ⚠ `source` is not decoration. Three levels resolve into one number (order → event → platform), and
   * a card that shows only the number produces the support question "I set the event to 30, why does
   * this order say 20?". `.nullish()` so a server that predates the field renders the card without the
   * threshold block rather than blanking the whole page.
   */
  threshold: z
    .object({
      batchId: z.guid(),
      effectivePercent: z.number().int(),
      source: z.enum(['order', 'event', 'platform']),
      orderOverride: z.number().int().nullish(),
      eventPercent: z.number().int().nullish(),
      platformDefault: z.number().int(),
      /** The percentage turned into MONEY — the figure the partner's row shows. */
      seatOpenThresholdAmount: z.number(),
      currency: z.string()
    })
    .nullish(),
  /**
   * OFR-CAP (mig 369): may the CALLER change this order's threshold — i.e. do they hold a role that the
   * batch's offer chain granted `can_update_threshold`?
   *
   * ⚠ Surface, not authorization (same standing as `canEditQuantity`): the real gate is the command and
   * it 403s. False on an order with no offer at all, which is v1's deliberate limit — the card renders
   * read-only and says so.
   */
  canUpdateThreshold: z.boolean().optional().default(false),
  /**
   * ORD-PRICE: the seats that can never be taken away (`max(assigned, funded)`). Sent separately from
   * `minQuantity` because the dialog now edits the PRICE too, and `minQuantity` folds in a money floor
   * computed at the CURRENT price. `.optional()` so a server predating ORD-PRICE still parses.
   */
  seatFloor: z.number().int().optional(),
  /** ORD-PRICE: the lowest unit price at the CURRENT seat count. */
  minUnitAmount: z.number().optional(),
  /**
   * ORD-PRICE: why the price is locked — `offer_pending` (an offer still being negotiated owns it) — or
   * null. A string, not `z.enum`: an unknown token must lock the field, not blank the dialog.
   */
  priceLock: z.string().nullish()
});

/**
 * BATCH-QTY: the client sends the count it wants the batch to BE, never a delta. ORD-PRICE: and the unit
 * price, in the SAME request — seats and price are judged together (`unit × seats` must stay above what
 * was paid) and saved in one transaction, so Commerce re-prices the order once. `unitAmount` is optional
 * on the wire for pre-ORD-PRICE clients; this client always sends it.
 */
export interface UpdateTicketBatchQuantityRequest {
  quantity: number;
  unitAmount?: number;
}

/**
 * ORD-PRICE: the batch dialog's two floors at the values being TYPED.
 *
 * ⚠ THIS IS A PORT, NOT A NEW RULE, and it exists only because the dialog edits two fields that bound
 * each other: the server's `minQuantity` is the seat floor at the CURRENT price and `minUnitAmount` the
 * price floor at the CURRENT seats, so neither is right once both move. The rule is one line on the server
 * (`TicketBatchQuantityRules`: `unit × seats > paid`, seats ≥ `seatFloor`); the tests pin this copy to the
 * same worked numbers (16.400 over 8 → 2.050,01; 11.900 at 3.500 → 4 seats). The server still judges the
 * save and its 409 is shown verbatim.
 */
export function ticketBatchFloors(
  batch: { seatFloor?: number; minQuantity: number; amountPaid: number },
  quantity: number,
  unitAmount: number
): { minQuantity: number; minUnitAmount: number } {
  // In CENTS: the server divides decimals, and a float division like 0.3 / 0.1 = 2.9999… would floor one low.
  const paidCents = Math.round(batch.amountPaid * 100);
  const unitCents = Math.round(unitAmount * 100);
  // A server predating ORD-PRICE sends no seatFloor; its minQuantity is the best available bound.
  const seatFloor = batch.seatFloor ?? batch.minQuantity;
  const moneyFloor = paidCents > 0 && unitCents > 0 ? Math.floor(paidCents / unitCents) + 1 : 1;
  const minUnitAmount = paidCents > 0 && quantity >= 1 ? (Math.floor(paidCents / quantity) + 1) / 100 : 0.01;

  return { minQuantity: Math.max(1, seatFloor, moneyFloor), minUnitAmount };
}

/**
 * OFR-CAP (mig 370): this order's seat-opening threshold. `percent: null` CLEARS the override — it means
 * "this order has no opinion, follow the event (then the platform)", which is NOT the same as sending 20.
 */
export interface UpdateBatchFundingThresholdRequest {
  percent: number | null;
}

/**
 * FUND-AMT: how much of a purchase's arrived money goes to each student.
 *
 * ⚠ ONE REQUEST FOR THE WHOLE SPLIT. Distributing 10.000 across six students is one decision; sent seat
 * by seat it hits the pot ceiling half-way and leaves a partly-applied split with no undo. The server
 * validates the set and writes all of it or none. A single inline edit sends one item through the same
 * endpoint.
 *
 * Seats not listed are left ALONE — omission is not "set to zero".
 */
export interface SetTicketPaidAmountsRequest {
  items: { allocationId: string; amount: number }[];
}

export const ticketAllocationSchema = z.object({
  id: z.guid(),
  batchId: z.guid(),
  ticketId: z.guid(),
  status: z.enum(ticketAllocationStatuses),
  assignedUserRef: z.guid().nullish(),
  /** Resolved by the server from Identity; null once the student is no longer this teacher's. */
  assignedStudentName: z.string().nullish(),
  assignedAtUtc: isoDate.nullish(),
  claimedAtUtc: isoDate.nullish(),
  registrationId: z.guid().nullish(),
  /**
   * FUND (mig 351): when this seat was marked PAID. Non-null = funded, and funded is the ONLY state in
   * which the seat reaches the student — an assigned-but-unfunded seat is invisible to them and the
   * server refuses the claim.
   *
   * `.nullish()` rather than required: a server that predates the field reads as "not funded", which
   * hides the seat rather than promising one that cannot be claimed.
   */
  paidSelectedAtUtc: isoDate.nullish(),
  /**
   * FUND-AMT (mig 367) — how much of the purchase's arrived money is allocated TO THIS STUDENT.
   *
   * Below the seat's threshold (`batch.seatOpenThresholdAmount`) it is still stored: a partner who
   * collected 300 of a 400 share has a real number to record, and the seat simply stays closed.
   */
  paidAmount: z.number().optional().default(0),
  /**
   * FUND-AMT — what is still to be collected for this seat (ticket price − allocated, floored at 0).
   *
   * ⚠ SERVER-COMPUTED, and deliberately not `batch.unitAmount - paidAmount` here: the seat renders in a
   * child table and a subtraction in this file is one refactor away from using the wrong order's unit
   * price.
   */
  remainingAmount: z.number().optional().default(0),
  /**
   * FUND-AMT — can the student see the event's registered-only content yet? The same fact as
   * `paidSelectedAtUtc != null`, named for what it means.
   *
   * ⚠ DO NOT re-derive it from `paidAmount >= seatOpenThresholdAmount`. Once open, a seat stays open
   * (2026-08-17): lowering the amount re-apportions money and leaves the content alone, so an open seat
   * can legitimately sit below its threshold — and the threshold comparison would call it closed.
   */
  contentOpen: z.boolean().optional().default(false)
});

export const ticketBatchSchema = z.object({
  id: z.guid(),
  eventId: z.guid(),
  ticketId: z.guid(),
  ticketName: z.string(),
  quantity: z.number().int(),
  unitAmount: z.number(),
  totalAmount: z.number(),
  currency: z.string(),
  paymentState: z.enum(ticketBatchPaymentStates),
  /** OPAQUE commerce plan id — the installment panel opens with this; null before a plan exists. */
  paymentPlanRef: z.guid().nullish(),
  paymentDueByUtc: isoDate.nullish(),
  /**
   * Server-derived: may seats be handed out at all? Never re-derive in the FE.
   *
   * ⚠ FUND CHANGED WHAT THIS MEANS. It used to be "the batch is fully paid"; it is now "a live
   * purchase exists" — true while payment is still running, false only when the offer is undecided or
   * rejected, or the plan defaulted. The money question moved to `maxFundedSeats`.
   */
  canAssign: z.boolean(),
  /**
   * TKT-ASG (2026-08-18): WHY `canAssign` is what it is — a machine token, never a sentence.
   *
   * `payment_state` (no live purchase: offer undecided/rejected, or the plan defaulted) ·
   * `funding` (a direct purchase, and the money has not opened another seat yet) ·
   * `seats_distributed` (every seat is already handed out — nothing to do with money) ·
   * `offer_approved` (an approved price request — assignment is open with no money at all) ·
   * `free` (a free ticket) · `funded` (the allowance has room).
   *
   * ⚠ `seats_distributed` WAS REPORTED AS `funding` UNTIL 2026-09-22, and the difference is not
   * cosmetic: the allowance is bounded by the seat count as well as by money, so a fully-distributed
   * batch clamped `payToOpenNextSeat` to 0 and the banner asked the partner to pay "0,00 ₺ more" for a
   * seat that does not exist. Handle it before falling through to the funding copy.
   *
   * ⚠ NULLISH on purpose: a server that predates this field must not blank the whole tickets tab. The UI
   * falls back to its previous, less specific copy when it is missing.
   */
  assignReason: z.string().nullish(),
  /**
   * TKT-ASG: how much more money opens ONE more seat — meaningful only when `assignReason` is `funding`,
   * otherwise 0.
   *
   * ⚠ SERVER-COMPUTED. It is `(seatsHeld + 1) × threshold − amountPaid`, and every term of that is a
   * server concept (the threshold resolves three levels deep). Re-deriving it here is the mistake
   * `seatOpenThresholdAmount` already exists to prevent.
   */
  payToOpenNextSeat: z.number().optional().default(0),
  createdAtUtc: isoDate,
  allocations: z.array(ticketAllocationSchema).nullish().transform((v) => v ?? []),
  /**
   * TCH-SELF (B4): is this batch's ticket open to the CALLER's own role — i.e. may they keep one of its
   * instances for themselves (`assign-to-me`)?
   *
   * Batch-level rather than per-allocation because a batch carries exactly ONE ticket, so the role rule is
   * the same for every instance in it.
   *
   * ⚠ NOT an authorization result — the real gate is server-side and 403s. This only stops the UI offering
   * a button that cannot work. `.default(false)` so a server that predates the field hides the action
   * rather than showing one that fails.
   */
  canAssignToSelf: z.boolean().optional().default(false),
  /**
   * OFR-REQ: this purchase started as a PRICE OFFER, and this is that offer's status. Null on a plain
   * purchase — the row must not read as a negotiation when it was not one.
   *
   * ⚠ It explains `paymentState`, it does not replace it. `awaiting_offer` says "payment is not open";
   * this says WHY, and distinguishes rejected from withdrawn from expired — three outcomes the batch
   * state deliberately collapses into one (`offer_rejected`), because none of them opens a payment.
   */
  offerStatus: z.enum(offerStatuses).nullish(),
  /**
   * OFR-APR — WHY this purchase is waiting for its first payment: `offer_approved` (an approved price
   * request opened the payment step) or `direct` (bought at list price). Null when it is not waiting.
   *
   * ⚠ SERVER-DERIVED, AND NOT THE SAME AS `offerStatus === 'approved'`. The answer is the PAIR of the
   * offer status and `paymentState`: an approved offer that is fully paid is waiting for nothing, and
   * `awaiting_plan` on its own does not say which route got here. Deriving it in the client would put
   * a third copy of that rule next to the admin report's and the partner tab's.
   *
   * ⚠ `.nullish()` NOT `.default(...)`: absence is meaningful (not waiting), so there is no safe
   * default to invent. A server that predates the field simply says nothing and the chip falls back
   * to the plain "awaiting payment" wording — the behaviour before this feature existed.
   */
  awaitingPaymentReason: z.enum(['direct', 'offer_approved']).nullish(),
  // ⚠ NO offerCurrentRoleCode HERE, DELIBERATELY. The approval chain's steps are the tenant's
  // INTERNAL sign-off structure; a partner is an outside party and needs to know that a decision is
  // pending, not whose signature is next. The server does not send it on this surface at all — a
  // field hidden only in the UI still travels in the JSON and is readable in devtools. The role
  // stays on the ADMIN queue (eventTicketOfferQueueItemSchema.currentRoleCode).
  /** OFR-APR: the rejecting step's note — the ONLY explanation the partner gets. Required server-side. */
  offerRejectionNote: z.string().nullish(),
  /** OFR-REQ: the list price at offer time. `unitAmount` is the OFFERED price; without this the partner
   *  cannot see how big a discount they asked for. */
  offerListUnitAmount: z.number().nullish(),
  /**
   * OFR-ADM (mig 617): this purchase's offer was opened by the TENANT on the partner's behalf. A flag, not
   * a name — who inside the tenant typed it is the tenant's internal matter, like the approval chain.
   */
  offerCreatedOnYourBehalf: z.boolean().optional().default(false),
  /**
   * PCOM P6 (mig 657) — paid with the company's prepaid ticket credits: no order, every seat open at once, and
   * unitAmount is 0 (the money gates read it as fully funded). listUnitAmount is the ticket price then, for
   * display; creditsUsed falls when free seats are given back.
   */
  fundingSource: z.enum(['money', 'credits']).optional().default('money'),
  creditsUsed: z.number().int().optional().default(0),
  listUnitAmount: z.number().nullish(),
  /**
   * FUND (mig 351) — the funding counters. Every one of these is SERVER-DERIVED and none of them may be
   * recomputed here: the same figures gate the write, and a second copy of the rule in the browser would
   * disagree with it the first time either changes.
   *
   * `.default()` on all of them so a server that predates FUND reads as "nothing funded, nothing
   * fundable" — the safe direction: it hides the control rather than offering one the server refuses.
   */
  /** Cumulative money received against this purchase, projected from Commerce. */
  amountPaid: z.number().optional().default(0),
  /** Seats currently marked paid. */
  fundedSeats: z.number().int().optional().default(0),
  /**
   * The most seats the money received can cover.
   *
   * ⚠ CAN BE LESS THAN `fundedSeats`. A refund or a default lowers `amountPaid` but never revokes a
   * funded seat (the student may already have travelled), so the purchase reads as OVER-COMMITTED —
   * see `fundingOverCommitted`.
   */
  maxFundedSeats: z.number().int().optional().default(0),
  /** The share of the unit price each funded seat consumes (per event; platform default 20). */
  fundingMinPercent: z.number().int().optional().default(20),
  /**
   * FUND (D2): the payment deadline has passed but the seats were NOT released, because something has
   * been paid. A flag, not a state — `paymentState` is unchanged and the payment path stays open.
   */
  fundingOverdue: z.boolean().optional().default(false),
  /**
   * FUND (D3): more money is allocated to seats than has arrived (a refund or a default).
   *
   * ⚠ FUND-AMT reads this from MONEY (`allocatedAmount > amountPaid`), not from a seat count. No seat is
   * ever closed by it — the student may already have travelled.
   */
  fundingOverCommitted: z.boolean().optional().default(false),
  /**
   * FUND-AMT (mig 367) — the money view of this purchase, which is what the seat columns and the split
   * panel are built on. Server-derived, same discipline as the FUND counters above.
   */
  /** Total allocated across this purchase's seats (`SUM(paidAmount)`). */
  allocatedAmount: z.number().optional().default(0),
  /** Arrived but not yet written to any student — the pot the split panel may distribute. Never negative. */
  unallocatedAmount: z.number().optional().default(0),
  /**
   * What one seat must hold to OPEN (the percentage turned into money).
   *
   * ⚠ Never recompute this from `fundingMinPercent`: the percentage can be overridden per event and the
   * rounding rule (up to the cent) is the server's. A second copy in the browser would disagree with the
   * 409 the command returns.
   */
  seatOpenThresholdAmount: z.number().optional().default(0),
  /**
   * FUND-EDIT (2026-09-22): may amounts be written to this purchase's seats?
   *
   * ⚠ NOT A SYNONYM FOR `canAssign`, and the UI treated it as one. `canAssign` answers "may ONE MORE
   * seat be handed out", which is necessarily false once every seat is distributed — so hanging the
   * money controls off it took the "edit amount" and "split payment" actions away at exactly the point
   * a partner needs them: all seats given out, money still arriving in instalments.
   *
   * ⚠ `.default(false)` mirrors `canAssignToSelf`: against a server that predates the field the money
   * controls stay hidden rather than appearing and 409-ing. That is the safe direction here, since the
   * old builds are the ones that also send the old `assignReason`.
   */
  canApportionPayments: z.boolean().optional().default(false),
  /**
   * DISC-BATCH (mig 620): the discount code on this purchase's order, or null. The code is applied on the
   * order (`POST /commerce/orders/{id}/discount`); Engagement learns it from Commerce a moment later.
   */
  discountCode: z.string().nullish(),
  /**
   * DISC-BATCH: what one seat costs AFTER the order's discount — the figure every funding amount on this
   * batch (`seatOpenThresholdAmount`, a seat's `remainingAmount`) is derived from. `unitAmount` stays the
   * LIST price. ⚠ Read it through `batchSeatPrice`, which falls back to `unitAmount` against a server that
   * predates the field.
   */
  seatUnitAmount: z.number().nullish(),
  /** DISC-BATCH: the tickets' total after the discount. Read it through `batchNetTotal`. */
  netTotalAmount: z.number().nullish()
});

/**
 * DISC-BATCH: one seat's price after the order's discount (the list `unitAmount` when there is none, or
 * when the server predates the field). The ONE place the fallback is written, so a screen cannot show the
 * list price beside a threshold computed from the discounted one.
 */
export function batchSeatPrice(batch: Pick<TicketBatch, 'unitAmount' | 'seatUnitAmount'>): number {
  return batch.seatUnitAmount ?? batch.unitAmount;
}

/** DISC-BATCH: the tickets' total after the order's discount (`totalAmount` without one). */
export function batchNetTotal(batch: Pick<TicketBatch, 'totalAmount' | 'netTotalAmount'>): number {
  return batch.netTotalAmount ?? batch.totalAmount;
}

export const teacherTicketsSchema = z.object({
  batches: z.array(ticketBatchSchema).nullish().transform((v) => v ?? []),
  totalTickets: z.number().int(),
  assignedTickets: z.number().int(),
  availableTickets: z.number().int()
});

/** A student who may still receive a ticket (already-registered / already-holding are excluded server-side). */
export const assignableStudentSchema = z.object({
  userId: z.guid(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  displayName: z.string(),
  /**
   * TCH-BULK: OPAQUE definitions.definitions.id (group 'country') — the filter dimension for bulk seat
   * assignment, where a partner's roster runs to hundreds of names across countries.
   *
   * ⚠ NULL means UNKNOWN, not "no country": the student has not filled their profile. Render it as such —
   * bucketing them into a country because the field is empty is how a filter starts lying.
   */
  countryDefinitionId: z.guid().nullish(),
  /**
   * TCH-BULK: the sign-in identifiers, so a picker can tell two people with the same name apart.
   *
   * ⚠ NOT DECORATION. Display names repeat in real data (the demo tenant carries three "Ada Lovelace"),
   * so a seat picker showing names alone cannot answer "which of these is the student in front of me" —
   * and ticking the wrong row gives a paid seat to the wrong person.
   */
  email: z.string().nullish(),
  username: z.string().nullish(),
  /**
   * TKT-SCH: the student's school — the seat picker's second filter dimension, beside country.
   *
   * ⚠ ID *AND* NAME, which the country field above deliberately is not. A country label is a translated,
   * tenant-editable Definitions row and the SPA already loads that catalogue; a school name is a proper
   * noun, and the only school list this persona may read is UNCAPPED (11,779 rows on the demo tenant), so
   * resolving one client-side would mean pulling a register into a dialog to label a handful of rows.
   *
   * ⚠ FILTER ON THE ID, DISPLAY THE NAME — school names repeat across a register.
   *
   * ⚠ NULL = no school recorded, not "no school": an unfilled profile, exactly like the country.
   */
  schoolId: z.guid().nullish(),
  schoolName: z.string().nullish(),
  /**
   * TCH-CREATED: when the student's ACCOUNT was created (UTC ISO) — the seat picker's date column and
   * range filter, both client-side because the endpoint returns the whole assignable list.
   *
   * ⚠ NULLISH for a rolling deploy: an Identity older than the field sends nothing. Render "unknown", and
   * exclude the row while a date range is active rather than guess a date for it.
   */
  createdAtUtc: isoDate.nullish(),
  /**
   * TCH-CODES: the student's user code (e.g. TE2700251) — what the picker's pasted code list matches,
   * exactly and case-insensitively, in the browser.
   *
   * ⚠ NULLISH: no code yet (the tenant has no prefix) or an older Identity. Such a row never matches a code.
   */
  userCode: z.string().nullish()
});

/**
 * TCH-INV F3 (mig 292) — a teacher's invitation to one student for one event.
 *
 * TWO KINDS, and they differ in exactly one thing: `ticket` hands over an ALREADY PAID allocation
 * (allocationRef holds the seat), `open` reserves NOTHING and the student joins the normal paid flow —
 * so an open invitation promises nothing about availability and its copy must not imply a seat.
 */
export const eventInvitationKinds = ['ticket', 'open'] as const;
export const eventInvitationStatuses = ['pending', 'accepted', 'withdrawn'] as const;

export const eventInvitationSchema = z.object({
  id: z.guid(),
  studentUserRef: z.guid(),
  studentName: z.string(),
  kind: z.enum(eventInvitationKinds),
  status: z.enum(eventInvitationStatuses),
  /** Set for kind='ticket' only — the allocation that holds the seat. */
  allocationRef: z.guid().nullish(),
  message: z.string().nullish(),
  notifiedAtUtc: z.string().nullish(),
  respondedAtUtc: z.string().nullish(),
  createdAtUtc: z.string()
});
export const eventInvitationListSchema = z.array(eventInvitationSchema);

/**
 * One row of a BULK invite. `invited: false` is NOT an error — it is an expected outcome in a bulk
 * operation (already registered, already invited, no ticket left), and the UI must show it per student
 * instead of collapsing the request into one success/failure.
 */
export const eventInvitationOutcomeSchema = z.object({
  studentUserRef: z.guid(),
  studentName: z.string(),
  invited: z.boolean(),
  /** already_registered | already_invited | already_holds_ticket | no_ticket_available */
  reason: z.string().nullish(),
  invitationId: z.guid().nullish()
});

export const inviteStudentsResultSchema = z.object({
  outcomes: z.array(eventInvitationOutcomeSchema).nullish().transform((v) => v ?? []),
  invitedCount: z.number().int(),
  skippedCount: z.number().int()
});

/**
 * POST /events/{id}/teacher/invitations — an invite RESERVES NO SEAT.
 *
 * ⚠ INV-NOSEAT: `kind` and `ticketId` were REMOVED from this body, server side too. Inviting a student and
 * giving them a ticket are separate actions: this creates the invitation, and a ticket is assigned through
 * `POST …/teacher/allocations`. `eventInvitationKinds` still exists because READS need it — existing rows say
 * 'ticket' and the roster renders them — but nothing can be WRITTEN as a ticket invitation any more.
 */
export interface InviteStudentsRequest {
  studentUserRefs: string[];
  message?: string | null;
}

/**
 * TCH-INV F2 — POST /api/v1/users/my-students (IDENTITY, not Engagement).
 *
 * ⚠ THERE IS STILL NO roleCodes FIELD. `roleId` below is not the same thing: it may only name a role of an
 * ASSIGNABLE type — participant (student, parent …) or supervisor (teacher) — which the server re-checks. A
 * teacher still cannot mint an administrator. The old free-form role list stays admin-only.
 */
export interface InviteMyStudentRequest {
  /**
   * STU-HANDLE (mig 314): OPTIONAL. Omit it when the teacher has no address for this student — the server
   * then mints a username and a one-time password instead of e-mailing an activation link.
   */
  email?: string;
  // USR-PARTS (mig 311): the name of record. The server still accepts a displayName and splits it, but a
  // teacher typing into two labelled boxes has already answered the question the split would guess at.
  // ⚠ Both are REQUIRED when there is no e-mail: the username is built from the initials, and a student with
  // neither a name nor an address would render as nothing at all.
  firstName: string;
  lastName: string;
  /** USR-PARTS: optional, and part of the same generated full_name as the other two. */
  middleName?: string;
  /**
   * TCH-USR: both optional and both validated server-side against the SAME lists the pickers are fed from
   * (GET /users/my-students/registration-options). Omit `roleId` and the account becomes a student, which is
   * what every invite did before these fields existed.
   */
  schoolId?: string;
  roleId?: string;
}

/**
 * TCH-USR: one pickable school or role. `isDefault` marks the role an omitted `roleId` means.
 *
 * TCH-XLSX-SCOPE: `countryDefinitionId` is the school's own country — an opaque definitions id, present so a
 * Country picker can cascade into the School one without a second request. Always null on a role.
 *
 * ⚠ NULL IS NOT "ANY COUNTRY". A school with no country recorded is in NO country's filtered list, matching
 * the server, so a client that filters MUST count them and say so — on the demo tenant that is every school,
 * and an unexplained empty dropdown reads as a broken page.
 */
export const studentRegistrationOptionSchema = z.object({
  id: z.guid(),
  name: z.string(),
  isDefault: z.boolean().default(false),
  countryDefinitionId: z
    .guid()
    .nullish()
    .transform((v) => v ?? null)
});

/**
 * GET /api/v1/users/my-students/registration-options — the register form's two picker lists.
 *
 * ⚠ Why not the admin queries: `ListSchoolsQuery` needs `identity.schools.view`, which a teacher does not
 * hold, and it returns the whole school record (tax id, contact person, address). This carries id + display
 * name for live, active schools, and the ASSIGNABLE roles only — participant (student, parent …) plus
 * supervisor (teacher). Administrator, superadmin and partner roles are never offered, and the write
 * re-checks the same set.
 */
export const myStudentRegistrationOptionsSchema = z.object({
  schools: z.array(studentRegistrationOptionSchema),
  roles: z.array(studentRegistrationOptionSchema),
  /**
   * PPT-TYPES: the program types the caller's partner company runs — what the single partner invite sends.
   * Empty for a teacher. `.default([])` so a server one deploy behind still parses.
   */
  programTypeIds: z.array(z.guid()).default([])
});

export type StudentRegistrationOption = z.infer<typeof studentRegistrationOptionSchema>;
export type MyStudentRegistrationOptions = z.infer<typeof myStudentRegistrationOptionsSchema>;

export const inviteMyStudentResultSchema = z.object({
  userId: z.guid(),
  displayName: z.string(),
  status: z.string(),
  /** created = a new account · linked = the address already had one. */
  outcome: z.enum(['created', 'linked']),
  /** STU-HANDLE: the generated sign-in handle (f.l.15), present only when no e-mail was given. */
  username: z.string().nullish(),
  /**
   * STU-HANDLE: the one-time password, present only on the create-without-e-mail path.
   *
   * ⚠ THIS IS THE ONLY TIME IT EXISTS IN READABLE FORM. It is hashed server-side before the row is written,
   * so it cannot be re-read — only re-issued. The UI must show it once and say so; putting it in a toast that
   * auto-dismisses over a credential nobody has written down is a support ticket.
   */
  temporaryPassword: z.string().nullish()
});

/**
 * STU-CRED — GET /api/v1/users/my-students/{id}: how ONE of my students signs in.
 *
 * Feeds the Details box on the teacher's My-invitations tab, which answers the question a teacher actually
 * asks the lesson after an invite: "this student is standing here and cannot log in."
 */
export const myStudentCredentialsSchema = z.object({
  userId: z.guid(),
  displayName: z.string(),
  /** Null for a student created without one (mig 314 made the column nullable). */
  email: z.string().nullish(),
  /** The generated handle (a.l.15) or one the student chose. Null = e-mail-only sign-in. */
  username: z.string().nullish(),
  hasEmail: z.boolean(),
  /**
   * ⚠ THE field that decides whether a re-issue is offered — not `status`, which is 'active' for a handle
   * student from the moment they are created. Once this is true the student can reset their own password, and
   * the server REFUSES a teacher-issued one with a 409.
   */
  emailVerified: z.boolean(),
  /** Still on a teacher-issued credential, i.e. they have not signed in and chosen their own yet. */
  mustChangePassword: z.boolean()
});

/**
 * STU-CRED — POST /api/v1/users/my-students/{id}/reset-password.
 *
 * ⚠ This is a RE-ISSUE, not a reveal, and no endpoint can ever be the latter: the password is Argon2id-hashed
 * at rest and the plaintext is never stored. So `temporaryPassword` is a BRAND-NEW value and the previous one
 * stops working the instant this returns — the UI has to say so, or a teacher will keep handing out the old
 * slip. Like the invite response, this is the only time it exists in readable form.
 */
export const myStudentPasswordResetSchema = z.object({
  userId: z.guid(),
  displayName: z.string(),
  /** TCH-USR: may be a handle this very call minted — see `usernameCreated`. */
  username: z.string(),
  temporaryPassword: z.string(),
  /**
   * TCH-USR: the student had NO username and one was created along with the password. Two consequences the
   * UI must state, because neither is guessable: the student now signs in with a handle they have never
   * seen, and any activation link already sent to them is dead (activation refuses an account that has a
   * password). Defaulted so an older server's response still parses.
   */
  usernameCreated: z.boolean().nullish().transform((v) => v ?? false)
});

/**
 * TCH-CRED-XLSX — why one student in a credential export got no password.
 *
 * ⚠ AN EMPTY PASSWORD CELL IS AN EXPECTED STATE, NOT A FAILURE, and these are the five ways to reach it.
 * The server ships the CODE and the SPA owns the sentence [STD-I18N]; keep this list in step with
 * Identity's `CredentialExportSkipReasons`, which is the only other place they are written down.
 */
export const credentialExportSkipReasons = [
  /** Verified their address, so password recovery is theirs — minting would be a takeover. */
  'email_verified',
  /** Invited by e-mail and never activated: no password to replace, and a live link that must survive. */
  'awaiting_activation',
  /** They chose their own password. Not a teacher-issued credential any more. */
  'not_temporary',
  'account_disabled',
  /** Not on the caller's list. Deliberately indistinguishable from "no such student". */
  'not_found'
] as const;

export const skippedStudentCredentialSchema = z.object({
  userId: z.guid(),
  /** Empty for `not_found` — there is no row to read a name from, and inventing one would leak existence. */
  displayName: z.string(),
  /**
   * Kept a plain string rather than an enum so a server that adds a reason does not fail the parse and
   * blank the whole result panel — an unknown code falls back to a generic sentence.
   */
  reason: z.string()
});

/**
 * TCH-CRED-XLSX — POST /api/v1/users/my-students/credentials/export.
 *
 * ⚠ THIS IS A WRITE THAT LOOKS LIKE A DOWNLOAD. Every password in the file was MINTED by this call, because
 * the old one cannot be read back at any price. So each of `issuedCount` students is now holding a slip that
 * no longer works and has had their sessions closed — the UI must say that BEFORE the click, and must treat
 * the returned file as the only copy that will ever exist.
 *
 * ⚠ `contentBase64` IS THE .xlsx, base64 inside the normal envelope rather than a raw file response. The
 * server's Idempotency middleware buffers a write's body as TEXT and replays it as text, so a binary body
 * comes back corrupt on a replayed key — after the passwords were really rotated. Decode with `atob` into a
 * Blob; see `decodeBase64ToBlob` in the teacher SPA.
 */
export const myStudentCredentialExportSchema = z.object({
  fileName: z.string(),
  contentBase64: z.string(),
  /** Rows in the file, skipped students included. */
  totalCount: z.number(),
  /** How many passwords were actually minted — the irreversible number. */
  issuedCount: z.number(),
  skipped: z.array(skippedStudentCredentialSchema).nullish().transform((v) => v ?? [])
});

/**
 * PROF-REQ — the profile fields a user is expected to have filled in, as the server names them.
 *
 * ⚠ THESE ARE WIRE NAMES, NOT LABELS, and they match the form controls on purpose: the server returns
 * `missingProfileFields` as this exact vocabulary (Identity's `ProfileCompleteness`), so a client can map a
 * missing field straight onto the input that fixes it without a lookup table. Never translate one of these
 * strings — resolve it to a message id at render time.
 */
export const profileCompletenessFields = [
  'firstName',
  'lastName',
  'birthDate',
  'genderDefinitionId',
  'countryDefinitionId'
] as const;

/**
 * TCH-USR — one row of GET /api/v1/users/my-students: a student the CALLER supervises.
 *
 * ⚠ NOT the guardian endpoint. `/users/me/students` is a parent's linked children and a different
 * population entirely; this is the teacher/partner-employee roster (Identity's `SupervisedStudentScope`).
 *
 * ⚠ THE ROW CARRIES FIELD NAMES, NEVER DEMOGRAPHIC VALUES. There is no birth date, gender or country here
 * and none may be added: the teacher is entitled to know WHAT a student still has to fill in — the answers
 * are the student's. Same boundary that masks profile-sourced answers on the event roster.
 */
export const myStudentRowSchema = z.object({
  userId: z.guid(),
  displayName: z.string(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  /** Null for a student created without one (STU-HANDLE, mig 314). */
  email: z.string().nullish(),
  /** The generated handle (a.l.15) or one the student chose. Null = e-mail-only sign-in. */
  username: z.string().nullish(),
  hasEmail: z.boolean(),
  emailVerified: z.boolean(),
  /** Still on a teacher-issued credential — they have not signed in and chosen their own yet. */
  mustChangePassword: z.boolean(),
  /** Account lifecycle: 'invited' = never activated, 'active', 'disabled'. */
  status: z.string(),
  createdAt: z.string(),
  /**
   * Wire names from {@link profileCompletenessFields}; empty = complete. Kept as a plain string array
   * rather than an enum so a server that adds a field to the rule does not fail the parse and blank the
   * whole page — an unknown name simply renders as itself.
   */
  missingProfileFields: z.array(z.string()).nullish().transform((v) => v ?? []),
  // TCH-USR-COLS: the list fills these; the single-student write responses leave them null / empty, hence
  // nullish everywhere — a fill response must not fail the parse for not carrying a list column.
  schoolId: z.guid().nullish(),
  schoolName: z.string().nullish(),
  /** The ASSIGNED teacher (user_profiles.teacher_user_id) — not "who supervises this student". */
  teacherUserId: z.guid().nullish(),
  teacherName: z.string().nullish(),
  /** The stored band per programme; opaque ids, named from Definitions' age categories. */
  ageCategories: z
    .array(z.object({ programTypeId: z.guid(), ageCategoryId: z.guid() }))
    .nullish()
    .transform((v) => v ?? []),
  /** USER-CODE (mig 629): the 9-character code; null until the tenant has a prefix. */
  userCode: z.string().nullish()
});

/**
 * TCH-USR-COLS — GET /api/v1/users/my-students/filter-options: the schools and teachers that occur on the
 * caller's OWN roster, for the School / Teacher column filters. Not the tenant's register — a teacher may
 * not browse that, and a choice matching none of their students could only answer "nobody".
 */
export const myStudentFilterOptionsSchema = z.object({
  schools: z.array(z.object({ id: z.guid(), name: z.string() })).nullish().transform((v) => v ?? []),
  teachers: z.array(z.object({ id: z.guid(), name: z.string() })).nullish().transform((v) => v ?? [])
});
export type MyStudentFilterOptions = z.infer<typeof myStudentFilterOptionsSchema>;

/**
 * TCH-USR — PATCH /api/v1/users/my-students/{id}/profile: FILL IN what the student left empty.
 *
 * ⚠ THIS IS NOT THE NULL-THROUGH CONTRACT of PUT /users/me/profile, where a null field CLEARS the
 * attribute. Here an omitted field is LEFT ALONE, and a field the student has already answered is
 * REFUSED with a 409 (`teacher-student-profile-field-answered`, whose detail names the fields) rather
 * than overwritten. The data subject's own answer always wins.
 */
export interface FillMyStudentProfileRequest {
  firstName?: string;
  lastName?: string;
  /** ISO date (YYYY-MM-DD). */
  birthDate?: string;
  /** Opaque definition id, group 'gender'. Guid.Empty is rejected by the server — omit instead. */
  genderDefinitionId?: string;
  /** Opaque definition id, group 'country'. */
  countryDefinitionId?: string;
}

/** TCH-USR — POST /api/v1/users/my-students/{id}/resend-activation. */
export const activationResultSchema = z.object({
  status: z.string()
});

// ==================== PPT-TCH — a partner employee assigns one of their teachers ==================== //
//
// ⚠ WHAT THIS WRITES IS AN ACCESS EDGE. `teacher_user_id` is the only source the teacher-scope gRPC edge
// resolves "who are my students" from, so the chosen teacher gains the students' travel details, passport
// presence, event registrations and ticket assignment. The CALLER gains nothing new — they already see those
// students through their company. Plan: ZerEduService/docs/partner-assign-teacher-plan.md.

/** One of the caller's company teachers, for the picker. */
export const partnerTeacherOptionSchema = z.object({
  userId: z.guid(),
  displayName: z.string()
});

/**
 * What assignment WOULD do to a row. Rendered from CODES [STD-I18N] — the server never sends prose.
 *
 * ⚠ ONE "held" case, not two (2026-08-13 decision). An earlier version split "another of our teachers" from
 * "the teacher the student chose" because only the first was movable; the coordinator may now override either,
 * so a distinction that changes nothing would only be UI to explain.
 */
export const teacherAssignOutcomes = [
  'would_assign',
  'already_this_teacher',
  'has_other_teacher'
] as const;

export const teacherAssignCandidateSchema = z.object({
  userId: z.guid(),
  // Parts AND the composed name: re-splitting a display name guesses which token is the middle one, and
  // USR-PARTS exists because that guess was wrong.
  firstName: z.string().nullish(),
  middleName: z.string().nullish(),
  lastName: z.string().nullish(),
  displayName: z.string(),
  email: z.string().nullish(),
  username: z.string().nullish(),
  countryDefinitionId: z.guid().nullish(),
  /** The types this user routes through the caller's company — context for the coordinator, not a write key. */
  programTypeIds: z.array(z.guid()).nullish().transform((v) => v ?? []),
  currentTeacherUserId: z.guid().nullish(),
  /** Who holds them now — what makes a "will be skipped/moved" row explicable. */
  currentTeacherName: z.string().nullish(),
  /** Kept a plain string: an unrecognised code from a newer server must still render. */
  outcomeIfAssigned: z.string(),
  /** TKT-SCH: id AND name — see {@link assignableStudentSchema} for why this one carries text. */
  schoolId: z.guid().nullish(),
  schoolName: z.string().nullish()
});

/**
 * TKT-SCH: one school the current filter set actually contains — the options for the modal's school picker.
 *
 * ⚠ A FACET, NOT A CATALOGUE. This list is SERVER-paged, so the dialog cannot derive its options from the
 * rows it holds the way the seat picker does; and every option here is guaranteed to match at least one
 * candidate, so picking one can never empty the list.
 */
export const teacherAssignSchoolFacetSchema = z.object({
  schoolId: z.guid(),
  schoolName: z.string()
});

export const teacherAssignCandidateListSchema = z.object({
  items: z.array(teacherAssignCandidateSchema).nullish().transform((v) => v ?? []),
  totalItems: z.number().int(),
  /**
   * Counts over the WHOLE filtered set, not the page: the confirmation has to say how many people move, and
   * a page-scoped count cannot see the ones the coordinator never scrolled to.
   */
  wouldAssignCount: z.number().int(),
  alreadyThisTeacherCount: z.number().int(),
  hasOtherTeacherCount: z.number().int(),
  /**
   * TKT-SCH: computed over the filter set BEFORE the school filter is applied, so choosing a school does
   * not collapse the picker to the option already chosen. Nullish-with-a-default so a server that predates
   * the field leaves the picker empty rather than failing the parse — a field missing from this schema is
   * silently stripped [STD-FE], and the same is true in reverse.
   */
  schools: z.array(teacherAssignSchoolFacetSchema).nullish().transform((v) => v ?? [])
});

/** `truncated` = the server returned fewer ids than match, so "select all" must SAY it selected fewer. */
export const teacherAssignCandidateIdsSchema = z.object({
  userIds: z.array(z.guid()).nullish().transform((v) => v ?? []),
  truncated: z.boolean()
});

export const teacherAssignRowSchema = z.object({
  userId: z.guid(),
  outcome: z.string(),
  reason: z.string().nullish(),
  previousTeacherName: z.string().nullish()
});

export const teacherAssignResultSchema = z.object({
  teacherUserId: z.guid(),
  rows: z.array(teacherAssignRowSchema).nullish().transform((v) => v ?? []),
  assignedCount: z.number().int(),
  replacedCount: z.number().int(),
  unchangedCount: z.number().int(),
  skippedCount: z.number().int()
});

/**
 * POST /api/v1/users/my-partner-students/assign-teacher.
 *
 * ⚠ NO companyId — the server resolves it from the caller's own employment link. Sending one would let any
 * holder of the permission route a rival company's students.
 */
export interface AssignPartnerTeacherRequest {
  teacherUserId: string;
  userIds: string[];
  /**
   * Moves students who already have a teacher — ANY teacher, including one the student chose themselves.
   * Unticked, those rows are skipped and reported with the holder's name.
   */
  replaceExisting?: boolean;
}

export type PartnerTeacherOption = z.infer<typeof partnerTeacherOptionSchema>;
export type TeacherAssignCandidate = z.infer<typeof teacherAssignCandidateSchema>;
export type TeacherAssignSchoolFacet = z.infer<typeof teacherAssignSchoolFacetSchema>;
export type TeacherAssignCandidateList = z.infer<typeof teacherAssignCandidateListSchema>;
export type TeacherAssignResult = z.infer<typeof teacherAssignResultSchema>;
export type TeacherAssignRow = z.infer<typeof teacherAssignRowSchema>;

// ============================ TCH-XLSX — bulk register from a spreadsheet ====================== //

/**
 * The row-error codes the server reports. Mirrors `BulkRegisterErrorCodes` on the Identity side.
 *
 * ⚠ CODES, NEVER PROSE, and that is not a style preference: the report is rendered in the reader's
 * language, so an English sentence from the server would be untranslatable. Each code maps to a message
 * id in the SPA.
 */
export const bulkRegisterErrorCodes = [
  'first_name_required',
  'last_name_required',
  'name_too_long',
  'email_invalid',
  'email_too_long',
  'email_duplicate_in_file',
  'email_already_registered',
  'email_belongs_to_deleted_account',
  'gender_unknown',
  'birth_date_invalid',
  'school_unknown',
  'role_unknown',
  /**
   * Two LIVE rows share the name the cell holds. Neither table is unique on its display name, so this is
   * reachable from ordinary tenant data — and resolving it by picking one would put a student in a school
   * they do not attend, or grant a role nobody chose.
   */
  'school_ambiguous',
  'role_ambiguous',
  /**
   * TCH-XLSX-SCOPE: the upload was scoped to ONE school and this row names a different one. Distinct from
   * `school_unknown` on purpose — the school is real and live, so "retype the cell" is the wrong advice; the
   * file was built for another school. Never silently corrected: that would move a student without saying so.
   */
  'school_outside_selection',
  /** TCH-XLSX-SCOPE: scoped to a COUNTRY, and this row's school is in another one — or in none at all. */
  'school_outside_country'
  // ⚠ STILL NO 'country_unknown': the Country COLUMN is informational and never read. The country a created
  // account gets comes from the scope the dialog sends, falling back to the uploader's own.
] as const;

/** Whole-FILE rejections (as opposed to row ones): unreadable archive, wrong shape, too many rows. */
export const bulkRegisterFileErrors = [
  'file_unreadable',
  'file_no_sheet',
  'file_no_rows',
  'file_too_many_rows',
  'file_not_template'
] as const;

/**
 * One rejected row.
 *
 * ⚠ `row` is the number AS IT APPEARS IN EXCEL, header included. It is the whole usable output of a
 * rejected upload — a 200-row file whose errors carry no row numbers cannot be fixed.
 */
export const bulkRegisterRowErrorSchema = z.object({
  row: z.number(),
  column: z.string(),
  /** Kept a plain string, not the enum: an unrecognised code from a newer server must still render. */
  code: z.string(),
  /** The offending cell text, echoed back so the teacher can find it without opening the file alongside. */
  value: z.string().nullish()
});

export const bulkRegisterRowErrorListSchema = z.array(bulkRegisterRowErrorSchema);

/** One created account. */
export const bulkRegisteredStudentSchema = z.object({
  userId: z.guid(),
  displayName: z.string(),
  email: z.string().nullish(),
  username: z.string().nullish(),
  /**
   * ⚠ PRESENT ONLY for a row with no e-mail, and the ONLY time this value exists in readable form — it is
   * hashed before the row is written, so it can never be re-read, only re-issued from the Manage box. A
   * 50-row file returns 50 of these: the UI must offer to SAVE them, never merely display them.
   */
  temporaryPassword: z.string().nullish()
});

export const bulkRegisterResultSchema = z.object({
  createdCount: z.number(),
  students: z.array(bulkRegisteredStudentSchema)
});

/**
 * A gender the CLIENT resolved from the Definitions catalogue, sent WITH the upload and with the template
 * request.
 *
 * ⚠ Identity cannot read definitions.definitions at all [STD-OWN] — there is no channel to that service —
 * so it cannot turn "Kadın" into an id on its own. The same trust as `PUT /users/me/profile`, which already
 * accepts an arbitrary GUID for that field for exactly this reason.
 *
 * ⚠ SCHOOLS AND ROLES ARE NOT SENT. They live in Identity's own tables, so the server builds those two
 * dropdowns and resolves those two columns itself — nothing the client sends decides which school or role a
 * row may name.
 */
export interface BulkRegisterChoice {
  id: string;
  name: string;
}

/**
 * The template's visible words. Passed in so the workbook is generated in the READER's language — copy
 * lives in the SPA's catalogues, and a hard-coded Turkish header would be the one part of this feature a
 * German teacher could not use.
 */
export interface RosterTemplateLabels {
  sheetName: string;
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  birthDate: string;
  gender: string;
  school: string;
  role: string;
  country: string;
  instructions: string;
  /**
   * The note on the Country header, which must already NAME the uploader's country. That column does
   * nothing on upload, so this sentence is the only thing standing between the teacher and "why did my
   * Country column get ignored".
   */
  countryNote: string;
  /**
   * The tip Excel shows on a Birth date cell, and its refusal message when the typed value is not a
   * YYYY-MM-DD date. Optional on the wire: without it the column is still guarded, just wordlessly.
   */
  birthDateHint?: string;
}

/**
 * TCH-XLSX-SCOPE: what the teacher narrowed the template to.
 *
 * ⚠ THE SAME OBJECT MUST BE SENT WITH THE UPLOAD. The template request alone is a promise the upload would
 * not keep: a file scoped to one school, plus a pasted row naming another, would import.
 *
 * `countryName` is cosmetic — the cell text — because Identity cannot resolve a definitions id to a name
 * [STD-OWN]. `schoolId` is checked against Identity's own table, and against `countryDefinitionId`.
 */
export interface BulkRegisterScope {
  countryDefinitionId?: string | null;
  countryName?: string | null;
  schoolId?: string | null;
}

export interface BulkRegisterTemplateRequest extends BulkRegisterScope {
  genders: BulkRegisterChoice[];
  labels: RosterTemplateLabels;
}

/**
 * The `references` multipart part — the catalogue the server matches cell text against, plus the scope it
 * enforces. `countryName` is not needed here (nothing is written into a cell), so it is omitted.
 */
export interface BulkRegisterReferences {
  genders: BulkRegisterChoice[];
  countryDefinitionId?: string | null;
  schoolId?: string | null;
  /**
   * AGECAT-SEED: the age-band catalogue, so the server can place each created account from its Birth
   * date cell (identity.user_age_categories).
   *
   * ⚠ THE SERVER DERIVES HERE, unlike self-registration, where the form derives and posts the
   * placements. Not an inconsistency: these rows come out of a workbook the uploader never
   * enumerated, so there is no client that could place them. The catalogue travels for the same
   * reason `genders` does — `definitions.age_categories` is another service's schema [STD-OWN].
   *
   * ⚠ NOT ON `BulkRegisterTemplateRequest`. A band decides nothing about a cell, so the workbook has
   * no use for it; only the upload does.
   *
   * Omitted = no placements, which is what this upload did before the feature. Send the bands in
   * catalogue order — `orderNo` is carried, so order is not load-bearing on the wire, but a band
   * without it would be placed by array position.
   */
  ageBands?: BulkRegisterAgeBand[];
}

/**
 * AGECAT-SEED: one age band, as the uploader's SPA resolved it from the Definitions catalogue.
 *
 * ⚠ `orderNo` IS PART OF THE PLACEMENT RULE, not display data: overlapping bands resolve by
 * catalogue order. The band's NAME is deliberately absent — nothing server-side renders it.
 */
export interface BulkRegisterAgeBand {
  id: string;
  programTypeId: string;
  minAge: number;
  maxAge: number;
  orderNo: number;
}

/**
 * Reads the row report out of a 422 problem.
 *
 * ⚠ It travels in the problem's EXTENSIONS (`rowErrors`), which survive because ProblemDetails carries an
 * index signature and the client does not zod-strip an error body. Returns an empty array for any other
 * failure, so a caller can render "the whole file was rejected" without special-casing shapes.
 */
export function bulkRegisterRowErrors(problem: ProblemDetails): BulkRegisterRowError[] {
  const parsed = bulkRegisterRowErrorListSchema.safeParse(problem.rowErrors);
  return parsed.success ? parsed.data : [];
}

/** Reads the whole-file rejection code out of a 422 problem, when that is what happened. */
export function bulkRegisterFileError(problem: ProblemDetails): string | null {
  return typeof problem.fileError === 'string' ? problem.fileError : null;
}

export interface PurchaseTicketBatchRequest {
  ticketId: string;
  quantity: number;
  /** PCOM P6: pay with the company's ticket credits (1 credit = 1 seat) — all or nothing. */
  useCredits?: boolean;
}

export interface AssignTicketRequest {
  studentUserRef: string;
}

/**
 * TCH-BULK — POST /events/{eventId}/teacher/ticket-batches/{batchId}/assign.
 *
 * ⚠ NO SEAT IDS. The server picks which free seat each student gets (creation order): letting the client
 * name seats would mean trusting that they were still free when it looked, which two teachers distributing
 * at once turns into an overwrite.
 */
export interface BulkAssignTicketsRequest {
  studentUserRefs: string[];
}

/** Why a student was skipped. Anything unrecognised renders as a generic "could not assign". */
export const bulkAssignSkipReasons = [
  'already_registered',
  'already_holds_ticket',
  'out_of_scope',
  'no_seat_left'
] as const;

export const bulkAssignOutcomeSchema = z.object({
  studentUserRef: z.guid(),
  studentName: z.string(),
  assigned: z.boolean(),
  /** Kept a plain string, not an enum: a server that adds a reason must not fail the parse and blank the
   *  whole result view — the row falls back to generic copy instead. */
  reason: z.string().nullish(),
  allocationId: z.guid().nullish()
});

/**
 * BATCH-CANCEL (mig 464) — POST /events/{eventId}/teacher/ticket-batches/{batchId}/cancel.
 *
 * ⚠ NOT the batch DTO. The server deliberately answers with the two COUNTS instead, because they
 * describe a MOMENT that no refetch can recover: how many seats went back, and how many of those a
 * waiting-list student took immediately. The second number is the difference between "you can change
 * your mind" and "that seat is gone now", and the confirmation is the only place it can be said.
 */
export const ticketBatchCancelResultSchema = z.object({
  batchId: z.guid(),
  /** Always 'cancelled'; sent so the client never assumes the resulting state. */
  paymentState: z.string(),
  releasedSeats: z.number().int(),
  promotedFromWaitlist: z.number().int()
});

export const bulkAssignResultSchema = z.object({
  outcomes: z.array(bulkAssignOutcomeSchema),
  assignedCount: z.number(),
  skippedCount: z.number(),
  /** Seats still free on this order AFTER the write, so the caller need not refetch to show a live cap. */
  remainingSeats: z.number()
});

export type EventRegistrationSource = (typeof eventRegistrationSources)[number];
export type TeacherAnswer = z.infer<typeof teacherAnswerSchema>;
export type TeacherRegistrationRow = z.infer<typeof teacherRegistrationRowSchema>;
export type TeacherTravelRow = z.infer<typeof teacherTravelRowSchema>;
export type TeacherVisaRow = z.infer<typeof teacherVisaRowSchema>;
export type TicketAllocation = z.infer<typeof ticketAllocationSchema>;
export type TicketAllocationStatus = (typeof ticketAllocationStatuses)[number];
export type TicketBatch = z.infer<typeof ticketBatchSchema>;
export type TicketBatchAdmin = z.infer<typeof ticketBatchAdminSchema>;
export type TicketBatchPaymentState = (typeof ticketBatchPaymentStates)[number];
export type TeacherTickets = z.infer<typeof teacherTicketsSchema>;
export type AssignableStudent = z.infer<typeof assignableStudentSchema>;
export type BulkAssignOutcome = z.infer<typeof bulkAssignOutcomeSchema>;
export type BulkAssignResult = z.infer<typeof bulkAssignResultSchema>;
export type TicketBatchCancelResult = z.infer<typeof ticketBatchCancelResultSchema>;
export type BulkAssignSkipReason = (typeof bulkAssignSkipReasons)[number];
export type EventInvitation = z.infer<typeof eventInvitationSchema>;
export type EventInvitationKind = (typeof eventInvitationKinds)[number];
export type EventInvitationStatus = (typeof eventInvitationStatuses)[number];
export type EventInvitationOutcome = z.infer<typeof eventInvitationOutcomeSchema>;
export type InviteStudentsResult = z.infer<typeof inviteStudentsResultSchema>;
export type InviteMyStudentResult = z.infer<typeof inviteMyStudentResultSchema>;
export type MyStudentCredentials = z.infer<typeof myStudentCredentialsSchema>;
export type MyStudentPasswordReset = z.infer<typeof myStudentPasswordResetSchema>;
export type MyStudentRow = z.infer<typeof myStudentRowSchema>;
export type CredentialExportSkipReason = (typeof credentialExportSkipReasons)[number];
export type SkippedStudentCredential = z.infer<typeof skippedStudentCredentialSchema>;
export type MyStudentCredentialExport = z.infer<typeof myStudentCredentialExportSchema>;
export type ProfileCompletenessField = (typeof profileCompletenessFields)[number];
export type ActivationResult = z.infer<typeof activationResultSchema>;
export type BulkRegisterRowError = z.infer<typeof bulkRegisterRowErrorSchema>;
export type BulkRegisteredStudent = z.infer<typeof bulkRegisteredStudentSchema>;
export type BulkRegisterResult = z.infer<typeof bulkRegisterResultSchema>;
export type BulkRegisterErrorCode = (typeof bulkRegisterErrorCodes)[number];
export type BulkRegisterFileError = (typeof bulkRegisterFileErrors)[number];

export type EventSummary = z.infer<typeof eventSummarySchema>;
export type EventDetail = z.infer<typeof eventDetailSchema>;
export type EventRegistration = z.infer<typeof eventRegistrationSchema>;

// ------------------------------ C-13b: event sponsors --------------------------------
// DG-3 (mig 205): tier ARTIK OPAK definition id (guid, nullish) — eski sabit string enum'un yerini aldı.
// Değerler Definitions 'sponsor_tiers' grubundan gelir; FE id'ye göre saklar/gösterir [STD-OWN].
export const sponsorContributionTotalSchema = z.object({ currency: z.string(), total: z.number() });

/** SponsorDto — one event sponsor; contactRef opaque (identity id) [STD-OWN]. */
export const sponsorSchema = z.object({
  id: z.guid(),
  eventId: z.guid(),
  name: z.string(),
  tierDefinitionId: z.guid().nullish(), // opak definition id (sponsor_tiers) — DG-3 mig 205
  contactRef: z.guid().nullish(),
  website: z.string().nullish(),
  contributionAmount: z.number().nullish(),
  currency: z.string().nullish(),
  logoRef: z.string().nullish(),
  notes: z.string().nullish(),
  createdAtUtc: isoDate,
  updatedAtUtc: isoDate
});
export const sponsorListSchema = z.object({
  items: z.array(sponsorSchema),
  totals: z.array(sponsorContributionTotalSchema)
});
export type Sponsor = z.infer<typeof sponsorSchema>;
export type SponsorContributionTotal = z.infer<typeof sponsorContributionTotalSchema>;

/** POST/PUT sponsor body — a contribution amount requires a 3-letter currency (server + DB CHECK). */
export interface SponsorRequest {
  name: string;
  /** DG-3: opaque definition id (sponsor_tiers); null/omitted = no tier. */
  tierDefinitionId?: string | null;
  contactRef?: string | null;
  website?: string | null;
  contributionAmount?: number | null;
  currency?: string | null;
  logoRef?: string | null;
  notes?: string | null;
}

// ------------------------------ C-13b: event safeguarding ----------------------------
export const safeguardingNoteCategories = ['concern', 'disclosure', 'incident', 'action', 'referral'] as const;
export type SafeguardingNoteCategory = (typeof safeguardingNoteCategories)[number];

/** EventSafeguardingDto — assigned lead + consent flag + append-only note count. SENSITIVE (safeguarding.read). */
export const eventSafeguardingSchema = z.object({
  eventId: z.guid(),
  safeguardingLeadRef: z.guid().nullish(),
  consentRequired: z.boolean(),
  noteCount: z.number().int()
});
export const safeguardingNoteSchema = z.object({
  id: z.guid(),
  eventId: z.guid(),
  category: z.enum(safeguardingNoteCategories),
  note: z.string(),
  createdByRef: z.guid().nullish(),
  createdAtUtc: isoDate
});
export const safeguardingNoteListSchema = z.array(safeguardingNoteSchema);
export type EventSafeguarding = z.infer<typeof eventSafeguardingSchema>;
export type SafeguardingNote = z.infer<typeof safeguardingNoteSchema>;

/** PUT safeguarding body (engagement.safeguarding.manage). */
export interface UpdateEventSafeguardingRequest {
  safeguardingLeadRef?: string | null;
  consentRequired: boolean;
}
/** POST safeguarding note body (append-only; engagement.safeguarding.manage). */
export interface AddSafeguardingNoteRequest {
  category: SafeguardingNoteCategory;
  note: string;
}

/**
 * CreateEventCommand / UpdateEventCommand body (DG-3 mig 205). eventTypeDefinitionId/
 * languageDefinitionId are OPAQUE definition IDs (guid, nullish) from the Definitions groups
 * event_types/languages; tags are OPAQUE definition IDs (group event_tags). The FE submits ids,
 * never resolves them cross-schema [STD-OWN]. responsibleUserRef is an opaque identity uuid.
 * capacity null = unlimited ("limit registrations" is a UI-only bool). is-online + location(venue)
 * + meetingUrl may all coexist (hybrid). UTC in/out; displayZone is a display-only hint.
 */
export interface EventWriteRequest {
  title: string;
  description?: string | null;
  eventTypeDefinitionId?: string | null;
  /**
   * PRG-MULTI (mig 491): opaque definition ids (group program_types) — AT LEAST ONE REQUIRED.
   *
   * ⚠ An empty array is rejected with 400, and that is not a formality: server-side an event with no
   * programme cannot be narrowed by anything, so it would appear in EVERY partner's catalogue.
   */
  programTypeIds: string[];
  /**
   * 3-letter ISO-4217 code for the event's price lists. THE PAYMENT PATH USES THIS: it rides the
   * payable event, is snapshotted onto commerce.orders.currency and reaches Stripe. Omit = service
   * default.
   */
  currency?: string;
  /**
   * CUR (mig 286): the OPAQUE definitions id of the PICKED currency (group 'currencies'). Sent
   * TOGETHER with the code because the server cannot resolve definitions [STD-OWN] — it validates the
   * code's FORMAT only and the FE owns set membership. The FE must take the code FROM the picked
   * option so the two can never disagree.
   */
  currencyDefinitionId?: string | null;
  startsAtUtc: string;
  endsAtUtc: string;
  location?: string | null;
  capacity?: number | null;
  displayZone?: string | null;
  languageDefinitionId?: string | null;
  passingRatio?: number | null;
  responsibleUserRef?: string | null;
  /** Organizer company uuid (type='organizer'); required on the backend. */
  organizerId?: string | null;
  isOnline?: boolean;
  meetingUrl?: string | null;
  /**
   * EVT-PAY (mig 271): the final installment must fall at least this many days BEFORE the event
   * starts; options that would not fit are never offered to the payer. Omit/null = service default (7).
   */
  paymentDeadlineDays?: number | null;
  /**
   * FUND (mig 353): the share of a ticket's charged unit price that each funded seat consumes, as a
   * percentage (1..100). Omit/null = platform default (20) — and that is NOT the same as sending 20,
   * which records a choice and stops following the default.
   */
  fundingMinPercent?: number | null;
  // DG-3: each tag element is a definition ID (group event_tags).
  tags?: string[];
  // Audience filters (EV audience epic) — all optional; empty array = unrestricted (open to everyone).
  // DG-3: countries/ageIntervals/genders are definition IDs (groups country/age_intervals/genders);
  // schools = company uuids (type='school', UNCHANGED).
  countries?: string[];
  schools?: string[];
  ageIntervals?: string[];
  genders?: string[];
  /**
   * EV-AUD-EXCL (mig 487): the EXCLUDE side of the same dimensions — "open to everyone except these".
   *
   * ⚠ A dimension EITHER includes OR excludes. Sending both `countries` and `excludedCountries`
   * non-empty is a 422; clear one. (Across DIFFERENT dimensions the two modes mix freely — include
   * a country while excluding a school is fine.)
   *
   * ⚠ Semantics differ from the include side for a caller with no value: an include REQUIRES the
   * attribute (no country on the profile → not eligible), an exclude does not (no country → not
   * excluded). So "everyone except Germany" still reaches anonymous visitors and incomplete profiles.
   *
   * No excludedAgeIntervals — the age_interval dimension does not constrain eligibility server-side.
   */
  excludedCountries?: string[];
  excludedSchools?: string[];
  excludedGenders?: string[];
  /**
   * EVT-MODE (mig 308): off | optional | required per information block.
   *
   * ⚠ OMITTED = LEAVE UNCHANGED on update (the server keeps the stored value) and the column default on
   * create. So a caller that does not know about these cannot accidentally reset them — which is also why
   * they must be listed HERE: a field missing from the wire type is silently dropped, and on the server
   * side that exact omission made every mode change appear to succeed while changing nothing.
   */
  travelInfoMode?: EventInfoMode | null;
  visaInfoMode?: EventInfoMode | null;
  passportMode?: EventInfoMode | null;
  /** AUTO-REG (mig 606): omitted = leave unchanged on update, false on create (same rule as the modes). */
  autoRegister?: boolean | null;
  /** PURCH-SW (mig 624): omitted = leave unchanged on update, false on create (same rule as the modes). */
  personalPurchaseDisabled?: boolean | null;
  salesPaused?: boolean | null;
}

export type CreateEventRequest = EventWriteRequest;
export type UpdateEventRequest = EventWriteRequest;

/**
 * OFR-REQ: POST /events/{id}/teacher/ticket-offers body — a request for n seats.
 *
 * ⚠ NO PRICE (2026-08-17). The buyer asks for seats at the LIST price and the amount is set on the
 * admin side during approval (a chain step holding *Can Create Payment Plan*). The field is gone from
 * this type on purpose rather than left optional: an optional money field on the buyer's request is an
 * invitation to start sending it again, and the server now **refuses** a body that carries one (422
 * `engagement-offer-amount-not-partner-settable`) instead of silently dropping it.
 */
export interface RequestTicketOfferRequest {
  ticketId: string;
  quantity: number;
}

/** OFR-APR: one snapshot step of an offer's chain, plus its decision once made. */
export const eventTicketOfferStepSchema = z.object({
  stepNo: z.number().int(),
  roleCode: z.string(),
  decision: z.enum(['approved', 'rejected']).nullish(),
  decisionNote: z.string().nullish(),
  decidedByName: z.string().nullish(),
  decidedAtUtc: isoDate.nullish()
});

/**
 * The PARTNER's view of their own offer (create + withdraw responses).
 *
 * ⚠ NO CHAIN — no waiting role, no steps. The approval chain is the tenant's INTERNAL sign-off
 * structure and a partner is an outside party: they need to know the offer is pending, approved, or
 * why it was refused, not whose signature is next. The server does not send it on this surface
 * (PartnerOfferDto), so there is nothing here to hide — hiding it only in the UI would leave the
 * value in the JSON.
 */
export const eventTicketOfferSchema = z.object({
  id: z.guid(),
  eventId: z.guid(),
  ticketId: z.guid(),
  batchId: z.guid(),
  quantity: z.number().int(),
  listUnitAmount: z.number(),
  offeredUnitAmount: z.number(),
  currency: z.string(),
  status: z.enum(offerStatuses),
  createdAtUtc: isoDate,
  decidedAtUtc: isoDate.nullish(),
  /** The rejecting step's reason — for the partner, unlike the chain itself. */
  rejectionNote: z.string().nullish()
});

export type EventTicketOfferStep = z.infer<typeof eventTicketOfferStepSchema>;
export type EventTicketOffer = z.infer<typeof eventTicketOfferSchema>;
export type OfferStatus = (typeof offerStatuses)[number];

// ==============================|| EXAM INTEGRITY SETTINGS (A1, migration 520) ||============== //

/**
 * A1 — how closely an attempt is supervised. Ordered: each tier includes the one below. `none` is the
 * default and always available; `standard` / `strict` are licensed per tenant (`availableTiers` says
 * which this tenant may pick). `strict` REQUIRES fullscreen — the server refuses it otherwise (422).
 */
export const proctoringTiers = ['none', 'standard', 'strict'] as const;
export type ProctoringTier = (typeof proctoringTiers)[number];

/** A1 — whether an IP allowlist restricts who may start an attempt (enforced in a later step). */
export const ipPolicies = ['none', 'allowlist'] as const;
export type IpPolicy = (typeof ipPolicies)[number];

/**
 * A1 — GET /exam/exams/{id}/integrity-settings.
 *
 * ⚠ `settingsVersion` IS THE If-Match TOKEN. The server also sends it as an `ETag` header, but a
 * browser cannot read that cross-origin (not a CORS-exposed header), so send THIS back as
 * `If-Match: "<n>"` on the PUT. Missing → 428; stale → 409.
 *
 * ⚠ THE ACCESS CODE IS NEVER HERE. `hasAccessCode` says whether one is required; the code itself is
 * only ever in the regenerate response (`regeneratedAccessCodeSchema`) and cannot be read back.
 *
 * The last five fields are READ-ONLY on this surface: they belong to the exam and are edited with it.
 */
export const examIntegritySettingsSchema = z.object({
  examId: z.string(),
  settingsVersion: z.number().int(),
  proctoringTier: z.enum(proctoringTiers),
  randomizeQuestions: z.boolean(),
  randomizeChoices: z.boolean(),
  hasAccessCode: z.boolean(),
  accessCodeSetAtUtc: isoDate.nullish(),
  ipPolicy: z.enum(ipPolicies),
  /** Canonical CIDR strings. Kept even under `ipPolicy: 'none'`, so switching the policy off loses nothing. */
  ipAllowlist: z.array(z.string()),
  requireFullscreen: z.boolean(),
  /** The tiers this tenant's plan lets an author SELECT. Always includes `none`. */
  availableTiers: z.array(z.enum(proctoringTiers)),
  examStatus: z.string(),
  durationMinutes: z.number(),
  opensAtUtc: isoDate.nullish(),
  closesAtUtc: isoDate.nullish(),
  maxAttempts: z.number().int().nullish(),
  retakeCooldownMinutes: z.number().int().nullish(),
  updatedAtUtc: isoDate,
  /** EXTRA-TIME (mig 528) — do candidates' access arrangements apply to this paper? Absent = pre-528 = yes. */
  allowExtraTime: z.boolean().optional().default(true),
  /** EXAM-RESUME (mig 543) — free | forward_only. Absent = pre-543 = free. */
  navigationMode: z.enum(examNavigationModes).optional().default('free')
});
export type ExamIntegritySettings = z.infer<typeof examIntegritySettingsSchema>;

/** A1 — the body of PUT /exam/exams/{id}/integrity-settings. No access-code field exists, by design. */
export interface ExamIntegritySettingsUpdate {
  proctoringTier: ProctoringTier;
  randomizeQuestions: boolean;
  randomizeChoices: boolean;
  ipPolicy: IpPolicy;
  ipAllowlist: string[];
  requireFullscreen: boolean;
  /**
   * EXTRA-TIME. REQUIRED here even though the server defaults it to true: a PUT is a full replacement, so a
   * caller that forgot it would silently switch a speed test's opt-out back off.
   */
  allowExtraTime: boolean;
  /**
   * EXAM-RESUME. The server treats an ABSENT field as "keep the current mode" (so a pre-543 client cannot
   * reset it), but this client always sends it — the form owns the value.
   */
  navigationMode: ExamNavigationMode;
}

/** A1 — POST …/access-code/regenerate. The ONLY time the plaintext code is ever sent. */
export const regeneratedAccessCodeSchema = z.object({
  examId: z.string(),
  accessCode: z.string(),
  settingsVersion: z.number().int()
});
export type RegeneratedAccessCode = z.infer<typeof regeneratedAccessCodeSchema>;

// ==============================|| EXTRA-TIME (mig 528/529) — access arrangements ||=================== //
//
// A candidate's standing extra time (a percentage), applied to every paper they START. Admins only
// (`exam.arrangements.manage`): an arrangement usually reveals a disability or SEN. There is no reason field.

export const candidateArrangementSchema = z.object({
  userId: z.guid(),
  extraTimePct: z.number().int(),
  updatedAtUtc: isoDate,
  /** EXAM-NAMES: profile name ?? email ?? username, added by the list endpoint; null = unresolved. */
  userDisplayName: z.string().nullish()
});
export type CandidateArrangement = z.infer<typeof candidateArrangementSchema>;

/** Why an invigilator extended a paper. A CODE — free text is where somebody writes a diagnosis. */
export const attemptExtensionReasons = ['incident', 'technical', 'arrangement_missed', 'other'] as const;
export type AttemptExtensionReason = (typeof attemptExtensionReasons)[number];

export const attemptExtensionSchema = z.object({
  id: z.guid(),
  attemptId: z.guid(),
  minutes: z.number().int(),
  reason: z.enum(attemptExtensionReasons),
  grantedAtUtc: isoDate,
  previousDeadlineAtUtc: isoDate,
  newDeadlineAtUtc: isoDate,
  totalExtendedMinutes: z.number().int()
});
export type AttemptExtension = z.infer<typeof attemptExtensionSchema>;

// ==============================|| ATTEMPTS-TAB — every attempt on an exam + a live monitor ||======== //
//
//   GET /exam/grading/exams/{examId}/attempts   exam.attempts.grade OR exam.sittings.invigilate
//   GET /exam/grading/attempts/{id}/monitor     same
//
// Both [NoCache] server-side. An invigilator gets `marksVisible: false` and every score as null — the
// client hides the column rather than rendering blanks.

/**
 * `away` (EXAM-RESUME): still being sat, candidate not seen for 2 minutes. `timed_out`: handed in by the
 * expiry sweep rather than the candidate — the only papers Reopen applies to. Both computed server-side.
 */
export const attemptListFilters = ['all', 'started', 'submitted', 'graded', 'needs_review', 'away', 'timed_out'] as const;
export type AttemptListFilter = (typeof attemptListFilters)[number];

export const examAttemptRowSchema = z.object({
  attemptId: z.guid(),
  userId: z.guid(),
  status: z.string(),
  startedAtUtc: isoDate,
  deadlineAtUtc: isoDate,
  submittedAtUtc: isoDate.nullish(),
  /** What the attempt STARTED under (A4 snapshot), not the exam's setting today. */
  proctoringTier: z.string(),
  settingsVersion: z.number().int().nullish(),
  extraTimeMinutes: z.number().int(),
  extendedMinutes: z.number().int(),
  /** null = not supervised; there is nothing to summarise. */
  proctoring: proctoringSummarySchema.nullish(),
  lastHeartbeatAtUtc: isoDate.nullish(),
  pendingManualCount: z.number().int(),
  scorePct: z.number().nullish(),
  passed: z.boolean().nullish(),
  // EXAM-RESUME (mig 543). Question numbers are 1-based in the order the CANDIDATE was shown.
  navigationMode: z.enum(examNavigationModes).optional().default('free'),
  questionCount: z.number().int().optional().default(0),
  /** null = never reported (an attempt from before mig 543). */
  currentQuestionNumber: z.number().int().nullish(),
  furthestQuestionNumber: z.number().int().nullish(),
  answeredCount: z.number().int().optional().default(0),
  /** Last save, page turn or presence ping — on every paper, supervised or not. */
  lastSeenAtUtc: isoDate.nullish(),
  lastAnswerSavedAtUtc: isoDate.nullish(),
  lastAnsweredQuestionNumber: z.number().int().nullish(),
  isAway: z.boolean().optional().default(false),
  autoSubmitted: z.boolean().optional().default(false),
  reopenCount: z.number().int().optional().default(0),
  reopenedMinutes: z.number().int().optional().default(0),
  /**
   * null = this paper may be reopened. Says nothing about whether the VIEWER may (`exam.attempts.update`).
   * ⚠ Defaults to 'still-open', NOT null: a pre-543 payload must never grow a Reopen button.
   */
  reopenBlocker: z.string().nullable().optional().default('still-open'),
  /** EXAM-ATT-TABLE: country / school at START (mig 547 snapshot); null = not set. Opaque ids — the client names them. */
  countryId: z.string().uuid().nullish(),
  schoolId: z.string().uuid().nullish(),
  /** QUIZ (mig 586): the course a quiz attempt was opened from. Absent on an exam attempt. */
  courseRef: z.string().uuid().nullish(),
  courseTitle: z.string().nullish(),
  /** EXAM-OMR (mig 631): 'online' | 'paper'. A paper attempt came from an optical sheet: no clock, no proctoring. */
  source: z.string().optional().default('online'),
  /** EXAM-OMR: the booklet letter a paper attempt was marked against; null online. */
  bookletLabel: z.string().nullish(),
  resultImportId: z.string().uuid().nullish(),
  /** EXAM-NAMES: profile name ?? email ?? username, added by the LIST endpoint (null on the monitor read). */
  userDisplayName: z.string().nullish(),
  /** NAMES-SWEEP: `schoolId`'s legal name + city (opaque Definitions id), from Identity via the LIST endpoint. */
  schoolName: z.string().nullish(),
  schoolCityDefinitionId: z.string().uuid().nullish(),
  /**
   * EXAM-OVR (mig 641): set while an admin's override stands — `scorePct` then IS the override and this is the engine's
   * score. Graders only, like `scorePct` (an invigilator gets neither: an override is a mark).
   */
  computedScorePct: z.number().nullish(),
  scoreOverriddenAtUtc: isoDate.nullish(),
  /** EXAM-OVR: has the candidate been told this mark? The override dialog says what follows from it. */
  resultAnnounced: z.boolean().optional().default(false)
});
export type ExamAttemptRow = z.infer<typeof examAttemptRowSchema>;

/** EXAM-RESUME — why a paper cannot be reopened (the `reopenBlocker` codes). EXAM-OVR: + score-overridden. */
export const reopenBlockers = [
  'still-open',
  'handed-in-by-candidate',
  'results-released',
  'another-attempt-open',
  'score-overridden'
] as const;

/** EXAM-OVR (mig 641) — why a score was overridden. Internal: never shown to the student. */
export const scoreOverrideReasons = [
  'proctoring_violations',
  'unsupervised_gaps',
  'identity_doubt',
  'technical_problem',
  'other'
] as const;
export type ScoreOverrideReason = (typeof scoreOverrideReasons)[number];

/** The note's limit on the server (ck_attempt_score_overrides_note). */
export const SCORE_OVERRIDE_NOTE_MAX = 500;

/**
 * EXAM-OVR — one change in an attempt's override history (GET /exam/attempts/{id}/score-overrides), and the reply to
 * POST …/score-override and …/score-override/revert. `reason` is null on a revert; `note` on an override is what the
 * student reads, on a revert it is internal.
 */
export const attemptScoreOverrideSchema = z.object({
  id: z.guid(),
  attemptId: z.guid(),
  kind: z.enum(['override', 'revert']),
  previousScorePct: z.number(),
  previousPassed: z.boolean(),
  newScorePct: z.number(),
  newPassed: z.boolean(),
  computedScorePct: z.number(),
  // ⚠ A plain string, not z.enum(scoreOverrideReasons): one unknown code from a newer server would reject the WHOLE
  // history (zod-enum-rejects-whole-response). The page labels known codes and falls back for the rest.
  reason: z.string().nullish(),
  note: z.string().nullish(),
  changedBy: z.guid(),
  changedAtUtc: isoDate,
  resultsReleased: z.boolean(),
  evidenceViolationCount: z.number().int(),
  evidenceGapCount: z.number().int(),
  evidenceNeedsReview: z.boolean(),
  /** EXAM-NAMES: filled by the endpoint from Identity; null when it could not name them. */
  changedByName: z.string().nullish()
});
export type AttemptScoreOverride = z.infer<typeof attemptScoreOverrideSchema>;
export const attemptScoreOverrideListSchema = z.array(attemptScoreOverrideSchema);

/** POST /exam/attempts/{id}/reopen — what one reopen did. The previous mark is echoed for the confirmation. */
export const attemptReopeningSchema = z.object({
  id: z.guid(),
  attemptId: z.guid(),
  minutes: z.number().int(),
  reason: z.enum(attemptExtensionReasons),
  reopenedAtUtc: isoDate,
  newDeadlineAtUtc: isoDate,
  previousStatus: z.string(),
  previousScorePct: z.number().nullish(),
  previousPassed: z.boolean().nullish(),
  reopenCount: z.number().int()
});
export type AttemptReopening = z.infer<typeof attemptReopeningSchema>;

/** PUT /exam/attempts/{id}/position — the candidate's position after a move (or a presence ping). */
export const attemptPositionSchema = z.object({
  attemptId: z.guid(),
  currentPosition: z.number().int(),
  furthestPosition: z.number().int(),
  questionCount: z.number().int(),
  navigationMode: z.enum(examNavigationModes),
  lastSeenAtUtc: isoDate
});
export type AttemptPosition = z.infer<typeof attemptPositionSchema>;

/** EXAM-ATT-TABLE: one option of the Attempts tab's country / school filter. */
export const attemptFacetSchema = z.object({
  id: z.string().uuid().nullable(),
  count: z.number().int(),
  /** EXAM-NAMES: CANDIDATE facets only — the person's display name from Identity. Null on country/school facets. */
  displayName: z.string().nullish(),
  /** NAMES-SWEEP: SCHOOL facets only — the school's legal name + city (opaque Definitions id), from Identity. */
  schoolName: z.string().nullish(),
  schoolCityDefinitionId: z.string().uuid().nullish()
});
export type AttemptFacet = z.infer<typeof attemptFacetSchema>;

export const examAttemptListSchema = z.object({
  items: z.array(examAttemptRowSchema),
  totalItems: z.number().int(),
  counts: z.object({
    all: z.number().int(),
    started: z.number().int(),
    submitted: z.number().int(),
    graded: z.number().int(),
    needsReview: z.number().int(),
    away: z.number().int().optional().default(0), // EXAM-RESUME
    timedOut: z.number().int().optional().default(0)
  }),
  marksVisible: z.boolean(),
  /** EXAM-ATT-TABLE: the filter options — over the whole exam; schools narrowed to the chosen countries. id null = Not set. */
  countries: z.array(attemptFacetSchema).optional().default([]),
  schools: z.array(attemptFacetSchema).optional().default([]),
  /** EXAM-ATT-FILTERS: the people with an attempt on THIS exam (id = user id), most attempts first — the Candidate filter. */
  candidates: z.array(attemptFacetSchema).optional().default([])
});
export type ExamAttemptList = z.infer<typeof examAttemptListSchema>;

export const attemptMonitorSchema = z.object({
  attempt: examAttemptRowSchema,
  satUnder: attemptConditionsSchema.nullish(),
  network: attemptNetworkSchema.nullish(),
  /** null = the attempt was not supervised. An answer, not an error. */
  session: proctoringSessionSchema.nullish(),
  /** Oldest first — a timeline. */
  events: z.array(proctoringViolationSchema),
  marksVisible: z.boolean(),
  serverNowUtc: isoDate,
  /** EXAM-WARN (mig 571): when the candidate confirmed the supervision notice at start; null = not asked. */
  proctoringAcknowledgedAtUtc: isoDate.nullish()
});
export type AttemptMonitor = z.infer<typeof attemptMonitorSchema>;

// ==============================|| DASH-P3 — Exam dashboard summary ||============================== //
// GET /api/v1/exam/dashboard/summary?from=YYYY-MM-DD&to=YYYY-MM-DD&timeZone= — [RequiresModule("exam")]
// + exam.attempts.grade (the cohort grant), [NoCache]. Counts only, no candidate identities.
//   • pass rate / mean use the same arithmetic as the per-exam results page, on the STORED pass flag.
//   • closingSoon[].enrolled / notStarted are NULL for an open (unrestricted) exam — the eligible
//     population lives in Identity, so it is unknown here, which is not the same as zero.
//   • attendance covers sittings that ENDED in the window; most exams have no sittings, so zero is normal.
//   • backlog / bucket / status keys are plain strings (a Zod enum rejects the whole response on a new value).

const examPeriodCount = z.object({ current: z.number().int(), previous: z.number().int() });
const examAttendanceCounts = z.object({ expected: z.number().int(), present: z.number().int(), absent: z.number().int(), excused: z.number().int() });

/** DASH-P5 parity: attempts STARTED per bucket — a week's Monday (yyyy-MM-dd) or a month (yyyy-MM). */
const examTrendPoint = z.object({ start: z.string(), started: z.number().int() });

export const examDashboardSummarySchema = z.object({
  /** DASH-P5 parity: the last 12 Monday weeks and 12 calendar months, oldest first; independent of the period */
  weekly: z.array(examTrendPoint).default([]),
  monthly: z.array(examTrendPoint).default([]),
  /** DASH-P5 filter bar — exam / sitting sections are listed while a country / school filter is on */
  filter: dashboardFilterSchema.nullish(),
  period: z.object({ from: z.string(), to: z.string(), previousFrom: z.string(), previousTo: z.string(), timeZone: z.string(), previousUntil: z.string().nullish() }),
  openNow: z.number().int(),
  closingSoon: z.array(
    z.object({
      examId: z.guid(),
      title: z.string(),
      closesAtUtc: z.string(),
      restricted: z.boolean(),
      enrolled: z.number().int().nullable(),
      notStarted: z.number().int().nullable(),
      started: z.number().int()
    })
  ),
  attempts: z.object({
    started: examPeriodCount,
    graded: examPeriodCount,
    passed: examPeriodCount,
    passRatePct: z.object({ current: z.number().nullable(), previous: z.number().nullable() })
  }),
  funnel: z.object({ started: z.number().int(), finished: z.number().int(), graded: z.number().int(), passed: z.number().int() }),
  backlog: z.object({
    total: z.number().int(),
    oldestSubmittedAtUtc: z.string().nullable(),
    byAge: z.array(z.object({ bucket: z.string(), count: z.number().int() })),
    integrityReview: z.number().int()
  }),
  attendance: z.object({ current: examAttendanceCounts, previous: examAttendanceCounts }),
  upcomingSittings: z.array(
    z.object({
      sittingId: z.guid(),
      examId: z.guid(),
      examTitle: z.string(),
      title: z.string(),
      startsAtUtc: z.string(),
      endsAtUtc: z.string(),
      location: z.string().nullable(),
      capacity: z.number().int().nullable(),
      booked: z.number().int(),
      status: z.string()
    })
  ),
  recentResults: z.array(
    z.object({
      examId: z.guid(),
      title: z.string(),
      endedAtUtc: z.string(),
      passScore: z.number(),
      sat: z.number().int(),
      graded: z.number().int(),
      awaitingMark: z.number().int(),
      passed: z.number().int(),
      passRatePct: z.number().nullable(),
      meanPct: z.number().nullable(),
      medianPct: z.number().nullable(),
      present: z.number().int(),
      absent: z.number().int(),
      excused: z.number().int()
    })
  )
});

export type ExamDashboardSummary = z.infer<typeof examDashboardSummarySchema>;

// =============================================================================
// QUIZ (mig 586) — course quizzes: exam rows of kind 'quiz', sat only from a course lesson with a pass
// Learning signs. The paper / publish / close / delete use the exam routes; these are what differs.
// ⚠ Names are "courseQuiz*" — the index re-exports every module flat, so a bare `quiz*` could collide.
// =============================================================================

export const courseQuizSummarySchema = z.object({
  id: z.guid(),
  title: z.string(),
  status: z.enum(examStatuses),
  questionCount: z.number().int(),
  /** null while a draft (no snapshot yet). */
  totalMarks: z.number().int().nullish(),
  /** null = untimed. */
  timeLimitMinutes: z.number().int().nullish(),
  passScore: z.number(),
  maxAttempts: z.number().int().nullish(),
  retakeCooldownMinutes: z.number().int().nullish(),
  createdAtUtc: isoDate,
  updatedAtUtc: isoDate
});
export type CourseQuizSummary = z.infer<typeof courseQuizSummarySchema>;
export const courseQuizListSchema = pagedListSchema(courseQuizSummarySchema);

export const courseQuizDetailSchema = courseQuizSummarySchema.extend({
  description: z.string().nullish(),
  shuffleQuestions: z.boolean(),
  shuffleChoices: z.boolean(),
  publishedAtUtc: isoDate.nullish(),
  closedAtUtc: isoDate.nullish()
});
export type CourseQuizDetail = z.infer<typeof courseQuizDetailSchema>;

export interface SaveCourseQuizRequest {
  title: string;
  description: string | null;
  passScore: number;
  timeLimitMinutes: number | null;
  maxAttempts: number | null;
  retakeCooldownMinutes: number | null;
  shuffleQuestions: boolean;
  shuffleChoices: boolean;
}

/** Question types a quiz may hold — the server refuses the rest (422). */
// v2 F1 (mig 605): fill_blank, numeric and matching are marked by the server too (QuestionTypes.IsAutoGradable).
export const autoMarkedQuestionTypes = ['multiple_choice', 'true_false', 'ordering', 'multi_select', 'fill_blank', 'numeric', 'matching'] as const;

/** GET /learning/lessons/quiz-usage — one row per lesson linking a quiz. */
export const courseQuizUsageSchema = z.object({
  quizId: z.guid(),
  courseId: z.guid(),
  courseTitle: z.string(),
  moduleId: z.guid(),
  moduleTitle: z.string(),
  lessonId: z.guid(),
  lessonTitle: z.string()
});
export type CourseQuizUsage = z.infer<typeof courseQuizUsageSchema>;
export const courseQuizUsageListSchema = z.array(courseQuizUsageSchema);

/** GET /learning/lessons/{id}/quiz-pass — short-lived; fetch a fresh one for each Exam call. */
export const courseQuizPassSchema = z.object({
  quizId: z.guid(),
  lessonId: z.guid(),
  pass: z.string(),
  expiresAtUtc: isoDate
});
export type CourseQuizPass = z.infer<typeof courseQuizPassSchema>;

/** GET /exam/quizzes/{id}/mine (header X-Quiz-Pass) — the learner's quiz card. */
export const myCourseQuizSchema = z.object({
  id: z.guid(),
  title: z.string(),
  description: z.string().nullish(),
  status: z.enum(examStatuses),
  questionCount: z.number().int(),
  timeLimitMinutes: z.number().int().nullish(),
  passScore: z.number(),
  maxAttempts: z.number().int().nullish(),
  attemptsUsed: z.number().int(),
  retakeAllowedAtUtc: isoDate.nullish(),
  bestScorePct: z.number().nullish(),
  passed: z.boolean(),
  openAttemptId: z.guid().nullish(),
  canStart: z.boolean(),
  attempts: z.array(
    z.object({
      id: z.guid(),
      number: z.number().int(),
      status: z.string(),
      startedAtUtc: isoDate,
      submittedAtUtc: isoDate.nullish(),
      scorePct: z.number().nullish(),
      passed: z.boolean().nullish()
    })
  ),
  serverNowUtc: isoDate
});
export type MyCourseQuiz = z.infer<typeof myCourseQuizSchema>;

// ---- EXAM-OMR (migs 630–631): printed booklets and optical-sheet results ---------------------------------
//
//   GET    /exam/exams/{examId}/booklets                    BookletsOverview
//   GET    /exam/exams/{examId}/booklets/{id}               BookletDetail
//   POST   /exam/exams/{examId}/booklets                    { mode, label?, questionOrder? } → BookletDetail
//   POST   /exam/exams/{examId}/booklets/{id}/reshuffle     → BookletDetail
//   PUT    /exam/exams/{examId}/booklets/{id}/order         { questionOrder } → BookletDetail
//   DELETE /exam/exams/{examId}/booklets/{id}
//   GET    /exam/exams/{examId}/booklets/keys?format=xlsx|txt   (getBlob)
//   GET    /exam/exams/{examId}/paper-results/template          (getBlob)
//   POST   /exam/exams/{examId}/paper-results/preview           multipart file → PaperResultPreview
//   POST   /exam/exams/{examId}/paper-results                   multipart file + satOn + note + skipRows → ResultImport
//   GET    /exam/exams/{examId}/paper-results/imports           ResultImport[]
//   POST   /exam/exams/{examId}/paper-results/imports/{id}/undo → ResultImport

/** 'paper' | 'questions' | 'questions_and_options' | 'manual' */
export const bookletModes = ['paper', 'questions', 'questions_and_options', 'manual'] as const;
export type BookletMode = (typeof bookletModes)[number];

export const bookletBlockerSchema = z.object({
  examQuestionId: z.string().uuid(),
  paperPosition: z.number().int(),
  type: z.string(),
  /** type_not_on_sheet | too_many_options | no_answer_key */
  reason: z.string()
});
export type BookletBlocker = z.infer<typeof bookletBlockerSchema>;

export const bookletSummarySchema = z.object({
  id: z.string().uuid(),
  label: z.string(),
  mode: z.string(),
  /** In fives, as on the sheet: "BDACB CADBA …". */
  answerKey: z.string(),
  questionCount: z.number().int(),
  lockedAt: z.string().nullish(),
  createdAt: isoDate
});
export type BookletSummary = z.infer<typeof bookletSummarySchema>;

export const bookletsOverviewSchema = z.object({
  examId: z.string().uuid(),
  examStatus: z.string(),
  published: z.boolean(),
  questionCount: z.number().int(),
  blockers: z.array(bookletBlockerSchema),
  booklets: z.array(bookletSummarySchema),
  nextLabel: z.string().nullish()
});
export type BookletsOverview = z.infer<typeof bookletsOverviewSchema>;

export const bookletPositionSchema = z.object({
  position: z.number().int(),
  examQuestionId: z.string().uuid(),
  paperPosition: z.number().int(),
  type: z.string(),
  text: z.string(),
  options: z.array(z.object({ key: z.string(), text: z.string(), letter: z.string() })),
  keyLetter: z.string(),
  /** EXAM-WEIGHT: the question's mark on this paper (frozen at publish). Nullish while an older server is still live. */
  points: z.number().nullish()
});
export type BookletPosition = z.infer<typeof bookletPositionSchema>;

export const bookletDetailSchema = z.object({
  booklet: bookletSummarySchema,
  positions: z.array(bookletPositionSchema)
});
export type BookletDetail = z.infer<typeof bookletDetailSchema>;

export const paperResultRowErrorSchema = z.object({
  row: z.number().int(),
  /** code | booklet | answers */
  column: z.string(),
  code: z.string(),
  value: z.string().nullish(),
  position: z.number().int().nullish()
});
export type PaperResultRowError = z.infer<typeof paperResultRowErrorSchema>;

export const paperResultPreviewRowSchema = z.object({
  row: z.number().int(),
  userCode: z.string(),
  userId: z.string().uuid(),
  displayName: z.string(),
  booklet: z.string(),
  /** Booklet order, one char per position: c correct · w wrong · b blank · m two marks. */
  strip: z.string(),
  sheet: z.string(),
  correct: z.number().int(),
  wrong: z.number().int(),
  blank: z.number().int(),
  multiple: z.number().int(),
  scorePct: z.number(),
  passed: z.boolean(),
  /** will_enroll | outside_audience | has_attempts | duplicate_user | duplicate_sheet | inactive_user | empty_sheet */
  warnings: z.array(z.string()),
  defaultSkip: z.boolean()
});
export type PaperResultPreviewRow = z.infer<typeof paperResultPreviewRowSchema>;

export const paperResultPreviewSchema = z.object({
  examId: z.string().uuid(),
  questionCount: z.number().int(),
  rollRestricted: z.boolean(),
  resultsReleased: z.boolean(),
  resultsReleaseAt: z.string().nullish(),
  errors: z.array(paperResultRowErrorSchema),
  rows: z.array(paperResultPreviewRowSchema)
});
export type PaperResultPreview = z.infer<typeof paperResultPreviewSchema>;

export const resultImportSchema = z.object({
  id: z.string().uuid(),
  fileName: z.string().nullish(),
  /** yyyy-MM-dd */
  satOn: z.string(),
  note: z.string().nullish(),
  attemptCount: z.number().int(),
  skippedCount: z.number().int(),
  enrolledCount: z.number().int(),
  createdBy: z.string().uuid().nullish(),
  createdAt: isoDate,
  undoneAt: z.string().nullish(),
  /** Live per-booklet counts; empty once undone. */
  byBooklet: z.record(z.string(), z.number().int()),
  passed: z.number().int()
});
export type ResultImport = z.infer<typeof resultImportSchema>;

/** The 422 rows-rejected problem's `rowErrors` (every problem in the file at once). */
export function paperResultRowErrors(problem: object | null | undefined): PaperResultRowError[] {
  const parsed = z.array(paperResultRowErrorSchema).safeParse((problem as { rowErrors?: unknown } | null | undefined)?.rowErrors);
  return parsed.success ? parsed.data : [];
}
